import React, { useState, useEffect, useRef } from 'react';
import nlp from 'compromise';
import { generateQRMatrix } from '../utils/generators';
import { TEMPLATE_PRESETS } from '../data/templates';
import { AuthUser, getStoredToken, postLog } from '../utils/authApi';
import { ClientAccount, InvoiceRecord, ActiveTemplate, CanvasElement, DiscountRule } from '../types';
import { getCurrencyDisplay } from '../utils/currency';
import { getBestDiscountRule, computeAutoDiscount } from '../utils/discount';
import { Plus, Trash2, FileText, User, Zap, RotateCcw, CheckCircle, Banknote, CreditCard, Smartphone, Globe, X as CloseIcon, Monitor, Printer, Settings, Scan } from 'lucide-react';
import QRCode from 'qrcode';
import QuickScannerModal from './QuickScannerModal';
import { detectStateFromGSTIN, isValidGSTINFormat, isValidGSTINChecksum } from '../utils/gstin';
import StatePicker from './StatePicker';
import { repositionAutoGroups } from '../utils/pdfLayout';
import { getPermissions } from '../utils/permissions';
import BilingualInvoiceRenderer from './BilingualInvoiceRenderer';
import CanvasWorkspace from './CanvasWorkspace';
import PaginatedInvoicePreview from './PaginatedInvoicePreview';
import POSReceiptRenderer from './POSReceiptRenderer';
import { inspectTaxId, TaxIdInfo, GST_STATE_CODES, US_STATES, CANADA_PROVINCES } from '../utils/taxId';
import {
  printElementUSB,
  printReceiptUSB,
  isUSBPrinterConnected,
  connectUSBPrinter,
  tryAutoReconnectUSB,
  getUSBPrinterName
} from '../utils/usbPrinter';
import {
  printReceiptBT,
  connectBluetoothPrinter,
  getBTPrinterName,
  resolveClientLineAr,
} from '../utils/bluetoothPrinter';

import { loadMessageTemplates, applyTemplate } from '../utils/messageTemplates';
import { amountToWords } from '../utils/numberToWords';
import { getDefaultSchemeForCountryAndRegion, computeTaxBreakdown, buildCustomScheme, getSchemeById as getSchemeByIdBase, CustomTaxSchemeConfig } from '../utils/taxSchemes';
import { COUNTRY_CODES as COUNTRY_CODES_FULL, POPULAR_COUNTRIES as POPULAR_QI } from '../utils/countryCodes';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

// Master set of all countries that have sub-national tax regions, states, provinces, or exempt zones (matches mobile app)
const REGIONAL_TAX_COUNTRIES = new Set([
  'IN', 'US', 'CA', 'AU', 'BR', 'AR', 'PK', 'ES', 'PT', 'CH', 'NG',
  'MX', 'CO', 'ID', 'PE', 'MY', 'PH', 'CL', 'CN', 'RU', 'EC', 'VN',
  'DE', 'IT', 'FI', 'NO', 'DK', 'AT', 'BA', 'KR', 'TH', 'IL',
]);

const ARABIC_INDIC_DIGITS: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
function toArabicIndicDigits(text: string): string {
  return text.replace(/[0-9]/g, d => ARABIC_INDIC_DIGITS[d]);
}

// Values that must never reach the translation backend:
// numbers, codes (TBM-SELLER-0441), dates and date ranges.
export function isLiteralFieldValue(v: string): boolean {
  const s = (v || '').trim();
  if (!s) return true;
  if (/^[\d\s+\-–—().#:/\\%,]+$/.test(s)) return true;
  if (/^\d[\d\-/.]*\s+(to|till|until|-|–)\s+\d[\d\-/.]*$/i.test(s)) return true;
  if (/^[A-Za-z0-9][A-Za-z0-9\-_/.#]{2,}$/.test(s) && /\d/.test(s)) return true;
  return false;
}


function detectCountryCode(): string {
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES_FULL[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached) return cached;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Yangon':'MM','Asia/Phnom_Penh':'KH',
    'Asia/Tokyo':'JP','Asia/Seoul':'KR','Asia/Shanghai':'CN','Asia/Hong_Kong':'HK',
    'Asia/Taipei':'TW','Asia/Kabul':'AF','Asia/Tehran':'IR','Asia/Baghdad':'IQ',
    'Asia/Beirut':'LB','Asia/Amman':'JO','Asia/Jerusalem':'IL','Asia/Tashkent':'UZ',
    'Asia/Almaty':'KZ','Asia/Baku':'AZ','Asia/Tbilisi':'GE','Asia/Yerevan':'AM',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Lisbon':'PT','Europe/Paris':'FR',
    'Europe/Berlin':'DE','Europe/Amsterdam':'NL','Europe/Brussels':'BE','Europe/Madrid':'ES',
    'Europe/Rome':'IT','Europe/Vienna':'AT','Europe/Zurich':'CH','Europe/Stockholm':'SE',
    'Europe/Oslo':'NO','Europe/Copenhagen':'DK','Europe/Helsinki':'FI','Europe/Warsaw':'PL',
    'Europe/Prague':'CZ','Europe/Budapest':'HU','Europe/Bucharest':'RO','Europe/Sofia':'BG',
    'Europe/Athens':'GR','Europe/Istanbul':'TR','Europe/Moscow':'RU','Europe/Kiev':'UA','Europe/Minsk':'BY',
    'America/New_York':'US','America/Chicago':'US','America/Denver':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Vancouver':'CA','America/Sao_Paulo':'BR',
    'America/Buenos_Aires':'AR','America/Mexico_City':'MX',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Australia/Perth':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
    'Africa/Casablanca':'MA',
  };
 
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}


function CountryPickerQI({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const all = Object.keys(COUNTRY_CODES_FULL);
  const filtered = search
    ? all.filter(c => c.toLowerCase().includes(search.toLowerCase()) || COUNTRY_CODES_FULL[c]?.includes(search.replace('+','')))
    : [...POPULAR_QI, ...all.filter(c => !POPULAR_QI.includes(c))];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{ display:'flex', alignItems:'center', gap:4, background:'#f8fafc', border:'none', borderRight:'1.5px solid #cbd5e1', padding:'9px 9px', cursor:'pointer', fontSize:12, fontWeight:700, color:'#0f172a', minWidth:70, whiteSpace:'nowrap', flexShrink:0 }}>
        +{COUNTRY_CODES_FULL[value]||'91'} <span style={{fontSize:9,opacity:0.6}}>▾</span>
      </button>
      {open && (
        <div onClick={e=>e.stopPropagation()} style={{ position:'absolute', top:'100%', left:0, zIndex:9999, background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 16px 48px rgba(0,0,0,0.15)', width:190, maxWidth:'calc(100vw - 24px)', maxHeight:240, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{padding:6}}>
            <input autoFocus placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'4px 7px',fontSize:11,outline:'none',boxSizing:'border-box'}} />
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.map(c => (
              <button key={c} onClick={()=>{onChange(c);setOpen(false);setSearch('');}}
                style={{ display:'flex',alignItems:'center',gap:6,width:'100%', background:c===value?'#eff6ff':'transparent', border:'none',padding:'6px 10px',cursor:'pointer',fontSize:11,color:'#374151',textAlign:'left' }}>
                <span style={{fontWeight:700,color:'#3b82f6',minWidth:30}}>+{COUNTRY_CODES_FULL[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Safe universal parser for 240+ countries
function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; number: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES_FULL[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES_FULL[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, number: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  // 1. Explicit international number with '+' (e.g. "+919108582545", "+6591234567", "+14155552671")
  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let maxLen = 0;

    // Check longest dial codes first so Bahamas (+1242) checks before USA (+1)
    const sortedEntries = Object.entries(COUNTRY_CODES_FULL).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxLen) {
        bestCountry = c;
        maxLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      number: cleanDigits.slice(maxLen),
    };
  }

  // 2. Domestic trunk zero (e.g. UK "07...", Australia "04...") — except Italy (+39) where 0 is mandatory
  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  // 3. Number without '+' that already starts with default dial code (only if exceeding 10 digits)
  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length > 10) {
    return {
      country: fallbackCountry,
      number: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  // 4. Standard local number (e.g. 10 digits in India "9108582545" or 8 digits in Singapore "91234567")
  return {
    country: fallbackCountry,
    number: cleanDigits,
  };
}

// Canonical normalizer: matches worldwide accounts using full international E.164 digits without slicing
function normalizePhoneForMatching(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const dialCode = (COUNTRY_CODES_FULL[parsed.country] || '91').replace(/\D/g, '');
  const cleanNumber = parsed.number.replace(/\D/g, '');
  if (!cleanNumber) return '';
  return `${dialCode}${cleanNumber}`;
}

// Validates what the user actually typed in the box and returns clean +country+number
export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const dialCode = (COUNTRY_CODES_FULL[parsed.country] || '91').replace(/\D/g, '');
  const national = parsed.number.replace(/\D/g, '');
  const total = `${dialCode}${national}`;

  // Cashier must type at least 4 digits in the box (world's shortest domestic numbers)
  // Total length with dial code must be 7 to 15 digits (ITU world standard)
  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dialCode}${national}`;
}

function PhoneInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [country, setCountry] = React.useState(() => {
    if (value) return parsePhoneValue(value).country;
    const code = detectCountryCode();
    return COUNTRY_CODES_FULL[code] ? code : 'IN';
  });
  const [number, setNumber] = React.useState(() => (value ? parsePhoneValue(value).number : ''));
  const lastEmitted = React.useRef(value);
  const isSyncingFromParent = React.useRef(false);
  const skipFirst = React.useRef(true);

  React.useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    isSyncingFromParent.current = true;
    if (!value) {
      setNumber('');
    } else {
      const parsed = parsePhoneValue(value);
      setCountry(parsed.country);
      setNumber(parsed.number);
    }
  }, [value]);

  const emitFormatted = (c: string, num: string) => {
    const cleanDigits = num.trim();
    const dialCode = (COUNTRY_CODES_FULL[c] || '91').replace(/\D/g, '');
    const combined = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = combined;
    onChange(combined);
  };

  const handleCountrySelect = (newCountry: string) => {
    setCountry(newCountry);
    emitFormatted(newCountry, number);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // 1. If user pastes an international number starting with '+' (e.g. "+65 9123 4567" or "+91 9108582545")
    // Auto-detects the country flag, sets dropdown, and extracts local digits cleanly
    if (raw.startsWith('+')) {
      const parsed = parsePhoneValue(raw);
      setCountry(parsed.country);
      setNumber(parsed.number);
      emitFormatted(parsed.country, parsed.number);
      return;
    }

    // 2. Normal typing or pasting without '+'
    const cleanNum = raw.replace(/\D/g, '');

    setNumber(cleanNum);
    emitFormatted(country, cleanNum);
  };

  return (
    <div>
    <div style={{ fontSize: 9, fontWeight: 700, color: '#b45309', marginBottom: 3 }}>
      📞 +{COUNTRY_CODES_FULL[country] || '91'} is already set. Type the number only.
    </div>
    <div style={{ display: 'flex', border: '1.5px solid #cbd5e1', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <CountryPickerQI value={country} onChange={handleCountrySelect} />
      <input
        value={number}
        onChange={handleTextChange}
        placeholder="Phone number"
        style={{
          flex: 1,
          border: 'none',
          padding: '9px 12px',
          fontSize: 13,
          outline: 'none',
          background: '#fff',
          fontWeight: 600,
          color: '#0f172a',
          boxSizing: 'border-box',
        }}
      />
    </div>
    </div>
  );
}
export interface LineItem {
  id: string;
  description: string;
  qty: number;
  rate: number;
  taxRate?: number;
  taxName?: string;
  b2bTaxRate?: number;
  b2bTaxName?: string;
  taxInclusive?: boolean;

  // Optional translated product description for bilingual previews
  translatedDescription?: string;
}

export interface TaxLine { label: string; rate: number; }
export interface DiscountLine { label: string; rate: number; }

interface QuickInvoiceViewProps {
  authUser: AuthUser | null;
  outletId?: number | null;
  moduleStatuses?: Partial<Record<import('../utils/permissions').ModuleId, import('../utils/permissions').ModuleStatus>>;
  accounts: ClientAccount[];
  bills: InvoiceRecord[];
  onRemoveTemplate?: (id: string) => void;
  setBills: React.Dispatch<React.SetStateAction<InvoiceRecord[]>>;
  customTemplates: ActiveTemplate[];
  activeCurrency: string;
  onAddLog: (action: string, details: string) => void;
  onPostSave?: (invoice: InvoiceRecord) => void;
  defaultTemplateId?: string;
  onSendInvoiceWhatsApp?: (invoice: InvoiceRecord, phone: string) => Promise<void>;
  editInvoice?: InvoiceRecord | null;
  onUpdateInvoice?: (billId: string, updatedElements: any[], newAmount: number, updatedFields?: Partial<InvoiceRecord>) => void | Promise<void>;
  onEditClose?: () => void;
  onAddAccount?: (account: Omit<ClientAccount, 'id' | 'createdAt'>) => void;
  discountRules?: DiscountRule[];
  onUpdateAccount?: (account: ClientAccount) => void;
  onExportPDF: (record?: InvoiceRecord, returnBlobInsteadOfDownload?: boolean) => Promise<Blob | undefined>;
}

const APP_URL = window.location.origin;
const PAY_MODES = [
  { key: 'cash', label: 'Cash', icon: <Banknote size={13} /> },
  { key: 'card', label: 'Card', icon: <CreditCard size={13} /> },
  { key: 'upi',  label: 'UPI',  icon: <Smartphone size={13} /> },
] as const;
const A4_BOTTOM_MARGIN = 60;
const ROW_HEIGHT = 20;

const TABLE_ROW_HEIGHT = 20; // TEMP — will replace with measured value
const TABLE_ROW_HEIGHT_BILINGUAL = 38; // rows render an extra Arabic subtext line per cell in bilingual mode — roughly doubles real row height, so the flat English-only constant badly underestimates table height once translated, leaving QR/notes/etc. floating mid-table before any DOM-measured correction can catch up.

function calcTableHeight(
  rowCount,
  taxCount,
  discCount,
  hasShipping,
  isBilingual = false,
  pageWidth = 794
) {
  const isThermal58 = pageWidth <= 220;
  const isThermal80 = pageWidth > 220 && pageWidth <= 302;

  const rowHeight = isBilingual
    ? (isThermal58 ? 42 : isThermal80 ? 40 : TABLE_ROW_HEIGHT_BILINGUAL)
    : (isThermal58 ? 28 : isThermal80 ? 24 : TABLE_ROW_HEIGHT);

  const headerHeight = isThermal58 ? 34 : isThermal80 ? 32 : 40;

  const totalsRowHeight = isThermal58 ? 22 : isThermal80 ? 20 : 18;

  const totalsBoxHeight =
    28 +
    (taxCount + discCount) * totalsRowHeight +
    (hasShipping ? totalsRowHeight : 0);

  const safeRowCount = Math.max(rowCount, 1);

  return headerHeight + safeRowCount * rowHeight + totalsBoxHeight;
}

// Auto-expands any background card/shape behind the customer text block
// and pushes the table down so text never spills out or collides with the table.
export function reflowClientBlockAndShapes(elements: any[]): any[] {
  if (!elements || elements.length === 0) return elements;

  const clientTextEl = elements.find(
    (el: any) =>
      el.type === 'text' &&
      el.textContent &&
      (/bill\s*to|invoice\s*to|sold\s*to|client|customer|patient|guest|member|recipient|buyer|tax\s*id|\{client_details\}/i.test(el.textContent))
  );

  let updatedElements = [...elements];
  let clientBottom = 0;

  if (clientTextEl) {
    const textY = clientTextEl.y || 0;
    const textH = clientTextEl.height || 0;
    const textX = clientTextEl.x || 0;
    const textW = clientTextEl.width || 0;
    clientBottom = textY + textH;

    updatedElements = updatedElements.map((el: any) => {
      if (el === clientTextEl) return el;

      const isShape = el.type === 'shape' || el.type === 'box' || el.type === 'rectangle' || el.shapeType === 'rectangle';
      if (!isShape) return el;

      const shapeX = el.x || 0;
      const shapeY = el.y || 0;
      const shapeW = el.width || 0;
      const shapeH = el.height || 0;

      const xOverlap = shapeX <= textX + 25 && (shapeX + shapeW) >= (textX + textW - 25);
      const yStartsAbove = shapeY <= textY + 15;
      const isBehindOrContains = xOverlap && yStartsAbove && (shapeY + shapeH >= textY);

      if (isBehindOrContains) {
        const paddingBottom = 16;
        const neededShapeHeight = Math.max(shapeH, (clientBottom - shapeY) + paddingBottom);
        const newShape = { ...el, height: neededShapeHeight };
        clientBottom = Math.max(clientBottom, shapeY + neededShapeHeight);
        return newShape;
      }
      return el;
    });
  }

  const table = updatedElements.find((el: any) => el.type === 'table');
  if (table && clientBottom > 0) {
    const tableY = table.y || 0;
    const MIN_GAP_BEFORE_TABLE = 18;

    if (tableY < clientBottom + MIN_GAP_BEFORE_TABLE) {
      const shiftDown = (clientBottom + MIN_GAP_BEFORE_TABLE) - tableY;

      updatedElements = updatedElements.map((el: any) => {
        if ((el.y || 0) >= tableY - 2) {
          return { ...el, y: (el.y || 0) + shiftDown };
        }
        return el;
      });
    }
  }

  return updatedElements;
}

function enforceNoTableOverlap(elements: any[], gap = 8) {
  // Step A: Reflow client card & push table if customer details grew
  const adjusted = reflowClientBlockAndShapes(elements);

  const table = adjusted.find((el: any) => el.type === 'table');
  if (!table) return adjusted;
  const tableTop = table.y || 0;
  const tableBottom = tableTop + (table.height || 0);

  const footerElements = adjusted
    .filter((el: any) => el.type !== 'table' && (el.y || 0) >= tableTop + 40)
    .sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  if (footerElements.length === 0) return adjusted;

  const rows: any[][] = [];
  footerElements.forEach((el: any) => {
    const elY = el.y || 0;
    const existingRow = rows.find(r => Math.abs((r[0].y || 0) - elY) <= 15);
    if (existingRow) {
      existingRow.push(el);
    } else {
      rows.push([el]);
    }
  });

  rows.sort((r1, r2) => (r1[0].y || 0) - (r2[0].y || 0));

  let cursor = tableBottom + gap;
  const newY = new Map<string, number>();

  rows.forEach(row => {
    const rowY = row[0].y || 0;
    const maxRowHeight = Math.max(...row.map(el => el.height || 0), 10);
    const targetY = Math.max(cursor, rowY);

    row.forEach(el => {
      newY.set(el.id, targetY);
    });

    cursor = targetY + maxRowHeight + gap;
  });

  return adjusted.map((el: any) => {
    if (el.type === 'table') return el;
    return newY.has(el.id) ? { ...el, y: newY.get(el.id) } : el;
  });
}



// Puts each custom column right after the template column the user chose.
// No choice (or column not found) = goes to the end.
function placeCustomColumns(baseCols: any[], newCols: any[]) {
  const result = [...baseCols];
  const newKeys = new Set(newCols.map((c: any) => c.key));
  newCols.forEach((nc: any) => {
    const { after, ...col } = nc;
    col.align = 'center';
    let idx = after ? result.findIndex((c: any) => c.key === after) : -1;
    if (idx >= 0) {
      // keep several custom columns chosen for the same spot in the order they were added
      while (idx + 1 < result.length && newKeys.has(result[idx + 1].key)) idx++;
      result.splice(idx + 1, 0, col);
    } else {
      result.push(col);
    }
  });
  return result;
}

// On thermal paper, keep Qty/Rate/Amt wide enough to avoid "Q.." and "₹550.\n00";
// the space comes out of the Item column.
function fitThermalCols(cols: any[], pageWidth: number) {
  if (pageWidth > 320) return cols;
  const MIN: Record<string, number> = { qty: 14, rate: 22, total: 24 };
  const out = cols.map((c: any) => ({ ...c, width: c.width || 12 }));
  let deficit = 0;
  out.forEach((c: any) => {
    const m = MIN[c.key];
    if (m && c.width < m) { deficit += m - c.width; c.width = m; }
  });
  const desc = out.find((c: any) => c.key === 'description');
  if (desc) {
    const give = Math.min(deficit, Math.max(0, desc.width - 28));
    desc.width -= give;
  }
  const total = out.reduce((s: number, c: any) => s + c.width, 0) || 100;
  return out.map((c: any) => ({ ...c, width: (c.width * 100) / total }));
}

export const PAGE_CONFIGS: Record<string, { width: number; height: number }> = {
  A4: { width: 794, height: 1123 },
  A5: { width: 559, height: 794 },
  LETTER: { width: 816, height: 1056 },
  LEGAL: { width: 816, height: 1344 },
  THERMAL_80MM: { width: 302, height: 800 },
  THERMAL_58MM: { width: 220, height: 800 },
};

// Maps a template's pageSize to the correct @page CSS size for browser
// print — printing a label template with @page:A4 (or vice versa) makes
// the browser scale/crop the content wrong.
const PRINT_PAGE_CSS: Record<string, string> = {
  THERMAL_80MM: '80mm auto',
  THERMAL_58MM: '58mm auto',
  LABEL_4X6: '4in 6in',
  LABEL_3X2: '3in 2in',
  A4: 'A4',
  A5: 'A5',
  LETTER: 'letter',
  LEGAL: 'legal',
};

// ── Universal Customer Details Formatter ────────────────────────────────────
export function buildClientBlock(
  data: { name: string; phone: string; email: string; address: string; taxId: string },
  inc: { name: boolean; phone: boolean; email: boolean; address: boolean; taxId: boolean },
  isBilingual = false,
  trMap?: { nameTr?: string; addressTr?: string; taxLabelTr?: string; walkInLabelTr?: string }
): { en: string; tr?: string } {
  const isReal = Boolean(data.name && data.name.trim() && data.name.toLowerCase() !== 'walk-in customer');
  const enName = inc.name ? (isReal ? data.name.trim() : 'Walk-in Customer') : '';
  const trName = inc.name
    ? (isReal ? (trMap?.nameTr || data.name.trim()) : (trMap?.walkInLabelTr || 'Walk-in Customer'))
    : '';

  const enLines: string[] = [];
  const trLines: string[] = [];

  if (enName) {
    enLines.push(enName);
    if (isBilingual) trLines.push(trName);
  }
  if (inc.address && data.address?.trim()) {
    enLines.push(data.address.trim());
    if (isBilingual) trLines.push(trMap?.addressTr || data.address.trim());
  }
  if (inc.taxId && data.taxId?.trim()) {
    enLines.push(`Tax ID: ${data.taxId.trim()}`);
    if (isBilingual) {
      const taxPrefix = (trMap?.taxLabelTr || 'Tax ID').replace(/:$/, '');
      trLines.push(`${taxPrefix}: ${data.taxId.trim()}`);
    }
  }
  if (inc.email && data.email?.trim() && data.email.includes('@')) {
    enLines.push(data.email.trim());
    if (isBilingual) trLines.push(data.email.trim());
  }
  if (inc.phone && data.phone?.trim()) {
    enLines.push(`Ph: ${data.phone.trim()}`);
    if (isBilingual) trLines.push(`Ph: ${data.phone.trim()}`);
  }

  return {
    en: enLines.join('\n'),
    tr: isBilingual ? trLines.join('\n') : undefined,
  };
}

// ── Pure Non-Destructive Token Resolver with Auto-Height ───────────────────
export function resolveElementTokens(
  el: any,
  tokens: {
    invoiceNumber: string;
    staffName: string;
    staffNameAr?: string;
    clientDetailsEn: string;
    clientDetailsTr?: string;
    clientNameEn?: string;
    readyBy?: string;
    notes?: string;
    notesAr?: string;
  }
) {
  if (el.type === 'signature') {
    const sName = (el.signatureName || tokens.staffName).replace(/\{staff_name\}/gi, tokens.staffName);
    const sText = (el.textContent || tokens.staffName).replace(/\{staff_name\}/gi, tokens.staffName);
    const sNameAr = (el.arabicSignatureName || tokens.staffNameAr || tokens.staffName).replace(
      /\{staff_name\}/gi,
      tokens.staffNameAr || tokens.staffName
    );
    const sTextAr = el.arabicContent ? el.arabicContent.replace(/\{staff_name\}/gi, tokens.staffNameAr || tokens.staffName) : undefined;
    return {
      ...el,
      signatureName: sName,
      textContent: sText,
      arabicSignatureName: sNameAr,
      ...(sTextAr ? { arabicContent: sTextAr } : {}),
    };
  }

  if (el.type !== 'text' || !el.textContent) return el;

  let text = el.textContent;
  let textAr = el.arabicContent;

  // Only replace invoice_number if we actually have a real assigned number (not 'PENDING')
  if (tokens.invoiceNumber && tokens.invoiceNumber !== 'PENDING') {
    text = text.replace(/\{invoice_number\}|\bPENDING\b/gi, tokens.invoiceNumber);
  }

  const cNameEn = tokens.clientNameEn || 'Walk-in Customer';
  text = text
    .replace(/\{staff_name\}/gi, tokens.staffName)
    .replace(/\{client_details\}/g, tokens.clientDetailsEn)
    .replace(/(Patient|Member|Student|Customer|Client|Pet|Guest):\s*\[Name\]/gi, `$1: ${cNameEn}`)
    .replace(/\[(Name|Patient|Client|Customer|Guest|Member|Student|Pet)\]/gi, cNameEn)
    .replace(/\[Name\]/gi, cNameEn);

  if (textAr) {
    if (tokens.invoiceNumber && tokens.invoiceNumber !== 'PENDING') {
      textAr = textAr.replace(/\{invoice_number\}|\bPENDING\b/gi, tokens.invoiceNumber);
    }
    const cNameAr = (tokens as any).clientNameAr || tokens.clientNameEn || 'Walk-in Customer';
    textAr = textAr
      .replace(/\{staff_name\}/gi, tokens.staffNameAr || tokens.staffName)
      .replace(/\{client_details\}/g, tokens.clientDetailsTr || tokens.clientDetailsEn)
      .replace(/:\s*\[(Name|नाम|الاسم|nom|nombre|naam)\]/gi, `: ${cNameAr}`)
      .replace(/:\s*\[[^\]]+\]/g, `: ${cNameAr}`)
      .replace(/\[(Name|नाम|الاسم|nom|nombre|naam)\]/gi, cNameAr);
  }

  // Handle Ready By and Notes cleanly
  if (tokens.readyBy && /^ready\s*by\s*:/im.test(text)) {
    text = text.replace(/^ready\s*by\s*:.*$/im, `Ready By: ${tokens.readyBy}`);
  }
  if (tokens.notes && (text.toLowerCase().startsWith('notes:') || text.toLowerCase().startsWith('terms:'))) {
    text = `Notes:\n${tokens.notes}`;
    if (tokens.notesAr) textAr = tokens.notesAr;
  }

  // Auto-expand height so adding Tax ID, Address, and Phone NEVER clips the bottom line
  const lineCount = text.split('\n').length;
  const fontSize = el.fontSize || 10;
  const neededHeight = Math.ceil(lineCount * (fontSize * 1.5) + 14);

  return {
    ...el,
    textContent: text,
    height: Math.max(el.height || 0, neededHeight),
    ...(textAr !== undefined ? { arabicContent: textAr } : {}),
  };
}

// Builds the CanvasWorkspace-compatible element array — same logic the
// live preview used inline, now shared with PDF generation so both paths
// render identically.
function patchDynamicFields(
  text: string,
  dynamicValues?: Record<string, string>
): string {
  if (!dynamicValues || !Object.keys(dynamicValues).length) {
    return text;
  }

  let result = text;

  for (const [key, value] of Object.entries(dynamicValues)) {
    if (!value?.trim()) continue;

    const cleanValue = value.trim();

    // {invoice_number}
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    result = result.replace(
      new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'gi'),
      cleanValue
    );

    // power_r -> Power R
    // optical_purchase_bill -> Optical Purchase Bill
    const labelPattern = key
      .split('_')
      .filter(Boolean)
      .map(part =>
        part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      )
      .join('[\\s\\/\\.\\(\\)#_-]*');

    // Label: [placeholder]
    result = result.replace(
      new RegExp(
        `(${labelPattern})\\s*:\\s*\\[[^\\]]*\\]`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );

    // Label: ______
    result = result.replace(
      new RegExp(
        `(${labelPattern})\\s*:\\s*_{2,}`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );

    // Label: —
    result = result.replace(
      new RegExp(
        `(${labelPattern})\\s*:\\s*[-—]{1,3}`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );

    // Label: ...
    result = result.replace(
      new RegExp(
        `\\b(${labelPattern})\\b\\s*:\\s*\\.{2,}`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );

    // Label: {placeholder}
    result = result.replace(
      new RegExp(
        `\\b(${labelPattern})\\b\\s*:\\s*\\{[^}]*\\}`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );

    // Label: <nothing> — bare label with no placeholder at all (e.g. "Time:"),
    // stopping before the next "Label:" pair on the same line. The
    // lookahead (not a consuming \s*) must own the whitespace check, or a
    // greedy \s* here eats the separator spaces before the lookahead ever
    // sees them, silently breaking the same-line case (e.g. "Time:    Patient:").
    result = result.replace(
      new RegExp(
        `\\b(${labelPattern})\\b\\s*:(?=\\s*\\n|\\s{2,}[A-Za-z][A-Za-z\\s]{1,20}:|\\s*$)`,
        'gi'
      ),
      (_, label) => `${label}: ${cleanValue}`
    );
  }

  return result;
}

// Excludes staff-name keys before patchDynamicFields runs, since
// cashierName (passed separately to BilingualInvoiceRenderer) already
// owns those lines — running them through patchDynamicFields too
// duplicated the name onto the same line (e.g. "Cashier: rihan rihan").
export const STAFF_FIELD_KEYS = new Set(['staff_name', 'staff', 'cashier']);

// Legacy regex removed — all templates now use universal {staff_name} token

export function isStaffField(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[\s\-_.]+/g, '_');
  return STAFF_FIELD_KEYS.has(normalized);
}

function omitStaffKeys(values: Record<string, string>): Record<string, string> {
  const cleaned = { ...values };
  Array.from(STAFF_FIELD_KEYS).forEach(k => delete cleaned[k]);
  return cleaned;
}

// Helper to reject accidental code snippets, formulas, or raw template syntax
export const isValidTaxIdString = (id: string): boolean => {
  if (!id || !id.trim()) return false;
  const clean = id.trim();
  if (/[\(\)\{\}\[\]<>=;\\\/]|IF\(|TARGETLABEL|REGEX|TEST/i.test(clean)) return false;
  return clean.length >= 3;
};

export function buildCwElements(
  template: ActiveTemplate,
  opts: {
    lineItems: LineItem[];
    taxLines: TaxLine[];
    discountLines: DiscountLine[];
    shippingCost: number;
    clientName: string; clientEmail: string; clientPhone: string; clientAddress: string;
    buyerTaxId?: string;
    notes: string;
    cashierName?: string;
    invoiceDate: string; dueDate: string; showDueDate: boolean;
    showInvoiceQR: boolean;
    autoQrPosition: 'left' | 'center' | 'right';
    autoQrSize: number;
    autoQrLabelSize: number;
    autoQrOffset: { x: number; y: number };
    qrValue: string;
    translatedValues?: Record<string, string>;
    dynamicValues?: Record<string, string>;
    customColumns?: { id: string; header: string; key: string; align: 'left' | 'center' | 'right'; after?: string }[];
    hiddenColumns?: string[];
    catalogProducts?: any[];
    getCatalogColValue?: (p: any, col: { key: string; header: string }) => string | undefined;
  }
) {
  const pageConfig = PAGE_CONFIGS[template.pageSize || 'A4'] || PAGE_CONFIGS['A4'];
  const templateTable = template.elements.find(el => el.type === 'table');
  const activeRowCount = Math.max(opts.lineItems.filter(li => li.description.trim()).length, 1);
  const defaultRowCount = templateTable?.rows?.length || 0;
  const rowDelta = activeRowCount - defaultRowCount;
  const taxCount = opts.taxLines.filter(t => t.rate > 0).length;
  const discCount = opts.discountLines.filter(d => d.rate > 0).length;
  const minTableHeight = calcTableHeight(
  activeRowCount,
  taxCount,
  discCount,
  opts.shippingCost > 0,
  !!opts.translatedValues && Object.keys(opts.translatedValues).length > 0,
  pageConfig.width
);
const originalTableHeight = templateTable?.height || 0;

  // Regex to detect Bill-To / Customer / Sold-To blocks
  const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;

  // 1. Build customer block cleanly using universal formatter
  const clientBlock = buildClientBlock(
    {
      name: opts.clientName || '',
      phone: opts.clientPhone || '',
      email: opts.clientEmail || '',
      address: opts.clientAddress || '',
      taxId: opts.buyerTaxId || '',
    },
    {
      name: Boolean(opts.clientName?.trim()),
      phone: Boolean(opts.clientPhone?.trim()),
      email: Boolean(opts.clientEmail?.trim()),
      address: Boolean(opts.clientAddress?.trim()),
      taxId: Boolean(opts.buyerTaxId?.trim()),
    }
  );

  const originalBillToEl = template.elements.find(el => 
    el.type === 'text' && (el.textContent?.includes('{client_details}') || targetLabelRegex.test((el.textContent || '').trim()))
  );
  let billToGrowthDelta = 0;
  let billToOriginalBottom = 0;

  if (originalBillToEl) {
    const linesCount = (originalBillToEl.textContent?.includes('{client_details}')
      ? originalBillToEl.textContent.replace('{client_details}', clientBlock.en)
      : clientBlock.en
    ).split('\n').length;

    const calculatedHeight = Math.max(originalBillToEl.height || 40, linesCount * 17 + 14);
    billToGrowthDelta = Math.max(0, calculatedHeight - (originalBillToEl.height || 40));
    billToOriginalBottom = (originalBillToEl.y || 0) + (originalBillToEl.height || 40);
  }

  const totalPush = minTableHeight - originalTableHeight;
  const tableBottom = templateTable ? (templateTable.y || 0) + billToGrowthDelta + originalTableHeight : 0;

  const cwElements: any[] = template.elements.map(el => {
    // 1. Auto-expand background shape/card behind Bill To
    if ((el.type === 'shape' || el.type === 'rectangle' || el.type === 'container') && originalBillToEl) {
      const isBehindBillTo = Math.abs((el.x || 0) - (originalBillToEl.x || 0)) < 80 && Math.abs((el.y || 0) - (originalBillToEl.y || 0)) < 60;
      if (isBehindBillTo) {
        return { ...el, height: (el.height || 40) + billToGrowthDelta };
      }
    }

    // 2. Shift table and anything below Bill To down so it NEVER collides with Bill To
    const isBelowBillTo = originalBillToEl && (el.y || 0) >= billToOriginalBottom - 15 && el.id !== originalBillToEl.id;
    const adjustedY = isBelowBillTo ? (el.y || 0) + billToGrowthDelta : (el.y || 0);

    if (el.type === 'table') {
      const customCols = opts.customColumns || [];
      const hiddenCols = new Set(opts.hiddenColumns || []);
      const activeRows = opts.lineItems.filter(li => li.description.trim()).map(li => {
        const getCustomVal = (col: { key: string; header: string }) => {
          const raw = (li as any)[col.key] ?? (li as any)[col.header] ?? (col.key === 'cat_tax_name' ? ((li as any).taxName || (li as any).b2bTaxName) : '');
          if (raw != null && raw !== '') return String(raw);

          // Fallback directly to catalog product if not explicitly typed in lineItem
          const p = (opts.catalogProducts || []).find((cp: any) =>
            li.description && cp.name?.trim().toLowerCase() === li.description.trim().toLowerCase()
          );
          return (opts.getCatalogColValue && p ? opts.getCatalogColValue(p, col) : '') ?? '';
        };

        const getCustomAr = (col: { key: string; header: string }) => {
          const raw = getCustomVal(col);
          return opts.translatedValues?.[`custom_${li.id}_${col.key}`] ||
                 opts.translatedValues?.[`custom_${li.id}_${col.key.replace(/^cat_/, '')}`] ||
                 opts.translatedValues?.[`custom_${li.id}_${col.header}`] ||
                 opts.translatedValues?.[`tax_name_${raw}`] ||
                 '';
        };

        return {
          id: li.id,
          values: {
            description: li.description,
            qty: li.qty,
            rate: li.rate,
            total: li.qty * li.rate,
            taxRate: (li as any).taxRate || 0,
            taxInclusive: !!(li as any).taxInclusive,
            taxName: (li as any).taxName || '',
            b2bTaxName: (li as any).b2bTaxName || '',
            arabicDescription: opts.translatedValues?.[`item_${li.id}`] || '',
            // Expose BOTH col.key ('shelf_no') and col.header ('Shelf No') so renderer never misses it
            ...Object.fromEntries(customCols.flatMap(col => [
              [col.key, getCustomVal(col)],
              [col.header, getCustomVal(col)],
            ])),
            ...Object.fromEntries(customCols.flatMap(col => [
              [`arabic_${col.key}`, getCustomAr(col)],
              [`arabic_${col.header}`, getCustomAr(col)],
              [`arabicContent_${col.key}`, getCustomAr(col)],
              [`arabicContent_${col.header}`, getCustomAr(col)],
            ])),
          },
        };
      });

      // Filter out hidden base columns
      const rawBaseCols = (el.columns || []).filter((c: any) => c.key === 'description' || !hiddenCols.has(c.key));
      const existingKeys = new Set(rawBaseCols.map((c: any) => c.key));
      const newColsToAdd = customCols.filter(col => !existingKeys.has(col.key));
      
      let rebalancedCols = rawBaseCols;
      if (newColsToAdd.length > 0) {
        const perNewColWidth = 12;
        const totalNewWidth = perNewColWidth * newColsToAdd.length;
        const existingTotalWidth = rawBaseCols.reduce((s: number, c: any) => s + (c.width || 0), 0) || 100;
        const shrinkFactor = Math.max(0, (100 - totalNewWidth)) / existingTotalWidth;
        rebalancedCols = rawBaseCols.map((c: any) => ({ ...c, width: (c.width || 0) * shrinkFactor }));
      }
      const newTableCols = newColsToAdd.map(col => ({ id: col.id, key: col.key, header: col.header, align: col.align, width: 12, after: (col as any).after }));
      return {
        ...el,
        y: adjustedY,
        columns: fitThermalCols(placeCustomColumns(rebalancedCols, newTableCols), pageConfig.width),
        rows: activeRows.length > 0 ? activeRows : el.rows,
        height: minTableHeight,
        taxes: opts.taxLines.filter(t => t.rate > 0),
        discounts: opts.discountLines.filter(d => d.rate > 0),
        shippingCost: opts.shippingCost > 0 ? opts.shippingCost : undefined,
      };
    }

    if ((adjustedY || 0) >= tableBottom - 10 && !(el as any)._auto) {
      return { ...el, y: (adjustedY || 0) + totalPush };
    }

    const currentStaff = opts.cashierName?.trim() || 'Staff';

    // 1. Automatically fill signature if it has {staff_name}
    if (el.type === 'signature') {
      const sig = (el.signatureName || currentStaff).replace(/\{staff_name\}/gi, currentStaff);
      const text = (el.textContent || currentStaff).replace(/\{staff_name\}/gi, currentStaff);
      return { ...el, signatureName: sig, textContent: text, y: adjustedY };
    }

    if (el.type === 'text' && el.textContent) {
      let t = el.textContent;
      const low = t.toLowerCase();
      const buyerTaxStr = (opts.buyerTaxId && isValidTaxIdString(opts.buyerTaxId)) ? `Tax ID: ${opts.buyerTaxId.trim()}` : '';

      if (t.includes('{client_details}')) {
        t = t.replace('{client_details}', clientBlock.en);
        const calculatedHeight = Math.max(el.height || 40, t.split('\n').length * 17 + 14);
        return { ...el, textContent: t, height: calculatedHeight, y: adjustedY };
      }

      if (targetLabelRegex.test(t.trim()) || targetLabelRegex.test(t.split('\n')[0])) {
        const headerLabel = (t.split('\n')[0].split(':')[0] || 'Bill To').trim() + ':';
        t = `${headerLabel}\n${clientBlock.en}`;
        const calculatedHeight = Math.max(el.height || 40, t.split('\n').length * 17 + 14);
        return { ...el, textContent: t, height: calculatedHeight, y: adjustedY };
      }

      if (opts.notes && (low.startsWith('notes:') || low.startsWith('terms:'))) {
        t = `Notes:\n${opts.notes}`;
      }
      t = t.replace(/\{staff_name\}/gi, currentStaff);

      if (opts.invoiceDate) {
        t = t.replace(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/g, opts.invoiceDate);
        t = t.replace(/(?<!Due\s)Date:\s*[\d]{1,4}[\/\-\.][\d]{1,2}[\/\-\.][\d]{2,4}/gi, `Date: ${opts.invoiceDate}`);
      }
      if (opts.showDueDate && opts.dueDate) {
        t = t.replace(/Due\s*Date:\s*[\d\/\-\.]+/gi, `Due Date: ${opts.dueDate}`);
      }

      // Allow custom field from sidebar; if left blank, fall back to customer name
      const activeCustFallback = opts.dynamicValues?.patient || opts.dynamicValues?.guest || opts.dynamicValues?.pet || opts.dynamicValues?.student || opts.clientName?.trim() || '';
      const mergedFields = {
        patient: activeCustFallback,
        guest: activeCustFallback,
        pet: activeCustFallback,
        student: activeCustFallback,
        ...opts.dynamicValues,
      };
      if (!mergedFields.patient) mergedFields.patient = activeCustFallback;

      t = patchDynamicFields(t, mergedFields);
      return { ...el, textContent: t, y: adjustedY };
    }
    return { ...el, y: adjustedY };
  });

  // Invoice QR is no longer generated here — the backend attaches the
  // secure, display_token-based QR (_auto === 'invoice' / 'invoice_label')
  // after save. Generating a second QR here with a raw, unsigned URL
  // (opts.qrValue) duplicated it and bypassed the security overhaul.

  const safeCwElements = enforceNoTableOverlap(cwElements);
  const naturalContentBottom = safeCwElements.reduce((max, el) => Math.max(max, (el.y || 0) + (el.height || 0)), 0) + 60;
  return { cwElements: safeCwElements, pageConfig, naturalContentBottom };
}


// Renders CanvasWorkspace offscreen into static HTML for Puppeteer —
// replaces the old TemplatePreviewCanvas-based PDF render path so what
// gets printed matches the live preview exactly.
// Pulls the actual compiled CSS (Tailwind + everything else) out of the
// live page's stylesheets, so the offscreen Puppeteer render — which gets
// a bare HTML string with no access to this app's assets — still has every
// utility class CanvasWorkspace depends on. Without this, only inline
// styles survive (e.g. the table header's dark backgroundColor), which is
// exactly why PDFs were showing solid black boxes with no text/layout.
let _cachedPageCss: string | null = null;
export async function getPageCss(): Promise<string> {
  if (_cachedPageCss !== null) return _cachedPageCss;
  const chunks: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = Array.from(sheet.cssRules || []).map(r => r.cssText).join('\n');
      chunks.push(rules);
    } catch {
      // Cross-origin stylesheet (cssRules blocked) — fetch its text directly.
      if (sheet.href) {
        try {
          const res = await fetch(sheet.href);
          chunks.push(await res.text());
        } catch { /* skip unreachable sheet */ }
      }
    }
  }
  _cachedPageCss = chunks.join('\n');
  return _cachedPageCss;
}

export async function renderCwToHtml(
  template: ActiveTemplate,
  cwData: { cwElements: any[]; pageConfig: { width: number; height: number }; naturalContentBottom: number },
  pageBehavior: 'fixed' | 'auto_expand' | 'multi_page',
  containerWidth: number,
  activeCurrency: string = 'INR',
  currencyDisplayMode: 'symbol' | 'code' = 'symbol',
  bodyOnly: boolean = false,
  bilingualMode: boolean = false,
  translateTargetLang: string = 'en'
): Promise<string> {
  const container = document.createElement('div');
  container.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${containerWidth}px`;
  document.body.appendChild(container);
  const { createRoot } = await import('react-dom/client');
  const root = createRoot(container);

  // auto_expand (thermal receipts) must size purely to actual content —
  // Math.max against pageConfig.height was flooring every thermal
  // receipt at 800px even when real content only needed ~500px.
  const computedHeight = pageBehavior === 'fixed'
    ? cwData.pageConfig.height
    : pageBehavior === 'auto_expand'
      ? cwData.naturalContentBottom
      : Math.max(cwData.pageConfig.height, cwData.naturalContentBottom);
  const pageCount = pageBehavior === 'multi_page'
    ? Math.max(1, Math.ceil(cwData.naturalContentBottom / cwData.pageConfig.height))
    : 1;

  const paperRef = { current: null as HTMLDivElement | null };

  // Rendering QR elements without a real data URL falls back to
  // CanvasWorkspace's raw <div>-grid QR renderer — hundreds to 1000+
  // individual divs PER QR code. That's negligible for one invoice, but
  // batch generation concatenates many rows into a single request, and
  // that div bloat (not the CSS) was the actual cause of 413s. Generate
  // real compact PNG data URLs up front so each QR is a single <img> tag.
  const qrDataUrls: Record<string, string> = {};
  await Promise.all(
    cwData.cwElements
      .filter((el: any) => el.type === 'qr' && el.qrValue)
      .map(async (el: any) => {
        try {
          const QRCode = (await import('qrcode')).default;
          qrDataUrls[el.id] = await QRCode.toDataURL(el.qrValue, { margin: 0, width: 200 });
        } catch { /* fall back to div-grid rendering for this one QR */ }
      })
  );

  await new Promise<void>(resolve => {
    root.render(
      React.createElement(CanvasWorkspace, {
        pageConfig: { ...cwData.pageConfig, name: template.pageSize || 'A4' },
        computedHeight, pageCount, pageBehavior, setPageBehavior: () => {},
        zoom: 1, setZoom: () => {}, showGridLines: false,
        elements: cwData.cwElements,
        selectedElementId: null, inlineEditingId: null, inlineEditText: '',
        setInlineEditText: () => {}, lockedElementIds: [], dragState: null,
        qrDataUrls, isDrawingMode: false, isDrawingActive: false,
        currentPathPoints: [], currentPenColor: '#000000', currentPenWidth: 2,
        bilingualMode, bilingualDirection: 'en-ar', translateTargetLang,
        activeCurrency, currencyDisplayMode, printMode: true,
        computeTableTotals: (el: any) => {
          let subtotal = 0;
          const lineBases: { base: number; taxRate: number; taxName?: string }[] = [];
          (el.rows || []).forEach((r: any) => {
            const qty = parseFloat(r.values.qty) || 0;
            const rate = parseFloat(r.values.rate) || 0;
            const lineTotal = qty * rate;
            const taxRate = parseFloat(r.values.taxRate ?? r.values.tax_rate ?? 0) || 0;
            const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
            const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
            const taxName = r.values.taxName || r.values.tax_name;
            lineBases.push({ base, taxRate, taxName });
            subtotal += base;
          });
          const discount = (el.discounts || []).reduce((s: number, d: any) => s + subtotal * (d.rate/100), 0);
          const discountRatio = subtotal > 0 ? Math.min(1, discount / subtotal) : 0;
          const productTax = lineBases.reduce((s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0);
          const taxableValue = subtotal - discount;
          const tax = (el.taxes || []).reduce((s: number, t: any) => s + taxableValue * (t.rate/100), 0);
          const shipping = el.shippingCost || 0;

          // Forward the named tax breakdown (e.g. PPN, rihan4, GST, VAT)
          let breakdown = el.taxBreakdown;
          if (!breakdown && productTax > 0) {
            const combined: Record<string, { rate: number; amount: number }> = {};
            lineBases.forEach(lb => {
              if (lb.taxRate <= 0) return;
              const name = (lb.taxName || 'Tax').replace(/\s*\(.*?\)/g, '').trim();
              if (!combined[name]) combined[name] = { rate: lb.taxRate, amount: 0 };
              combined[name].amount += (lb.base * (1 - discountRatio)) * (lb.taxRate / 100);
            });
            const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
            if (entries.length > 0) breakdown = entries;
          }

          return { subtotal, tax, discount, shipping, productTax, taxBreakdown: breakdown, total: taxableValue + productTax + tax + shipping };
        },
        formatSerialNumber: (s: any) => s ? `${s.prefix}${String(s.current).padStart(s.digits,'0')}${s.suffix}` : '',
        canvasRef: paperRef,
        handleMouseDown: () => {}, handleDeleteElement: () => {},
        handleInlineEditStart: () => {}, handleInlineEditSave: () => {},
        commitCurrentState: () => {}, updateElementProps: () => {},
        handleCanvasMouseDown: () => {}, handleCanvasMouseMove: () => {},
        handleCanvasMouseUp: () => {}, onBackgroundClick: () => {},
      })
    );
    setTimeout(resolve, 800);
  });

  // The table's declared height (from buildCwElements) is a guess —
  // totals-box padding, row wrapping, and print-mode font sizes all
  // change actual rendered height in ways that formula can't predict
  // (see the table/totals JSX in CanvasWorkspace.tsx). Elements were
  // positioned against that guess, so anything placed below the table
  // — like the auto QR — can end up floating in dead space. Measure the
  // real DOM here and shift any element below the estimated bottom to
  // sit flush against what actually rendered.
  if (paperRef.current) {
    const paperRect = paperRef.current.getBoundingClientRect();
    const tableNode = paperRef.current.querySelector('[data-elid]')
      ? Array.from(paperRef.current.querySelectorAll('[data-elid]')).find(
          (node) => cwData.cwElements.find(e => e.id === node.getAttribute('data-elid'))?.type === 'table'
        ) as HTMLElement | undefined
      : undefined;
    if (tableNode) {
      const realTableBottom = tableNode.getBoundingClientRect().bottom - paperRect.top;
      const estimatedTableEl = cwData.cwElements.find(e => e.type === 'table');
      const estimatedBottom = estimatedTableEl ? (estimatedTableEl.y || 0) + (estimatedTableEl.height || 0) : 0;
      const drift = realTableBottom - estimatedBottom;
      if (Math.abs(drift) > 2) {
        // Shift every element positioned at/after the estimated table
        // bottom (QR, its label, anything else below the table) by the
        // measured drift, so they sit against real content, not the guess.
        cwData.cwElements.forEach((el: any) => {
          if ((el.y || 0) >= estimatedBottom - 10) {
            const node = paperRef.current!.querySelector(`[data-elid="${el.id}"]`) as HTMLElement | null;
            if (node) node.style.top = `${(el.y || 0) + drift}px`;
          }
        });
      }
    }
  }

// Serialize only the "paper" element itself — not the toolbar, and not
  // the scrollable "virtual desktop" wrapper around it (which has its own
  // p-12 padding + dot-grid background). Grabbing the whole component tree
  // was why PDFs showed a grey margin and shrunk/cramped content instead
  // of the page filling edge-to-edge like the live preview's paper does.
  const paperHtml = paperRef.current ? paperRef.current.outerHTML : container.innerHTML;
  root.unmount();
  document.body.removeChild(container);

  // Batch PDFs concatenate many rows into ONE request — if every row
  // embedded the full page CSS in its own document, N rows meant N
  // copies of that CSS glued together, which is what was blowing past
  // the backend's request-size limit (413) as row counts grew. bodyOnly
  // skips the CSS/doctype wrap so the caller can include the CSS once
  // for the whole batch instead of once per row.
  if (bodyOnly) return paperHtml;

  const pageCss = await getPageCss();
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&family=Noto+Sans+Arabic:wght@400;700;800&family=Noto+Sans+Devanagari:wght@400;700;800&display=swap" rel="stylesheet">
<style>
${pageCss}
@page {
  size: A4 portrait;
  margin-top: 15mm;
  margin-bottom: 12mm;
  margin-left: 8mm;
  margin-right: 8mm;
}
* {
  box-sizing: border-box;
}
html, body, div, span, p, td, th, h1, h2, h3, h4, h5, h6 {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Noto Sans Devanagari', 'Noto Sans Arabic', 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif !important;
}
body {
  background: #fff;
  margin: 0;
  -webkit-font-smoothing: antialiased;
}
table { page-break-inside: auto; }
tr { page-break-inside: avoid; page-break-after: auto; }
thead { display: table-header-group; }
</style></head><body>${paperHtml}</body></html>`;
  return html;
}

export default function QuickInvoiceView({
  authUser, outletId = null, moduleStatuses, accounts, bills, setBills, customTemplates,
  onRemoveTemplate, activeCurrency, onAddLog, onPostSave, defaultTemplateId, onSendInvoiceWhatsApp,
  editInvoice, onUpdateInvoice, onEditClose, onAddAccount,
  discountRules, onUpdateAccount, onExportPDF,
}: QuickInvoiceViewProps) {
  const isEditMode = !!editInvoice;

  // Same register id POSView uses — lets the Counter Display's SSE stream
  // filter to bills from this specific terminal/tab.
  const [registerId] = useState(() => {
    let id = localStorage.getItem('cloudairambo_register_id');
    if (!id) {
      id = 'reg_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('cloudairambo_register_id', id);
    }
    return id;
  });
  const [outletDisplayToken, setOutletDisplayToken] = useState('');
  const [outletDefaultTemplateId, setOutletDefaultTemplateId] = useState<string>(() => {
    return (outletId ? localStorage.getItem(`cloudairambo_qi_default_tpl_${outletId}`) : '') || '';
  });
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    if (!jwt) return;
    fetch(`${BACKEND}/auth/company-profile`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data?.name) setCompanyName(data.name);
        else if (authUser?.name) setCompanyName(authUser.name);
      })
      .catch(() => {
        if (authUser?.name) setCompanyName(authUser.name);
      });
  }, [authUser]);
  // Seller country/region — sourced from the actual outlet record fetched
  // below. authUser.country/region are never set anywhere, and the old
  // localStorage keys were never written either — both were dead reads
  // that permanently pinned this to 'IN'.
  const [qiOutletCountry, setQiOutletCountry] = useState((authUser as any)?.country || 'IN');
  const [qiOutletRegion, setQiOutletRegion] = useState((authUser as any)?.region || '');
  const [customTaxScheme, setCustomTaxScheme] = useState<CustomTaxSchemeConfig | null>(null);
  const [selectedSchemeId, setSelectedSchemeId] = useState<string | null>(() => {
    return localStorage.getItem(`cloudairambo_outlet_scheme_${outletId}`) || null;
  });

  useEffect(() => {
    if (!authUser?.org_id) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets?module=pos`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then((outlets: any[]) => {
        if (!Array.isArray(outlets)) return;
        const outlet = outlets.find(o => o.id === outletId) || outlets.find(o => o.id === authUser.outlet_id);
        setOutletDisplayToken(outlet?.display_token || '');
        const defTpl = outlet?.default_quick_invoice_template_id || '';
        setOutletDefaultTemplateId(defTpl);
        if (defTpl && (outletId || outlet?.id)) {
          localStorage.setItem(`cloudairambo_qi_default_tpl_${outletId || outlet?.id}`, defTpl);
        }
        setQiOutletCountry(outlet?.country || (authUser as any)?.country || 'IN');
        setQiOutletRegion(outlet?.region || (authUser as any)?.region || '');
        setCustomTaxScheme(outlet?.custom_tax_scheme || null);
        const activeScheme = outlet?.tax_scheme_id || localStorage.getItem(`cloudairambo_outlet_scheme_${outletId || outlet?.id}`) || (outlet?.custom_tax_scheme ? 'outlet_custom' : null);
        setSelectedSchemeId(activeScheme);
      })
      .catch(() => {});
  }, [authUser?.org_id, outletId]);

  // Picks up country/region/tax scheme saved from Profile or Catalog for
  // THIS outlet, live — no page refresh needed.
  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail?.outletId !== outletId) return;
      const o = e.detail?.outlet;
      setQiOutletCountry((o?.country ?? e.detail.country) || 'IN');
      setQiOutletRegion((o?.region ?? e.detail.region) || '');
      if (o && 'custom_tax_scheme' in o) setCustomTaxScheme(o.custom_tax_scheme || null);
      if (e.detail?.tax_scheme_id || o?.tax_scheme_id) {
        setSelectedSchemeId(e.detail?.tax_scheme_id || o?.tax_scheme_id);
      }
    };
    window.addEventListener('outlet-location-updated', handler);
    return () => window.removeEventListener('outlet-location-updated', handler);
  }, [outletId]);

  const qiTaxScheme = React.useMemo(() => {
    if ((selectedSchemeId === 'outlet_custom' || selectedSchemeId === 'custom' || !selectedSchemeId) && customTaxScheme) {
      return { ...buildCustomScheme(customTaxScheme), id: 'outlet_custom' };
    }
    if (selectedSchemeId) {
      const found = getSchemeByIdBase(selectedSchemeId);
      if (found) return found;
    }
    return getDefaultSchemeForCountryAndRegion(qiOutletCountry, qiOutletRegion);
  }, [qiOutletCountry, qiOutletRegion, selectedSchemeId, customTaxScheme]);

const perms = getPermissions(authUser?.role, (authUser as any)?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);

const showToast = (message: string, background = '#dc2626') => {
  const toast = document.createElement('div');
  toast.innerText = message;
  toast.style.cssText = `position:fixed;bottom:28px;left:50%;transform:translateX(-50%);background:${background};color:#fff;padding:12px 24px;border-radius:12px;font-size:13px;font-weight:700;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,0.3);max-width:90vw;text-align:center;`;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.4s';
    setTimeout(() => {
      if (document.body.contains(toast)) document.body.removeChild(toast);
    }, 400);
  }, 3500);
};

// Moved up (same reason as qiTaxScheme above it) — must exist before
// itemTaxBreakdowns/getItemTaxBreakdown run further down, since B2B vs
// B2C determines which tax rate every calculation uses.
const [clientGSTIN, setClientGSTIN] = useState('');
const [forceB2BOverride, setForceB2BOverride] = useState(false);
const taxIdMeta = React.useMemo<TaxIdInfo>(
  () => inspectTaxId(clientGSTIN, qiOutletCountry),
  [clientGSTIN, qiOutletCountry]
);

// Only activate B2B if Tax ID is valid or manually confirmed by cashier
const isB2B = isValidTaxIdString(clientGSTIN) && (taxIdMeta.isValid || forceB2BOverride);

// Let the cashier know the moment a sale becomes B2B — this changes which
// tax rate gets charged, so it should never happen silently. Only fires
// on the false->true transition, not on every render while isB2B stays true.
const wasB2BRef = useRef(false);
useEffect(() => {
  if (isB2B && !wasB2BRef.current) {
    if (taxIdMeta.isValid) {
      showToast(`✅ B2B billing active — valid ${taxIdMeta.taxLabel} detected. B2B tax rates will apply where set.`, '#2563eb');
    } else if (forceB2BOverride) {
      showToast(`⚠️ B2B billing active — Tax ID could not be validated, applied manually by cashier.`, '#f59e0b');
    }
  }
  wasB2BRef.current = isB2B;
}, [isB2B]);

const allTemplates: ActiveTemplate[] = [
    ...TEMPLATE_PRESETS.map(t => ({ ...t, id: t.id || t.name })),
    ...customTemplates,
  ];

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() => {
    const cachedDefault = outletId ? localStorage.getItem(`cloudairambo_qi_default_tpl_${outletId}`) : null;
    if (cachedDefault && allTemplates.some(t => t.id === cachedDefault)) {
      return cachedDefault;
    }
    return defaultTemplateId || allTemplates[0]?.id || '';
  });
  const [defaultTemplateStar, setDefaultTemplateStar] = useState<string>(
    () => (outletId ? localStorage.getItem(`cloudairambo_qi_default_tpl_${outletId}`) : null) || defaultTemplateId || ''
  );
  const canSetQIDefaultTemplate = ['owner', 'admin'].includes((authUser?.role || '').toLowerCase());
  const [savingQIDefaultTemplate, setSavingQIDefaultTemplate] = useState(false);

  useEffect(() => {
    if (editInvoice) return;
    if (!outletDefaultTemplateId) return;
    if (!allTemplates.some(t => t.id === outletDefaultTemplateId)) return;
    setSelectedTemplateId(outletDefaultTemplateId);
    setDefaultTemplateStar(outletDefaultTemplateId);
  }, [outletDefaultTemplateId, canSetQIDefaultTemplate]);
  const [formSide, setFormSide] = useState<'left' | 'right'>('left');
  const [saving, setSaving] = useState(false);
  const [paymentWaitModal, setPaymentWaitModal] = useState<{ invoiceId: string; invoiceNumber: string; link: string; qr: string } | null>(null);
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [regeneratingQR, setRegeneratingQR] = useState(false);
  // Set when the person closes the payment-wait modal to go add a
  // forgotten item — remembers which pending invoice to patch and which
  // QR/link to resume once they hit Update, mirroring POSView's
  // "+ Add Items" flow (close modal → edit normally → Save becomes Update).
  const [editingPendingInvoice, setEditingPendingInvoice] = useState<{ invoiceId: string; invoiceNumber: string; link: string; qr: string } | null>(null);
  const pendingPostPaymentRef = useRef<null | (() => Promise<void>)>(null);
const confirmedInvoiceNumberRef = useRef<string | null>(null);

  // Manual confirm path for card payments taken on a physical terminal with
  // no webhook — cashier confirms by hand instead of the poller finding a
  // 'success' status that will never arrive. Reuses the exact same
  // finalize logic as an automatic webhook success.
  const confirmCardManually = async () => {
    if (!paymentWaitModal) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    setPaymentPolling(false);
    let confirmedNumber = paymentWaitModal.invoiceNumber;
    try {
      const confirmRes = await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}/confirm-payment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const confirmed = await confirmRes.json();
      if (confirmed?.invoice_number) confirmedNumber = confirmed.invoice_number;
    } catch (e) { console.warn('confirm-payment failed:', e); }
    confirmedInvoiceNumberRef.current = confirmedNumber;
    // Cashier manually confirmed — mark it fully paid, same as a webhook success.
    try {
      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ amount_paid: total, balance_due: 0 }),
      });
    } catch (e) { console.warn('amount_paid patch failed:', e); }
    setBills(prev => prev.map(b => b.id === paymentWaitModal.invoiceId ? { ...b, status: 'Paid' as any, invoiceNumber: confirmedNumber, amountPaid: total } : b));
    onAddLog('INVOICE_PAID_CARD_MANUAL', `Invoice ${paymentWaitModal.invoiceNumber} confirmed paid — ${paymentMode}, manually by cashier.`);
    postLog('INVOICE_PAID_CARD_MANUAL', `Invoice ${paymentWaitModal.invoiceNumber} confirmed paid via manual card entry`);
    if (pendingPostPaymentRef.current) {
      await pendingPostPaymentRef.current();
      pendingPostPaymentRef.current = null;
    }
    setPaymentWaitModal(null);
    handleReset();
    setSaved(true);
  };

  // Lets the cashier bail out of a stuck UPI/card payment without
  // re-entering the whole bill. Reuses the SAME pending invoice (line
  // items, client, everything already saved) — just flips payment_mode
  // to cash, assigns it a real number, marks it paid, and fires the same
  // print/WhatsApp/email flow every other payment mode uses.
  const switchToCashAndConfirm = async () => {
    if (!paymentWaitModal) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    setPaymentPolling(false);
    try {
      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ payment_mode: 'cash' }),
      });
      const confirmRes = await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}/confirm-payment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const confirmed = await confirmRes.json();
      const finalNumber = confirmed?.invoice_number || paymentWaitModal.invoiceNumber;
      confirmedInvoiceNumberRef.current = finalNumber;
      // Switched to cash — settled the instant it's confirmed, same as any cash sale.
      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ amount_paid: total, balance_due: 0 }),
      });
      setBills(prev => prev.map(b => b.id === paymentWaitModal.invoiceId
        ? { ...b, status: 'Paid' as any, payment_mode: 'cash' as any, invoiceNumber: finalNumber, amountPaid: total }
        : b));
      onAddLog('INVOICE_PAID_CASH_FALLBACK', `Invoice ${finalNumber} switched from ${paymentMode} to cash and confirmed by cashier.`);
      postLog('INVOICE_PAID_CASH_FALLBACK', `Invoice ${finalNumber} switched to cash payment after ${paymentMode} was stuck`);
    } catch (e) {
      console.warn('switchToCashAndConfirm failed:', e);
      alert('Could not switch to cash — please try again.');
      setPaymentPolling(true);
      return;
    }
    if (pendingPostPaymentRef.current) {
      await pendingPostPaymentRef.current();
      pendingPostPaymentRef.current = null;
    }
    setPaymentWaitModal(null);
    handleReset();
    setSaved(true);
  };

  // Fully discards a pending payment — cancels the invoice on the backend
  // (same /cancel endpoint the failed-payment flow uses) so it doesn't sit
  // around forever as an unpaid PENDING-xxxx row cluttering Bills.
  const discardPendingInvoice = async () => {
    if (!paymentWaitModal) return;
    // Same pattern as POSView's discard — require an actual typed reason
    // instead of a generic confirm, so the audit log/cancellation_reason
    // reflects why the cashier bailed on this sale.
    const reason = window.prompt('Reason for discarding this invoice? (required)');
    if (reason === null) return; // cancelled the prompt
    if (!reason.trim()) { alert('A reason is required to discard an invoice.'); return; }
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    try {
      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      setBills(prev => prev.filter(b => b.id !== paymentWaitModal.invoiceId));
      onAddLog('INVOICE_DISCARDED', `Invoice ${paymentWaitModal.invoiceNumber} discarded before payment confirmed. Reason: ${reason.trim()}`);
      postLog('INVOICE_DISCARDED', `Invoice ${paymentWaitModal.invoiceNumber} discarded — never paid. Reason: ${reason.trim()}`);
    } catch (e) {
      console.warn('Discard failed:', e);
    }
    pendingPostPaymentRef.current = null;
    setPaymentWaitModal(null);
    setPaymentFailed(false);
    handleReset();
  };

  // Fetches a fresh payment link/QR for the SAME pending invoice — no new
  // invoice is created, nothing is re-charged. Lets the cashier recover
  // from a glitched or expired QR (scanner error, gateway hiccup, customer's
  // UPI app timing out) without switching to cash or discarding the sale.
  const regeneratePaymentQR = async () => {
    if (!paymentWaitModal) return;
    setRegeneratingQR(true);
    try {
      const gwRes = await fetch(`${BACKEND}/payments/bill/${paymentWaitModal.invoiceId}`);
      const gwData = gwRes.ok ? await gwRes.json() : null;
      const payLink = gwData?.payment_link || `${window.location.origin}/pay/bill/${paymentWaitModal.invoiceId}`;
      const QRCode = await import('qrcode');
      const qrDataUrl = await QRCode.default.toDataURL(payLink, { width: 260, margin: 2 });
      setPaymentWaitModal(prev => prev ? { ...prev, link: payLink, qr: qrDataUrl } : prev);
      setPaymentFailed(false);
      setPaymentPolling(true);
      if (outletDisplayToken) {
        const jwt = localStorage.getItem('cloudairambo_jwt') || '';
        fetch(`${BACKEND}/display/push/${outletDisplayToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ bill_id: paymentWaitModal.invoiceId, register_id: registerId }),
        }).catch(() => {});
      }
      onAddLog('INVOICE_QR_REGENERATED', `Payment QR regenerated for invoice ${paymentWaitModal.invoiceNumber}.`);
      postLog('INVOICE_QR_REGENERATED', `QR regenerated for pending invoice ${paymentWaitModal.invoiceNumber}`);
    } catch (e) {
      console.warn('QR regeneration failed:', e);
      alert('Could not regenerate the QR — please try again.');
    } finally {
      setRegeneratingQR(false);
    }
  };

  // Lets the cashier add a forgotten item while a card/UPI/online payment
  // is still pending — the invoice exists on the backend but nothing's
  // been charged yet, so this just patches line_items/amount in place.
  // Mirrors POSView's "+ Add Items" button on its token/success modal.
  // Patches the pending invoice with whatever's now in the form, then
  // resumes the payment-wait modal so polling picks back up. Called from
  // the main Save button while editingPendingInvoice is set.
  const updatePendingInvoiceAndResume = async () => {
    if (!editingPendingInvoice) return;
    setSaving(true);
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const newLineItems = lineItems.filter(li => li.description.trim()).map(li => {
        const b = getItemTaxBreakdown(li);
        return {
          description: li.description, qty: li.qty, unit_price: li.rate,
          amount: li.qty * li.rate, tax_rate: getEffectiveTaxRate(li),
          tax_inclusive: !!li.taxInclusive, tax_amount: parseFloat(b.tax.toFixed(2)),
        };
      });
      await fetch(`${BACKEND}/invoices/${editingPendingInvoice.invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ line_items: newLineItems, amount: total, balance_due: total }),
      });
      setBills(prev => prev.map(b => b.id === editingPendingInvoice.invoiceId ? { ...b, amount: total } : b));
      onAddLog('INVOICE_ITEMS_UPDATED', `Invoice ${editingPendingInvoice.invoiceNumber} items updated while awaiting payment — new total ${sym}${total.toFixed(2)}.`);
      // Keep the counter display in sync — same reasoning as POSView.
      if (outletDisplayToken) {
        fetch(`${BACKEND}/display/push/${outletDisplayToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ bill_id: editingPendingInvoice.invoiceId, register_id: registerId }),
        }).catch(() => {});
      }
      // The QR/link the customer was shown still encodes the old amount —
      // resuming it as-is (not regenerating) matches the earlier decision
      // to avoid piling up extra pending Payment rows per edit.
      setPaymentWaitModal(editingPendingInvoice);
      setEditingPendingInvoice(null);
    } catch (e) {
      alert('Could not update the invoice — try again.');
    } finally {
      setSaving(false);
    }
  };

  const [panelWidth, setPanelWidth] = useState(520);
const [panelOffset, setPanelOffset] = useState({ x: 0, y: 0 });
const panelDragging = useRef(false);
const panelDragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });
  const [saved, setSaved] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const selectedTemplate = allTemplates.find(t => t.id === selectedTemplateId);
  const isThermalTemplate = (selectedTemplate?.pageSize || 'A4') === 'THERMAL_80MM' || (selectedTemplate?.pageSize || 'A4') === 'THERMAL_58MM';

  // Auto-scale invoice preview to fit mobile screen without sideways overflow
  useEffect(() => {
    if (isMobile && showMobilePreview) {
      const screenW = Math.max(300, window.innerWidth - 32);
      const isThermal = (selectedTemplate?.pageSize || 'A4') === 'THERMAL_80MM';
      const targetW = isThermal ? 302 : (PAGE_CONFIGS[selectedTemplate?.pageSize || 'A4']?.width || 794);
      const fitZoom = Math.min(1, screenW / targetW);
      setZoom(parseFloat(fitZoom.toFixed(2)));
    } else if (!isMobile) {
      setZoom(1);
    }
  }, [isMobile, showMobilePreview, selectedTemplateId]);
  const [previewPageBehavior, setPreviewPageBehavior] = useState<'fixed' | 'auto_expand' | 'multi_page'>('auto_expand');
  const previewRef = useRef<HTMLDivElement>(null);
  const invoiceWrapperRef = useRef<HTMLDivElement>(null);
  const previewCanvasRef = useRef<HTMLDivElement>(null);
  const [measuredTableBottom, setMeasuredTableBottom] = useState<number | null>(null);
const [autoFitScale, setAutoFitScale] = useState(1);
const [invoiceNaturalHeight, setInvoiceNaturalHeight] = useState(0);
const [previewWidth, setPreviewWidth] = useState(660);

useEffect(() => {
  if (!previewRef.current) return;
  const ro = new ResizeObserver(entries => {
    const w = entries[0].contentRect.width;
    setPreviewWidth(Math.floor(w - 64));
  });
  ro.observe(previewRef.current);
  return () => ro.disconnect();
}, []);

// Line items
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: `li-${Date.now()}`, description: '', qty: 1, rate: 0 },
  ]);


const [invoiceNumberOverride, setInvoiceNumberOverride] = useState('');
  // Payment mode defaults to 'cash', which is settled the instant it's
  // tendered — default status to match. If the user switches payment mode
  // away from cash without touching status, the PAY_MODES button handler
  // below leaves this as whatever it was, which is fine since cards/UPI
  // aren't confirmed at save time anyway.
  const [invoiceStatus, setInvoiceStatus] = useState<'draft'|'paid'|'unpaid'|'overdue'|'cancelled'>('paid');
  const [paymentMode, setPaymentMode] = useState<'cash' | 'card' | 'upi'>('cash');
  const [customColumns, setCustomColumns] = useState<{id: string; header: string; key: string; align: 'left'|'center'|'right'; after?: string}[]>(() => {
    if (editInvoice) return [];
    try {
      const saved = localStorage.getItem('qi_custom_columns');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    if (editInvoice) return [];
    try {
      const saved = localStorage.getItem('qi_hidden_columns');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Automatically persist custom & hidden columns whenever they change (except in edit mode)
  useEffect(() => {
    if (!isEditMode) {
      try {
        localStorage.setItem('qi_custom_columns', JSON.stringify(customColumns));
        localStorage.setItem('qi_hidden_columns', JSON.stringify(hiddenColumns));
      } catch {}
    }
  }, [customColumns, hiddenColumns, isEditMode]);

  const [showAddCol, setShowAddCol] = useState(false);
  const [newColName, setNewColName] = useState('');

  // Table columns of the selected template
  const templateTableCols: any[] = (selectedTemplate?.elements?.find((el: any) => el.type === 'table') as any)?.columns || [];
  const mainColKey = (templateTableCols.find((c: any) => c.key === 'description') || templateTableCols[0])?.key || 'description';
  const toggleableBase: { key: string; label: string }[] = templateTableCols.length > 0
    ? templateTableCols
        .filter((c: any) => c.key && c.key !== mainColKey)
        .map((c: any) => ({ key: c.key, label: c.originalHeader || c.header || c.key }))
    : [
        { key: 'qty', label: 'Qty' },
        { key: 'rate', label: 'Rate' },
        { key: 'total', label: 'Amt' },
      ];
const [showScanner, setShowScanner] = useState(false);
const [showUpgradeNudge, setShowUpgradeNudge] = useState<string | null>(null);
const [showPrintSettings, setShowPrintSettings] = useState(false);
const [useUSBPrinter, setUseUSBPrinter] = useState(() =>
  localStorage.getItem('cloudairambo_qi_usb_print') === 'true'
);
const [usbConnected, setUsbConnected] = useState(false);
const [useBTPrinter, setUseBTPrinter] = useState(() =>
  localStorage.getItem('cloudairambo_qi_bt_print') === 'true'
);
const [btConnected, setBtConnected] = useState(() => !!getBTPrinterName());
const [availablePrinters, setAvailablePrinters] = useState<string[]>([]);
const [selectedPrinter, setSelectedPrinter] = useState(() =>
  localStorage.getItem('cloudairambo_qi_selected_printer') || ''
);
const [isPrinting, setIsPrinting] = useState(false);

useEffect(() => {
  tryAutoReconnectUSB().then(ok => setUsbConnected(ok || isUSBPrinterConnected()));
}, []);

useEffect(() => {
  if (!showPrintSettings) return;
  fetch(`${BACKEND}/print/printers`, { headers: { Authorization: `Bearer ${getStoredToken()}` } })
    .then(r => r.json())
    .then(data => setAvailablePrinters(data.printers || []))
    .catch(() => {});
}, [showPrintSettings]);
const [catalogProducts, setCatalogProducts] = useState<{id:number;name:string;rate:number;unit?:string;barcode:string;tax_rate?:number;b2b_tax_rate?:number;tax_inclusive?:boolean;custom_fields?:Record<string,string>}[]>([]);

// Distinct custom-field column names already used in the Product Catalog —
// offered as a pick-list in Quick Invoice's "Add Col" so a column typed
// here actually matches the catalog's key instead of creating a silently
// separate one (e.g. "Batch No" vs "batch no").
const catalogCustomFieldKeys = React.useMemo(() => {
  const keys = new Set<string>();
  catalogProducts.forEach(p => Object.keys(p.custom_fields || {}).forEach(k => keys.add(k)));
  return Array.from(keys);
}, [catalogProducts]);

// Built-in catalog fields that can become invoice columns.
// Keys start with cat_ so they never clash with qty/rate/taxRate.
const BUILTIN_CATALOG_COLS: { key: string; label: string; header?: string; get: (p: any) => string }[] = [
  { key: 'cat_barcode',      label: 'Barcode',     get: p => p.barcode || '' },
  { key: 'cat_brand',        label: 'Brand',       get: p => p.brand || '' },
  { key: 'cat_category',     label: 'Category',    get: p => p.category || '' },
  { key: 'cat_unit',         label: 'Unit',        get: p => p.unit || '' },
  { key: 'cat_tax_b2c', label: 'Tax (B2C)', header: 'Tax', get: p => p.tax_rate ? `${p.tax_rate}%` : '' },
  { key: 'cat_tax_b2b', label: 'Tax (B2B)', header: 'Tax', get: p => { const r = (p.b2b_tax_rate !== undefined && p.b2b_tax_rate !== null && p.b2b_tax_rate !== '') ? p.b2b_tax_rate : p.tax_rate; return r ? `${r}%` : ''; } },
  { key: 'cat_tax_name',     label: 'Tax Name',    get: p => p.tax_name || '' },
  
  { key: 'cat_tax_type',     label: 'Tax Type',    get: p => p.tax_inclusive ? 'Inclusive' : 'Exclusive' },
  { key: 'cat_stock',        label: 'Stock',       get: p => p.stock_quantity != null ? String(p.stock_quantity) : '' },
  { key: 'cat_description',  label: 'Description', get: p => p.description || '' },
];

// Dropdown list: built-ins first, then the catalog's custom fields.
// A custom field with the same name as a built-in is hidden (no duplicates).
const catalogColumnOptions = React.useMemo(() => {
  const builtinLabels = new Set(BUILTIN_CATALOG_COLS.map(c => c.label.toLowerCase()));
  return [
    ...BUILTIN_CATALOG_COLS.map(c => ({ key: c.key, label: c.label, header: c.header || c.label })),
    ...catalogCustomFieldKeys
      .filter(k => !builtinLabels.has(k.toLowerCase()))
      .map(k => ({ key: k.toLowerCase().replace(/\s+/g, '_'), label: k, header: k })),
  ];
}, [catalogCustomFieldKeys]);

// Reads a column's value off a catalog product (built-in, direct property, or custom field)
const getCatalogColValue = (p: any, col: { key: string; header: string }): string | undefined => {
  if (!p) return undefined;
  const builtin = BUILTIN_CATALOG_COLS.find(c => c.key === col.key);
  if (builtin) return builtin.get(p);

  const targetNorm = (col.header || col.key).toLowerCase().replace(/[\s_-]/g, '');

  // 1. Check direct properties on product (e.g., p.cost_price, p.costPrice, p.shelf_no, p.shelf)
  if (targetNorm === 'costprice' || targetNorm === 'cost') {
    const cp = p.cost_price ?? p.costPrice ?? p.cost ?? p.purchase_price;
    if (cp != null && cp !== '') return String(cp);
  }
  if (p[col.key] != null && p[col.key] !== '') return String(p[col.key]);
  if (p[col.header] != null && p[col.header] !== '') return String(p[col.header]);

  for (const [k, v] of Object.entries(p)) {
    if (k.toLowerCase().replace(/[\s_-]/g, '') === targetNorm && v != null && v !== '') {
      return String(v);
    }
  }

  // 2. Safely parse custom_fields if returned as string from database
  let fields: Record<string, any> = {};
  if (typeof p.custom_fields === 'string') {
    try { fields = JSON.parse(p.custom_fields); } catch {}
  } else if (p.custom_fields && typeof p.custom_fields === 'object') {
    fields = p.custom_fields;
  }

  // 3. Direct key or header check on custom_fields
  if (fields[col.key] != null && fields[col.key] !== '') return String(fields[col.key]);
  if (fields[col.header] != null && fields[col.header] !== '') return String(fields[col.header]);

  // 4. Normalized case-insensitive fallback on custom_fields
  for (const [k, v] of Object.entries(fields)) {
    if (k.toLowerCase().replace(/[\s_-]/g, '') === targetNorm && v != null && v !== '') {
      return String(v);
    }
  }

  return undefined;
};
const [productDropdownId, setProductDropdownId] = useState<string|null>(null);

// Close product dropdown cleanly when clicking outside, without timeout conflicts between rows
useEffect(() => {
  const handleDocClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.qi-product-search-container')) {
      setProductDropdownId(null);
    }
  };
  document.addEventListener('mousedown', handleDocClick);
  return () => document.removeEventListener('mousedown', handleDocClick);
}, []);

const [activeScanSession, setActiveScanSession] = useState<string|null>(null);
const scanPollRef = useRef<ReturnType<typeof setInterval>|null>(null);

useEffect(() => {
  if (!activeScanSession) {
    if (scanPollRef.current) clearInterval(scanPollRef.current);
    return;
  }
  scanPollRef.current = setInterval(async () => {
    try {
      const res = await fetch(`${BACKEND}/scan-session/${activeScanSession}`);
      if (!res.ok) return;
      const data = await res.json();
      if (!data.value) return;
      // Find product in catalog by barcode or name
      const match = catalogProducts.find(p =>
        p.barcode === data.value || p.name.toLowerCase() === data.value.toLowerCase()
      );
      if (match) {
        handleScanAdd(match.name, 1, match.rate, match.barcode, {
          taxRate: match.tax_rate,
          b2bTaxRate: match.b2b_tax_rate,
          taxInclusive: match.tax_inclusive,
        });
      } else {
        handleScanAdd(data.value, 1, 0);
      }
      // Clear the session value after consuming
      await fetch(`${BACKEND}/scan-session/${activeScanSession}`, { method: 'DELETE' });
    } catch { /* silent */ }
  }, 800);
  return () => { if (scanPollRef.current) clearInterval(scanPollRef.current); };
}, [activeScanSession, catalogProducts]);
useEffect(() => {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const url = outletId
    ? `${BACKEND}/products/?outlet_id=${outletId}`
    : `${BACKEND}/products/`;
  fetch(url, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  })
    .then(r => r.json())
    .then(data => setCatalogProducts(Array.isArray(data) ? data : []))
    .catch(() => setCatalogProducts([]));
}, [outletId]);

// Navbar resolves outlet names via a separate /outlets fetch — authUser
// only carries outlet_name_display when the backend happens to set it,
// which is why this badge was falling back to "Outlet #<id>". Fetch the
// same list Navbar uses so the badge can show the real name too.
const [resolvedOutletName, setResolvedOutletName] = useState<string>('');
useEffect(() => {
  if (outletId == null) { setResolvedOutletName(''); return; }
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  fetch(`${BACKEND}/outlets`, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
    .then(r => r.json())
    .then((data: any[]) => {
      if (!Array.isArray(data)) return;
      const match = data.find(o => o.id === outletId);
      setResolvedOutletName(match?.name || '');
    })
    .catch(() => {});
}, [outletId]);
const [translatedValues, setTranslatedValues] = useState<Record<string, string>>({});
// Arabic/target-language translations of each Template Field's LABEL (e.g.
// "Patient" -> "المريض"), keyed by the field's key (same keys as
// dynamicValues/dynamicFields). Populated in handleQuickInvTranslate.
// Without this, BilingualInvoiceRenderer's patchElArabicText has no
// translated label to fall back to and silently leaves every field label
// (Patient/Age/Power R/L/PD/Bill/Date/etc.) in English on the Arabic side.
const [dynamicFieldLabelsAr, setDynamicFieldLabelsAr] = useState<Record<string, string>>({});
// Transliterated CGST/SGST/IGST/QST/etc. component names, keyed by the
// English name — these are acronyms, not dictionary words, so translateText
// just echoes them back. Populated in handleQuickInvTranslate from the same
// combined-breakdown computation the live preview and BilingualInvoiceRenderer
// use. Consumed by both the A4 bilingual renderer and the thermal one.
const [taxBreakdownAr, setTaxBreakdownAr] = useState<Record<string, string>>({});
const translateTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
const [quickInvLang, setQuickInvLang] = useState(() => 
  localStorage.getItem('cloudairambo_qi_lang') || 'en'
);

const [isQuickInvTranslating, setIsQuickInvTranslating] = useState(false);
const quickInvTranslateInFlightRef = useRef<Promise<any> | null>(null);
const [quickInvTranslatedTemplate, setQuickInvTranslatedTemplate] = useState<any>(null);
const [bilingualEnabled, setBilingualEnabled] = useState(() =>
  localStorage.getItem('cloudairambo_qi_bilingual_enabled') === 'true'
);
const [bilingualLayout, setBilingualLayout] = useState<'side' | 'stacked'>('side');
const translatedTemplateRef = useRef<any>(null);
// Mirrors translatedValues state but updates synchronously — handlePrintPreview
// awaits handleQuickInvTranslate() then immediately reads translated values in
// the SAME function call. setTranslatedValues schedules a re-render; it does
// NOT update the `translatedValues` variable already captured in that running
// function's closure. Any column/value translated by that in-flight call was
// silently invisible to print until the NEXT render. Read this ref instead.
const translatedValuesRef = useRef<Record<string, string>>({});
const patchedTemplateRef = useRef<any>(null);
// Captures exactly what the live preview last rendered — the single
// source of truth for layout. Populated inside the preview's render
// (selectedTemplate && (() => {...})() block) every time it runs.
// handleGenerate patches only the invoice number + real QR into this,
// instead of rebuilding positions independently.
const livePreviewElementsRef = useRef<any[] | null>(null);
// Bumped whenever we enter edit mode — invalidates any still-running
// create-flow completion (sendPostPaymentDeliverables) so it can't fire
// setSaved(true) on a form that has since moved into a different invoice's
// edit context (e.g. "+ Add Item" clicked before the original save's
// background work — PDF/email/WhatsApp — finished).
const saveGenerationRef = useRef(0);

const bilingualLayoutRef = useRef<'side' | 'stacked'>('side');


const handleQuickInvTranslate = async () => {
  if (quickInvTranslateInFlightRef.current) {
    return quickInvTranslateInFlightRef.current;
  }

  const runTranslate = async () => {
    setIsQuickInvTranslating(true);
    try {
    const token = getStoredToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const tl = quickInvLang;

    const transliterateName = async (text: string) => {
      if (!text?.trim()) return text;
      try {
        const res = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(text.trim())}&tl=${tl}`, { headers });
        const data = await res.json();
        return data.transliterated || text;
      } catch { return text; }
    };

    const translateText = async (text: string) => {
      if (!text?.trim()) return text;
      try {
        const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(text.trim())}&tl=${tl}`, { headers });
        const data = await res.json();
        let translated = data.translated || text;

        if (tl !== 'en' && /[A-Za-z]{2,}/.test(translated)) {
          const latinWords = Array.from(new Set(translated.match(/[A-Za-z]{2,}/g) || []));
          for (const word of latinWords) {
            if (/^\{.*\}$/.test(word) || word.includes('@') || /^(https?|www|net|am|pm)$/i.test(word)) continue;
            const tr = await transliterateName(word);
            if (tr && tr.toLowerCase() !== word.toLowerCase()) {
              translated = translated.replace(new RegExp(`\\b${word}\\b`, 'g'), tr);
            }
          }
        }

        return translated;
      } catch { return text; }
    };

    // Batch translate helper: Sends a whole list in 1 single API call
    const translateBatchText = async (texts: string[]): Promise<string[]> => {
      if (!texts.length) return [];
      try {
        const res = await fetch(`${BACKEND}/translate/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify({ texts, tl }),
        });
        if (!res.ok) return texts;
        const data = await res.json();
        return Array.isArray(data?.translations) ? data.translations : texts;
      } catch {
        return texts;
      }
    };

    // Language-agnostic label translation: normalize casing, retry with
    // abbreviations expanded, then fall back to transliteration.
    const translateLabel = async (label: string) => {
      const clean = label.trim();
      const titled = clean.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
      const echoed = (r: string) => !r || r.trim().toLowerCase() === clean.toLowerCase();
      let r = await translateText(titled);
      if (echoed(r) && /\bId\b/.test(titled)) {
        r = await translateText(titled.replace(/\bId\b/, 'Identification Number'));
      }
      if (echoed(r)) r = await transliterateName(clean);
      return r;
    };

    const translateLineBySegments = async (line: string) => {
      if (!line.trim()) return line;
      const activeStaff = authUser?.name?.trim() || 'Staff';
      const dynamicCust = (
        dynamicValues.patient ||
        dynamicValues.guest ||
        dynamicValues.client ||
        dynamicValues.customer ||
        dynamicValues.student ||
        dynamicValues.pet ||
        ''
      ).trim();
      const activeClient = (includeFields.name && clientName.trim())
        ? clientName.trim()
        : (dynamicCust || 'Walk-in Customer');
      line = line
        .replace(/\{staff_name\}/gi, activeStaff)
        .replace(/(Patient|Member|Student|Customer|Client|Pet|Guest):\s*\[Name\]/gi, `$1: ${activeClient}`)
        .replace(/\[(Name|Patient|Client|Customer|Guest|Member|Student|Pet)\]/gi, activeClient)
        .replace(/\[Name\]/gi, activeClient);
      const boundaryMarked = line.replace(
        /([^\s\u0000])\s*(?=[A-Za-z][A-Za-z]{0,23}:\S)/g,
        '$1\u0000'
      );
      const segments = boundaryMarked
        .split(/(\u0000|\s*\|\s*|\s*,\s*|\s{2,})/)
        .filter(s => s.length > 0);
      const translatedSegments = await Promise.all(
        segments.map(async (seg) => {
          if (seg === '\u0000') return '   ';
          if (/^(\s*\|\s*|\s*,\s*|\s{2,})$/.test(seg)) return seg;
          const trimmed = seg.trim();
          if (!trimmed) return seg;
          const fieldMatch = trimmed.match(/^([A-Za-z][A-Za-z\s]{1,24}?):\s*(\S.*)$/);
          if (fieldMatch) {
            let [, fieldLabel, fieldValue] = fieldMatch;
            fieldLabel = fieldLabel.trim();
            const cleanVal = fieldValue.trim().replace(/(\d)\s+to\s+(\d)/i, '$1 – $2');

            let labelFinal = '';
            if (tl === 'ar' && /^barista$/i.test(fieldLabel)) labelFinal = 'باريستا';
            else if (tl === 'ar' && /^table$/i.test(fieldLabel)) labelFinal = 'طاولة';
            else if (tl === 'ar' && /^cashier$/i.test(fieldLabel)) labelFinal = 'كاشير';
            else if (tl === 'ar' && /^waiter$/i.test(fieldLabel)) labelFinal = 'نادل';
            else {
              labelFinal = await translateLabel(fieldLabel);
            }

            const isPlaceholder = /^\{[^}]+\}$/.test(cleanVal);
            const isNumericOrCode = isLiteralFieldValue(cleanVal);
            
            const valueTranslated = (isPlaceholder || isNumericOrCode)
              ? (tl === 'ar' && /^[\d\s+\-().#:/\\%]+$/.test(cleanVal) ? cleanVal.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]) : cleanVal)
              : await translateText(cleanVal);

            return `${labelFinal}: ${valueTranslated}`;
          }
          if (/^\{[^}]+\}$/.test(trimmed)) return trimmed;
          return await translateText(trimmed);
        })
      );
      return translatedSegments.join('');
    };

    const template = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
    if (template) {
      const cloned = JSON.parse(JSON.stringify(template));

      const isPlaceholderQr = (qrEl: any) =>
        cloned.elements.some((other: any) =>
          other.type === 'text' &&
          (other.textContent === 'Scan to view invoice' || other.textContent === 'Scan to track your order') &&
          Math.abs((other.y || 0) - ((qrEl.y || 0) + (qrEl.height || 0))) < 40 &&
          Math.abs((other.x || 0) - (qrEl.x || 0)) < (qrEl.width || 0) + 40
        );

      cloned.elements = cloned.elements.filter((el: any) => el.type !== 'qr' || !isPlaceholderQr(el));

      const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;

      cloned.elements = await Promise.all(
        cloned.elements.map(async (el: any) => {
          if (el.arabicContent) el = { ...el, arabicContent: undefined };
          if (el.type === 'text' && el.textContent?.trim()) {
            const firstLine = el.textContent.split('\n')[0].trim();
            if (targetLabelRegex.test(firstLine)) {
              const effectiveName = includeFields.name && clientName.trim() ? [clientName.trim()] : [];
              const buyerTaxLines = includeFields.taxId && isValidTaxIdString(clientGSTIN) ? [`Tax ID: ${clientGSTIN.trim()}`] : [];
              const validEmail = (includeFields.email && clientEmail.includes('@')) ? [clientEmail.trim()] : [];
              const validPhone = (includeFields.phone && /[\d\+\-\(\)\s]{6,}/.test(clientPhone)) ? [clientPhone.trim()] : [];
              const validAddr = (includeFields.address && clientAddress.trim()) ? [clientAddress.trim()] : [];

              // Keep the template's own header (SELLER:, Bill To:, Patient:, ...)
              const tplLines = el.textContent.split('\n');
              const headerLabel = `${(tplLines[0].split(':')[0] || 'Bill To').trim()}:`;
              const isStdLine = (l: string) => {
                const t = l.trim();
                return !t
                  || targetLabelRegex.test(t)
                  || /^(tax\s*id|vat\s*no|gstin|trn|phone|tel|mobile|mob|contact|cell|email|e-mail|mail|address|adress|addr|attention)\s*[:\-]/i.test(t);
              };
              // Custom "Label: value" lines (STORE ID, PERIOD, ...) with live sidebar values
              const customLines = tplLines.slice(1)
                .filter(l => /^[^:@]{1,30}:\s*\S/.test(l.trim()) && !isStdLine(l))
                .map(l => {
                  const idx = l.indexOf(':');
                  const lab = l.slice(0, idx).trim();
                  const f = dynamicFields.find(d => d.label.toLowerCase() === lab.toLowerCase());
                  const v = f ? (dynamicValues[f.key] || '').trim() : '';
                  return v ? `${lab}: ${v}` : l;
                });
              const userAttn = (dynamicValues.attention || dynamicValues.attn || '').trim();
              const lines: string[] = [
                headerLabel,
                ...effectiveName,
                ...buyerTaxLines,
                ...validAddr,
                ...validEmail,
                ...validPhone,
                ...(userAttn ? [`Attention: ${userAttn}`] : []),
                ...customLines,
              ];
              const patched = lines.join('\n');
              const customStart = lines.length - customLines.length;
              const taxLabelAr = isValidTaxIdString(clientGSTIN) ? await translateText('Tax ID') : '';

              const isNameLine = (l: string) => !l.includes(':') && !l.includes('@') && !/^[\d\s+\-().]{6,}$/.test(l.trim());

              const translatedLines = await Promise.all(
                lines.map(async (l: string, i: number) => {
                  if (i === 0) return `${await translateText(headerLabel.replace(/:$/, ''))}:`;
                  if (includeFields.name && i === 1 && isNameLine(l)) return transliterateName(l);
                  if (i >= customStart || /^Attention:/i.test(l)) return translateLineBySegments(l);
                  if (/^Tax ID:/i.test(l)) return `${taxLabelAr}: ${l.replace(/^Tax ID:\s*/i, '')}`;
                  if (l.includes('@')) return l;                          // email stays as-is
                  if (/^[\d\s+\-().]{6,}$/.test(l.trim())) return l;      // phone stays as-is
                  return transliterateName(l);                            // address
                })
              );
              if (includeFields.name && clientName.trim()) {
                translatedValuesRef.current = { ...translatedValuesRef.current, clientName: translatedLines[1] };
                setTranslatedValues(prev => ({ ...prev, clientName: translatedLines[1] }));
              }
              if (validAddr.length > 0) {
                const addrIdx = lines.indexOf(validAddr[0]);
                const addrAr = addrIdx > 0 ? translatedLines[addrIdx] : '';
                if (addrAr) {
                  translatedValuesRef.current = { ...translatedValuesRef.current, clientAddress: addrAr };
                  setTranslatedValues(prev => ({ ...prev, clientAddress: addrAr }));
                }
              }
              return { ...el, textContent: patched, arabicContent: translatedLines.join('\n') };
            }

            const lines = el.textContent.split('\n');
            const translatedLines = await Promise.all(lines.map((l: string) => translateLineBySegments(l)));
            let joinedArabic = translatedLines.join('\n');

            if (/^notes:|^terms:/i.test(firstLine) && clientName.trim() && joinedArabic.includes(clientName.trim())) {
              const translitClientName = await transliterateName(clientName.trim());
              joinedArabic = joinedArabic.split(clientName.trim()).join(translitClientName);
            }
            return { ...el, arabicContent: joinedArabic };
          }

          // Stamps ("FEES SETTLED", "PAID", "APPROVED", etc.) — translate any custom stamp text
          if (el.type === 'stamp') {
            const rawStamp = (el.stampText || el.textContent || '').trim();
            if (rawStamp) {
              return { ...el, arabicContent: await translateText(rawStamp) };
            }
          }

          // Signatures — transliterate staff name (or fallback parts) cleanly
          if (el.type === 'signature') {
            const capAr = el.textContent?.trim() ? await translateText(el.textContent.trim()) : '';
            let nameAr = '';
            if (el.signatureName?.trim()) {
              if (/\{staff_name\}/i.test(el.signatureName)) {
                const author = authUser?.name?.trim() || 'Staff';
                nameAr = await transliterateName(author);
              } else {
                const parts = el.signatureName.split(',').map(p => p.trim()).filter(Boolean);
                const transliteratedParts = await Promise.all(
                  parts.map(async p => await transliterateName(p))
                );
                nameAr = transliteratedParts.join(', ');
              }
            }
            return { ...el, arabicContent: capAr || undefined, arabicSignatureName: nameAr || undefined };
          }

          // Any other element type a user adds that carries plain text
          // (skips images/QR/shapes and data-URL / http content)
          if (
            !['text', 'table', 'qr', 'image', 'shape', 'stamp', 'signature'].includes(el.type) &&
            typeof el.textContent === 'string' &&
            el.textContent.trim() &&
            !/^(data:|https?:)/i.test(el.textContent.trim())
          ) {
            const lines = el.textContent.split('\n');
            const translated = await Promise.all(lines.map((l: string) => translateLineBySegments(l)));
            return { ...el, arabicContent: translated.join('\n') };
          }

          if (el.type === 'table') {
            const translatedCols = await Promise.all(
              (el.columns || []).map(async (col: any) => {
                const orig = col.originalHeader || col.header;
                const forTranslation =
                  orig === 'Amt' ? 'Amount' :
                  orig === 'Qty' ? 'Quantity' :
                  orig === 'Sno' || orig === 'S.No' || orig === '#' ? 'Serial Number' :
                  orig;
                return {
                  ...col,
                  originalHeader: orig,
                  arabicHeader: await translateText(forTranslation),
                  header: orig,
                };
              })
            );

            const translatedCustomCols = await Promise.all(
              customColumns.map(async (col) => ({
                ...col,
                originalHeader: col.header,
                arabicHeader: await translateText(col.header),
                align: 'right' as const,
              }))
            );
            return { ...el, columns: placeCustomColumns(translatedCols, translatedCustomCols) };
          }
          return el;
        })
      );

      // Force translate all Line Item descriptions in 1 SINGLE BATCH CALL
      const activeLineItems = lineItems.filter(li => li.description.trim());
      const itemDescriptions = activeLineItems.map(li => li.description.trim());
      const translatedDescriptions = await translateBatchText(itemDescriptions);

      const itemArabicMap: Record<string, string> = {};
      activeLineItems.forEach((li, index) => {
        const val = translatedDescriptions[index];
        if (val) itemArabicMap[`item_${li.id}`] = val;
      });

      // Force translate & transliterate custom column cell values (e.g. Tax Name "rihan" -> "ريحان")
      const customCellPairs: [string, string][] = [];
      for (const col of customColumns) {
        for (const li of lineItems) {
          const val = (li as any)[col.key] ?? (li as any)[col.header] ?? (col.key === 'cat_tax_name' ? ((li as any).taxName || (li as any).b2bTaxName) : undefined);
          if (val && String(val).trim()) {
            const cleanVal = String(val).trim();
            // Purely numeric/percentage values (e.g. "5%", "12.5") have
            // nothing to translate or transliterate — translateText just
            // echoes them back unchanged, which was producing a duplicate
            // "5%" / "5%" line under Tax instead of one real Arabic value.
            // Push an empty string so the renderer's "only show if it
            // differs from the English value" check skips this cell.
            const isNumericOrPercent = /^[\d.,%\s]+$/.test(cleanVal);
            let translatedVal = cleanVal;
            if (!isNumericOrPercent) {
              // Try translate first; if it stays in English (like proper names "rihan"), transliterate into Arabic script
              translatedVal = await translateText(cleanVal);
              if (translatedVal.toLowerCase() === cleanVal.toLowerCase() && /[a-zA-Z]/.test(cleanVal)) {
                translatedVal = await transliterateName(cleanVal);
              }
            }
            const finalVal = isNumericOrPercent ? '' : translatedVal;
            customCellPairs.push([`custom_${li.id}_${col.key}`, finalVal]);
            customCellPairs.push([`custom_${li.id}_${col.key.replace(/^cat_/, '')}`, finalVal]);
            customCellPairs.push([`custom_${li.id}_${col.header}`, finalVal]);
            customCellPairs.push([`tax_name_${cleanVal}`, finalVal]);
          }
        }
      }

      // Translate QR captions
      const statusTextToTranslate = invoiceStatus === 'paid' ? 'PAID IN FULL' : (invoiceStatus || 'PAID').toUpperCase();
      const [invoiceCaptionAr, trackingCaptionAr, statusStampAr] = await Promise.all([
        translateText('Scan to view invoice'),
        translateText('Scan to track your order'),
        translateText(statusTextToTranslate),
      ]);

      // Translate standard labels and field labels in 1 SINGLE BATCH CALL
      const STANDARD_LABELS = ['Invoice #', 'Bill', 'Date', 'Due Date', 'PO #', 'Subtotal', 'Total', 'Shipping', 'Tax', 'Discount', 'Amount Paid', 'Balance Due', 'Email', 'Phone', 'Tax ID', 'Address', 'Bill To'];
      const fieldLabels = dynamicFields.map(f => f.label);
      
      const allLabelsToBatch = [...STANDARD_LABELS, ...fieldLabels];
      const translatedBatchLabels = await translateBatchText(allLabelsToBatch);

      const stdMap: Record<string, string> = {};
      STANDARD_LABELS.forEach((label, idx) => {
        stdMap[label] = translatedBatchLabels[idx] || label;
      });

      const fieldMap: Record<string, string> = {};
      dynamicFields.forEach((f, idx) => {
        fieldMap[f.key] = translatedBatchLabels[STANDARD_LABELS.length + idx] || f.label;
      });

      setDynamicFieldLabelsAr({
        ...fieldMap,
        ...stdMap,
        tax_id: stdMap['Tax ID'],
        email: stdMap['Email'],
        phone: stdMap['Phone'],
        address: stdMap['Address'],
      });

      // Translate dynamic values
      const dynamicValPairs = await Promise.all(
        Object.entries(dynamicValues).map(async ([k, val]) => {
          if (!val || !val.trim()) return [k, ''] as const;
          const trimmed = val.trim();
          if (isLiteralFieldValue(trimmed)) {
            return [k, trimmed.replace(/(\d)\s+to\s+(\d)/i, '$1 – $2')] as const;
          }
          const translated = await translateText(trimmed);
          return [k, translated] as const;
        })
      );

      // Translate Amount in Words
      const amountWordsText = amountToWords(total, activeCurrency);
      const amountWordsAr = await translateText(amountWordsText);

      // Transliterates sound into Hindi (Loyal2 -> लॉयल 2) just like rihan -> रिहान
      const smartTranslateLabel = async (text: string, fallback: string) => {
        const raw = (text || fallback).trim();
        if (!raw) return '';

        const numMatch = raw.match(/(\d+[\s%]*)$/);
        const baseWords = raw.replace(/(\d+[\s%]*)$/, '').trim();
        const trailingNum = numMatch ? ` ${numMatch[1].trim()}` : '';

        if (!baseWords) return raw;

        // 1. Try phonetic transliteration first (e.g. "Loyal" -> "लॉयल", "VIP" -> "वीआईपी", "Gold" -> "गोल्ड")
        let result = await transliterateName(baseWords);

        // 2. If transliteration returned English unchanged, try translation
        if (!result || (result.trim().toLowerCase() === baseWords.toLowerCase() && /[a-zA-Z]/.test(result))) {
          const tr = await translateText(baseWords);
          if (tr && tr.trim().toLowerCase() !== baseWords.toLowerCase()) {
            result = tr;
          } else {
            result = baseWords;
          }
        }

        return `${result || baseWords}${trailingNum}`.trim();
      };

      const taxLabelPairs = await Promise.all(
        taxLines.map(async (t, i) => [`tax_${i}`, await smartTranslateLabel(t.label, 'Tax')] as const)
      );
      const discLabelPairs = await Promise.all(
        discountLines.map(async (d, i) => [`discount_${i}`, await smartTranslateLabel(d.label, 'Discount')] as const)
      );

      // Translate standard row labels for receipts
      const [subtotalAr, totalAr, shippingAr, itemAr, qtyAr, amtAr, taxAr] = await Promise.all([
        translateText('Subtotal'),
        translateText('Total'),
        translateText('Shipping'),
        translateText('Item'),
        translateText('Qty'),
        translateText('Amt'),
        translateText('Total Tax'),
      ]);

      const clientNameArNow = clientName.trim() ? await transliterateName(clientName.trim()) : '';
      const clientAddressArNow = clientAddress.trim() ? await transliterateName(clientAddress.trim()) : '';
      const loyaltyLabelEn = (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0)
        ? (autoDiscountRule.label || 'Loyalty Discount') : '';
      // Uses the same smart detector for Loyalty rule names!
      const loyaltyLabelAr = loyaltyLabelEn ? await smartTranslateLabel(loyaltyLabelEn, 'Loyalty Discount') : '';
      const updatedTranslations: Record<string, string> = {
        ...(clientNameArNow ? { clientName: clientNameArNow } : {}),
        ...(clientAddressArNow ? { clientAddress: clientAddressArNow } : {}),
        ...itemArabicMap,
        ...Object.fromEntries(customCellPairs),
        ...Object.fromEntries(dynamicValPairs.filter(([, v]) => !!v)),
        ...Object.fromEntries(taxLabelPairs),
        ...Object.fromEntries(discLabelPairs),
        ...(loyaltyLabelAr ? { label_loyalty: loyaltyLabelAr } : {}),
        qr_caption_invoice: invoiceCaptionAr,
        qr_caption_tracking: trackingCaptionAr,
        status_stamp: statusStampAr,
        amountInWords: amountWordsAr,
        label_subtotal: subtotalAr,
        label_total: totalAr,
        label_shipping: shippingAr,
        label_item: itemAr,
        label_qty: qtyAr,
        label_amt: amtAr,
        label_tax: taxAr,
      };

      translatedValuesRef.current = { ...translatedValuesRef.current, ...updatedTranslations };
      setTranslatedValues(prev => ({
        ...prev,
        ...updatedTranslations,
      }));

      // Transliterate tax breakdown component names (separates numbers so proper names like rihan4 / rihan3 transliterate cleanly in all 100+ languages)
      if (quickInvTaxBreakdown && quickInvTaxBreakdown.length > 0) {
        const namePairs = await Promise.all(
          quickInvTaxBreakdown.map(async item => {
            const raw = item.name.trim();
            // Split name into letters and numbers (e.g., "rihan4" -> "rihan" + "4")
            const baseWords = raw.replace(/\d+/g, ' ').trim();
            const trailingDigits = raw.match(/\d+$/)?.[0] || '';
            
            let translated = '';
            if (baseWords) {
              translated = await transliterateName(baseWords);
              if (!translated || translated.toLowerCase() === baseWords.toLowerCase()) {
                translated = await translateText(baseWords);
              }
            }
            const finalTranslated = trailingDigits ? `${translated || baseWords} ${trailingDigits}`.trim() : (translated || raw);
            return [item.name, finalTranslated] as const;
          })
        );
        setTaxBreakdownAr(Object.fromEntries(namePairs));
      }

      setQuickInvTranslatedTemplate(cloned);
        translatedTemplateRef.current = cloned;
        return cloned;
      }
      return null;
    } catch (e) {
      console.error('Translation error:', e);
      return null;
    } finally {
      setIsQuickInvTranslating(false);
      quickInvTranslateInFlightRef.current = null;
    }
  };

  quickInvTranslateInFlightRef.current = runTranslate();
  return quickInvTranslateInFlightRef.current;
};

const handleQuickInvTranslateRef = useRef<() => void>(() => {});
useEffect(() => { handleQuickInvTranslateRef.current = handleQuickInvTranslate; });

const autoTranslateQITimer = useRef<any>(null);
const lineItemsSignature = lineItems
  .map(li => `${li.description}:${li.qty}:${getEffectiveTaxRate(li)}:${isB2B ? 1 : 0}:${li.taxInclusive ? 1 : 0}`)
  .join('|');
const customColumnsSignature = customColumns.map(c => `${c.key}@${(c as any).after || ''}`).join('|');
useEffect(() => {
  if (!bilingualEnabled) return;
  if (!lineItems.some(li => li.description.trim())) return;
  clearTimeout(autoTranslateQITimer.current);
  autoTranslateQITimer.current = setTimeout(() => {
    handleQuickInvTranslateRef.current();
  }, 800);
  return () => clearTimeout(autoTranslateQITimer.current);
}, [lineItemsSignature, bilingualEnabled, customColumnsSignature, qiTaxScheme?.kind, isB2B]);

// Don't make a newly-added column wait on the full (network-bound,
// 800ms-debounced) retranslate pass above before it's even visible —
// splice it into quickInvTranslatedTemplate's table columns immediately
// (English header only; the Arabic header fills in once the retranslate
// above resolves and rebuilds the whole snapshot).
useEffect(() => {
  if (!bilingualEnabled || !quickInvTranslatedTemplate) return;
  setQuickInvTranslatedTemplate((prev: any) => {
    if (!prev) return prev;
    let changed = false;
    const nextElements = prev.elements.map((el: any) => {
      if (el.type !== 'table') return el;
      const existingKeys = new Set((el.columns || []).map((c: any) => c.key));
      const missing = customColumns.filter(col => !existingKeys.has(col.key));
      if (missing.length === 0) return el;
      changed = true;
      // col.width is a PERCENTAGE of the table (same as buildCwElements'
      // rebalancing) — appending a flat-width column without shrinking
      // the existing ones pushes the row past 100%, and flex-shrink then
      // visually compresses/wraps every column, which is what made
      // "Shelf No" cram onto two lines here.
      const perNewColWidth = 12;
      const totalNewWidth = perNewColWidth * missing.length;
      const existingCols = el.columns || [];
      const existingTotalWidth = existingCols.reduce((s: number, c: any) => s + (c.width || 0), 0) || 100;
      const shrinkFactor = Math.max(0, (100 - totalNewWidth)) / existingTotalWidth;
      const rebalancedCols = existingCols.map((c: any) => ({ ...c, width: (c.width || 0) * shrinkFactor }));
      return {
        ...el,
        columns: [
          ...rebalancedCols,
          ...missing.map(col => ({
            id: col.id, key: col.key, header: col.header,
            originalHeader: col.header, align: col.align, width: perNewColWidth,
          })),
        ],
      };
    });
    if (!changed) return prev;
    const next = { ...prev, elements: nextElements };
    translatedTemplateRef.current = next;
    return next;
  });
}, [customColumnsSignature, bilingualEnabled]);

 useEffect(() => {
  setQuickInvTranslatedTemplate(null);
}, [selectedTemplateId]);


useEffect(() => {
    if (!editInvoice) return;
    const savedQrPosition = (editInvoice as any).qr_position || (editInvoice as any).autoQrPosition;
    if (savedQrPosition && (savedQrPosition === 'left' || savedQrPosition === 'center' || savedQrPosition === 'right')) {
      setAutoQrPosition(savedQrPosition);
    }
    const tableEl = editInvoice.elements.find((el: any) => el.type === 'table');
    // Universal detector for all headers: SELLER:, BUYER:, BILL TO:, CUSTOMER:, PATIENT:, etc.
    const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;
    const billToEl = editInvoice.elements.find((el: any) =>
      el.type === 'text' && el.textContent &&
      targetLabelRegex.test((el.textContent.split('\n')[0] || '').trim())
    );
    const btLines = billToEl?.textContent?.split('\n') || [];
    const phoneInBlock = btLines.find((l: string) => /^[\d\s+\-().]{6,}$/.test(l.trim()) && !l.includes('@')) || '';
    const TAX_ID_LINE = /\b(tax\s*id|gstin|vat|trn|npwp)\b\s*:\s*(\S+)/i;
    const rawExtractedId = btLines.map((l: string) => l.match(TAX_ID_LINE)?.[2]).find(Boolean) || '';

    const savedName = (
      (editInvoice.clientName && editInvoice.clientName !== 'General Client' && editInvoice.clientName !== 'Customer' && editInvoice.clientName !== 'Walk-in Customer')
        ? editInvoice.clientName
        : (editInvoice as any).client_name ||
          (btLines.length > 1 && !btLines[1].includes(':') && !btLines[1].includes('@') && !/^[\d\s+\-().]{6,}$/.test(btLines[1].trim()) ? btLines[1].trim() : '')
    );

    const savedEmail = editInvoice.clientEmail || (editInvoice as any).client_email || btLines.find((l: string) => l.includes('@')) || '';
    const savedPhone = (editInvoice as any).clientPhone || (editInvoice as any).client_phone || phoneInBlock || '';
    const savedTaxId = String(
      (editInvoice as any).buyerTaxId || (editInvoice as any).buyer_tax_id || (editInvoice as any).clientGSTIN ||
      (tableEl as any)?.buyerTaxId || (tableEl as any)?.buyer_tax_id ||
      rawExtractedId
    ).trim();

    const savedAddr = (editInvoice as any).clientAddress || (editInvoice as any).client_address || btLines.slice(1).filter((l: string) =>
      !l.includes('@') &&
      !/^[\d\s+\-().]{6,}$/.test(l.trim()) &&
      !TAX_ID_LINE.test(l) &&
      !/^[^:]{1,30}:\s*\S/.test(l.trim()) &&
      (!savedName || l.trim().toLowerCase() !== savedName.toLowerCase())
    ).join(', ') || '';

    if (savedName) {
      setClientName(savedName);
      setClientSearch(savedName);
    }
    if (savedEmail) setClientEmail(savedEmail);
    if (savedPhone) setClientPhone(savedPhone);
    if (savedAddr) setClientAddress(savedAddr);
    if (savedTaxId && isValidTaxIdString(savedTaxId)) {
      setClientGSTIN(savedTaxId.replace(/[\s\-\.]/g, '').toUpperCase());
    }

    // Restore saved invoice language and bilingual toggle
    const savedBilingual = (editInvoice as any).isBilingual ?? (editInvoice as any).is_bilingual;
    if (savedBilingual !== undefined) {
      setBilingualEnabled(Boolean(savedBilingual));
    }
    const savedLang = (editInvoice as any).translated_lang || (editInvoice as any).translatedLang || (editInvoice as any).language;
    if (savedLang) {
      setQuickInvLang(savedLang);
      localStorage.setItem('cloudairambo_qi_lang', savedLang);
    }

    // Clear stale Arabic cached translations if the active language is not Arabic
    if (savedLang && savedLang !== 'ar') {
      setTranslatedValues({});
      translatedValuesRef.current = {};
    } else if ((editInvoice as any).dynamic_values_ar) {
      setTranslatedValues((editInvoice as any).dynamic_values_ar);
      translatedValuesRef.current = (editInvoice as any).dynamic_values_ar;
    }

    // If this invoice already remembers which boxes were ticked/unticked, use that directly!
    const savedInclude = (editInvoice as any).includeFields || (editInvoice as any).include_fields;
    if (savedInclude) {
      setIncludeFields(savedInclude);
    } else {
      setIncludeFields({
        name: Boolean(savedName && savedName.trim() && savedName.toLowerCase() !== 'walk-in customer'),
        email: Boolean(savedEmail && savedEmail.trim()),
        phone: Boolean(savedPhone && savedPhone.trim()),
        address: Boolean(savedAddr && savedAddr.trim()),
        taxId: Boolean(savedTaxId && isValidTaxIdString(savedTaxId)),
      });
    }
    setInvoiceStatus((editInvoice.status as any) || 'draft');
    setInvoiceNumberOverride(editInvoice.invoiceNumber || '');
    const savedItemAr: Record<string, string> = {};
    (tableEl?.rows || []).forEach((r: any) => {
      if (r.values?.arabicDescription) savedItemAr[`item_${r.id}`] = r.values.arabicDescription;
    });
    if (Object.keys(savedItemAr).length > 0) {
      setTranslatedValues(prev => ({ ...prev, ...savedItemAr }));
    }
    setLineItems(
  (tableEl?.rows || []).map((r: any) => ({
    id: r.id,
    description: r.values.description || '',
    qty: parseFloat(String(r.values.qty)) || 1,
    rate: parseFloat(String(r.values.rate)) || 0,
    taxRate: parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0,
    taxInclusive: !!(r.values.taxInclusive ?? r.values.tax_inclusive),
    ...Object.fromEntries(
      Object.entries(r.values).filter(([k]) =>
        !['description','qty','rate','total','arabicDescription','taxRate','tax_rate','taxInclusive','tax_inclusive'].includes(k)
      )
    ),
  }))
);

// Load custom columns from saved invoice
if (tableEl?.columns) {
  const defaultKeys = new Set(['description', 'qty', 'rate', 'total', 'sno', 'serial', 'index']);
  const savedCustomCols = (tableEl.columns as any[])
    .filter(col => !defaultKeys.has(col.key))
    .map(col => ({
      id: col.id || `col-${col.key}`,
      header: col.originalHeader || col.header,
      key: col.key,
      align: (col.align as 'left'|'center'|'right') || 'left',
    }));
  setCustomColumns(savedCustomCols);
}
    const taxes = (tableEl as any)?.taxes || [];
    const discounts = (tableEl as any)?.discounts || [];
    const manualOnlyDiscounts = discounts.filter((d: any) => !d._isAuto && !/loyalty/i.test(d.label || ''));
    setTaxLines(taxes.length > 0 ? taxes : [{ label: 'Tax', rate: 0 }]);
    setDiscountLines(manualOnlyDiscounts.length > 0 ? manualOnlyDiscounts : [{ label: 'Discount', rate: 0 }]);
    setShippingCost((tableEl as any)?.shippingCost || 0);
    // Find the template matching the invoice layout
    // In edit mode, use the invoice's own elements as the template
const savedElIds = new Set((editInvoice.elements || []).map((el: any) => el.id).filter(Boolean));
let matchedTemplate: ActiveTemplate | undefined;
let bestScore = -Infinity;
for (const t of allTemplates) {
  if (t.pageSize !== editInvoice.pageSize) continue;
  const overlap = t.elements.filter((el: any) => el.id && savedElIds.has(el.id)).length;
  const score = overlap - (t.elements.length - overlap) * 0.01;
  if (overlap > 0 && score > bestScore) { bestScore = score; matchedTemplate = t; }
}
if (!matchedTemplate) {
  matchedTemplate = allTemplates.find(t => t.id === editInvoice.pageSize || t.pageSize === editInvoice.pageSize);
}
if (matchedTemplate) setSelectedTemplateId(matchedTemplate.id);
  }, [editInvoice?.id]);

  // Entering edit mode must start from a clean slate — `saved` is left
  // over from whatever was last submitted (a previous Generate/Update),
  // and if it's stale-true the button renders "Updated!" before the user
  // has touched anything.
  useEffect(() => {
    if (editInvoice) {
      setSaved(false);
      setSaving(false);
      saveGenerationRef.current++; // invalidate any pending async save completion from before we entered edit mode
    }
  }, [editInvoice?.id]);

  // Fired by App.tsx's post-save modal when the user clicks "Done" —
  // fully clears the form instead of leaving the just-saved invoice's
  // values sitting in it.
  useEffect(() => {
    const onDone = () => handleReset();
    window.addEventListener('quick-invoice-reset', onDone);
    return () => window.removeEventListener('quick-invoice-reset', onDone);
  }, []);
  // Client & Field Visibility Toggles
  const [clientSearch, setClientSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [includeFields, setIncludeFields] = useState({
    name: true,
    phone: true,
    email: true,
    address: true,
    taxId: true,
  });
 

  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const clientTranslateTimer = useRef<any>(null);
  useEffect(() => {
    if (!bilingualEnabled) return;
    if (!lineItems.some(li => li.description.trim())) return;
    clearTimeout(clientTranslateTimer.current);
    clientTranslateTimer.current = setTimeout(() => {
      handleQuickInvTranslateRef.current();
    }, 800);
    return () => clearTimeout(clientTranslateTimer.current);
  }, [clientName, clientEmail, clientPhone, clientAddress, clientGSTIN]);

  // Invoice details
  const toLocalISODate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = toLocalISODate(new Date());
  const due = toLocalISODate(new Date(Date.now() + 30 * 86400000));
const [invoiceDate, setInvoiceDate] = useState(today);
const [dueDate, setDueDate] = useState(due);
const [notes, setNotes] = useState('');
const [showManualDateTime, setShowManualDateTime] = useState(false);
const [showDueDate, setShowDueDate] = useState(false);
const [invoiceTime, setInvoiceTime] = useState(() => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
});
const [userTimezone, setUserTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
const [showTzPicker, setShowTzPicker] = useState(false);
const [tzSearch, setTzSearch] = useState('');
const [tzList, setTzList] = useState<string[]>([]);

// Load timezone list from Intl (free, offline, no API needed)
useEffect(() => {
  // Use built-in Intl timezones — no API needed
  const zones = (Intl as any).supportedValuesOf?.('timeZone') || [
    'UTC','Asia/Kolkata','Asia/Dubai','America/New_York','America/Los_Angeles',
    'Europe/London','Europe/Paris','Asia/Tokyo','Asia/Singapore','Australia/Sydney',
    'America/Chicago','America/Denver','Pacific/Auckland','Asia/Shanghai',
    'Asia/Bangkok','Asia/Karachi','Africa/Cairo','America/Sao_Paulo',
  ];
  setTzList(zones);
}, []);

// When timezone changes, update date/time to match
useEffect(() => {
  const now = new Date();
  const localDate = now.toLocaleDateString('en-CA', { timeZone: userTimezone }); // YYYY-MM-DD
  const localTime = now.toLocaleTimeString('en-US', { timeZone: userTimezone, hour: '2-digit', minute: '2-digit', hour12: true });
  setInvoiceDate(localDate);
  setInvoiceTime(localTime);
}, [userTimezone]);

  // Tax lines
  const [taxLines, setTaxLines] = useState<TaxLine[]>([{ label: 'Tax', rate: 0 }]);
  const [discountLines, setDiscountLines] = useState<DiscountLine[]>([{ label: 'Discount', rate: 0 }]);
  const [shippingCost, setShippingCost] = useState(0);

  useEffect(() => {
    setMeasuredTableBottom(null); // force a fresh measurement — old one may be stale
  }, [lineItems, taxLines, discountLines, shippingCost, customColumns, selectedTemplateId, previewWidth]);

  // Poll the same status endpoint BillsView already polls — once the
  // gateway webhook marks this invoice's payment as 'success', flip it to
  // paid and fire the deliverables (print/email/WhatsApp) that were held
  // back until now.
  useEffect(() => {
    if (!paymentWaitModal) return;
    setPaymentFailed(false);
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    setPaymentPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND}/payments/status/by-invoice`, { headers: { Authorization: `Bearer ${jwt}` } });
        const data = await res.json();
        const match = Array.isArray(data) ? data.find((p: any) => String(p.invoice_id) === String(paymentWaitModal.invoiceId)) : null;
        if (match?.status === 'success') {
          clearInterval(interval);
          setPaymentPolling(false);
          let confirmedNumber = paymentWaitModal.invoiceNumber;
          try {
            const confirmRes = await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}/confirm-payment`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${jwt}` },
            });
            const confirmed = await confirmRes.json();
            if (confirmed?.invoice_number) confirmedNumber = confirmed.invoice_number;
          } catch (e) { console.warn('confirm-payment failed:', e); }
          confirmedInvoiceNumberRef.current = confirmedNumber;
          // Gateway just confirmed payment — mark it fully paid, same as cash at save time.
          try {
            await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
              body: JSON.stringify({ amount_paid: total, balance_due: 0 }),
            });
          } catch (e) { console.warn('amount_paid patch failed:', e); }
          setBills(prev => prev.map(b => b.id === paymentWaitModal.invoiceId ? { ...b, status: 'Paid' as any, invoiceNumber: confirmedNumber, amountPaid: total } : b));
          onAddLog('INVOICE_PAID_ONLINE', `Invoice ${paymentWaitModal.invoiceNumber} paid online — webhook confirmed.`);
          postLog('INVOICE_PAID_ONLINE', `Invoice ${paymentWaitModal.invoiceNumber} confirmed paid via gateway webhook`);
          // Print/email/WhatsApp only fire here — after real confirmation.
          // A declined or abandoned payment never reaches this line.
          if (pendingPostPaymentRef.current) {
            await pendingPostPaymentRef.current();
            pendingPostPaymentRef.current = null;
          }
          setPaymentWaitModal(null);
          handleReset();
          setSaved(true);
        } else if (match?.status === 'failed') {
          // Stop polling and surface failure immediately — don't leave the
          // cashier staring at a spinner. They can retry the same customer
          // or discard and move to the next bill.
          clearInterval(interval);
          setPaymentPolling(false);
          setPaymentFailed(true);
          onAddLog('INVOICE_PAYMENT_FAILED', `Invoice ${paymentWaitModal.invoiceNumber} online payment failed.`);
          postLog('INVOICE_PAYMENT_FAILED', `Invoice ${paymentWaitModal.invoiceNumber} payment failed via gateway webhook`);
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [paymentWaitModal?.invoiceId]);

  const [showAmountInWords, setShowAmountInWords] = useState(() =>
  localStorage.getItem('cloudairambo_qi_amount_in_words') !== 'false'
);
  const [buyerRegion, setBuyerRegion] = useState('');
  const [gstinAutoDetected, setGstinAutoDetected] = useState(false);
  const [showTaxDisclaimerModal, setShowTaxDisclaimerModal] = useState(false);
  const [autoDownloadPdfOnSave, setAutoDownloadPdfOnSave] = useState(() =>
    localStorage.getItem('cloudairambo_qi_autodownload_pdf') !== 'false'
  );
  // clientGSTIN + taxIdMeta + isB2B moved above perms — see comment there.

  useEffect(() => {
    const clean = clientGSTIN.trim();

    // 1. If input is completely cleared, remove state
    if (clean.length === 0) {
      setBuyerRegion('');
      setGstinAutoDetected(false);
      return;
    }

    // 2. If length reached or exceeded expected length but is INVALID, clear state immediately
    if ((taxIdMeta.isComplete || clean.length >= (taxIdMeta.expectedLength || 15)) && !taxIdMeta.isValid) {
      setBuyerRegion('');
      setGstinAutoDetected(false);
      return;
    }

    // 3. If a national-tax country without regional sales tax is matched (UAE, Saudi, etc.), clear state
    if (!REGIONAL_TAX_COUNTRIES.has(taxIdMeta.countryCode) && !taxIdMeta.detectedRegion) {
      setBuyerRegion('');
      setGstinAutoDetected(false);
      return;
    }

    // 4. Valid state detected from tax ID (India, US State, Canada Province, Brazil State, etc.)
    if (taxIdMeta.detectedRegion) {
      setBuyerRegion(taxIdMeta.detectedRegion);
      setGstinAutoDetected(true);
    }
  }, [taxIdMeta.detectedRegion, taxIdMeta.isComplete, taxIdMeta.isValid, taxIdMeta.countryCode, taxIdMeta.expectedLength, clientGSTIN]);

  useEffect(() => {
    const fit = () => {
  if (!invoiceWrapperRef.current || !previewRef.current) return;
  const contentHeight = invoiceWrapperRef.current.scrollHeight;
  setInvoiceNaturalHeight(contentHeight);
  const available = previewRef.current.clientHeight - 100;
  if (contentHeight <= 0 || available <= 0) return;
  const rawScale = contentHeight > available ? available / contentHeight : 1;
  setAutoFitScale(Math.min(1, Math.max(rawScale, 0.15)));
};
    fit();
    window.addEventListener('resize', fit);
    const ro = new ResizeObserver(fit);
    if (invoiceWrapperRef.current) ro.observe(invoiceWrapperRef.current);
    return () => { window.removeEventListener('resize', fit); ro.disconnect(); };
  }, [selectedTemplateId, lineItems, taxLines, discountLines, notes, showAmountInWords]);
  const [showInvoiceQR, setShowInvoiceQR] = useState(() => 
  localStorage.getItem('qi_show_invoice_qr') !== 'false'
);

const [autoQrPosition, setAutoQrPosition] = useState<'left'|'center'|'right'>(() =>
  (localStorage.getItem('qi_auto_qr_position') as 'left'|'center'|'right') || 'center'
);
const [autoQrSize, setAutoQrSize] = useState<number>(() =>
  parseInt(localStorage.getItem('qi_auto_qr_size') || '130')
);
const [autoQrLabelSize, setAutoQrLabelSize] = useState<number>(() =>
  parseInt(localStorage.getItem('qi_auto_qr_label_size') || '11')
);
const [autoQrOffset, setAutoQrOffset] = useState<{x: number; y: number}>(() => {
  try { return JSON.parse(localStorage.getItem('qi_auto_qr_offset') || 'null') || {x: 0, y: 0}; }
  catch { return {x: 0, y: 0}; }
});
// A drag offset recorded on one template (e.g. a wide A4 layout) has no
// meaning on a different page size/template — it was silently carrying
// over via localStorage and shifting the QR off the selected position
// (left/center/right) on the new template. Reset it whenever the
// template changes so each template starts from a clean, position-only
// placement; the user can re-drag if they want a custom offset again.
useEffect(() => {
  setAutoQrOffset({ x: 0, y: 0 });
  localStorage.removeItem('qi_auto_qr_offset');
}, [selectedTemplateId]);
const [showTrackingQR] = useState(false);
const [trackingQrPosition, setTrackingQrPosition] = useState<'left'|'center'|'right'>(() =>
  (localStorage.getItem('qi_tracking_qr_position') as 'left'|'center'|'right') || 'center'
);
const [trackingQrSize, setTrackingQrSize] = useState<number>(() =>
  parseInt(localStorage.getItem('qi_tracking_qr_size') || '100')
);
const [trackingQrLabelSize, setTrackingQrLabelSize] = useState<number>(() =>
  parseInt(localStorage.getItem('qi_tracking_qr_label_size') || '8')
);
const [trackingQrOffset, setTrackingQrOffset] = useState<{x: number; y: number}>(() => {
  try { return JSON.parse(localStorage.getItem('qi_tracking_qr_offset') || 'null') || {x: 0, y: 0}; }
  catch { return {x: 0, y: 0}; }
});

// Drag-to-place for the QR placeholders shown in the live preview. The
// actual QR image is rendered by the backend after save — dragging here
// only updates the x/y offset sent along with the invoice, so the backend
// places the real QR where this placeholder was dropped.
const previewQrDrag = useRef<{ group: 'invoice' | 'tracking'; startX: number; startY: number } | null>(null);
const [previewQrDragDelta, setPreviewQrDragDelta] = useState<{ group: 'invoice' | 'tracking'; dx: number; dy: number } | null>(null);

const handlePreviewQrMouseDown = (e: React.MouseEvent, el: any) => {
  if (!el.id.startsWith('preview-') || !el.id.includes('-qr') || el.id.includes('label')) return;
  e.stopPropagation();
  const group: 'invoice' | 'tracking' = el.id.includes('tracking') ? 'tracking' : 'invoice';
  previewQrDrag.current = { group, startX: e.clientX, startY: e.clientY };
  const onMove = (ev: MouseEvent) => {
    if (!previewQrDrag.current) return;
    // The live canvas renders scaled by `zoom` — a raw screen-pixel mouse
    // delta is NOT the same as a document-space unit delta whenever zoom
    // isn't exactly 100%. Dividing by zoom converts what the user actually
    // saw move on screen back into the canvas's real coordinate space, so
    // the QR lands where it visually appeared to be dropped instead of
    // drifting further away the more zoomed-out the editor is.
    const z = zoom || 1;
    setPreviewQrDragDelta({
      group: previewQrDrag.current.group,
      dx: (ev.clientX - previewQrDrag.current.startX) / z,
      dy: (ev.clientY - previewQrDrag.current.startY) / z,
    });
  };
  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    if (previewQrDrag.current) {
      const { group } = previewQrDrag.current;
      setPreviewQrDragDelta(prev => {
        if (prev && prev.group === group) {
          if (group === 'invoice') {
            const next = { x: autoQrOffset.x + prev.dx, y: autoQrOffset.y + prev.dy };
            setAutoQrOffset(next);
            localStorage.setItem('qi_auto_qr_offset', JSON.stringify(next));
          } else {
            const next = { x: trackingQrOffset.x + prev.dx, y: trackingQrOffset.y + prev.dy };
            setTrackingQrOffset(next);
            localStorage.setItem('qi_tracking_qr_offset', JSON.stringify(next));
          }
        }
        return null;
      });
    }
    previewQrDrag.current = null;
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
};

  const [amountInWordsStyle, setAmountInWordsStyle] = useState({
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: 9,
    textAlign: 'center' as 'left' | 'center' | 'right',
    fontStyle: 'normal' as 'normal' | 'italic',
    fontWeight: 'normal' as 'normal' | 'bold',
    color: '#475569',
  });

  // Line items
  
  // Defined first so they can be safely called during calculations
  function getEffectiveTaxRate(item: LineItem): number {
    return (isB2B && item.b2bTaxRate !== undefined && item.b2bTaxRate !== null)
      ? item.b2bTaxRate
      : (item.taxRate || 0);
  }

  function getItemTaxBreakdown(item: LineItem) {
    const lineTotal = item.qty * item.rate;
    const rate = getEffectiveTaxRate(item);
    if (rate <= 0) return { base: lineTotal, tax: 0, total: lineTotal };
    if (item.taxInclusive) {
      const base = lineTotal / (1 + rate / 100);
      return { base, tax: lineTotal - base, total: lineTotal };
    }
    const tax = lineTotal * (rate / 100);
    return { base: lineTotal, tax, total: lineTotal + tax };
  }

  const sym = getCurrencyDisplay(activeCurrency, (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol');
  const itemTaxBreakdowns = lineItems.map(getItemTaxBreakdown);
  const subtotal = itemTaxBreakdowns.reduce((s, b) => s + b.base, 0);
  const discTotal = discountLines.reduce((s, d) => s + subtotal * (d.rate / 100), 0);

  // Resolve matched account by phone (falls back to name+email) for auto-discount lookup
  const matchedAccount = React.useMemo(() => {
    const myNormalized = normalizePhoneForMatching(clientPhone, qiOutletCountry);
    return accounts.find(a => {
      const accNormalized = normalizePhoneForMatching(a.phone || '', qiOutletCountry);
      if (myNormalized && accNormalized && accNormalized === myNormalized) return true;
      return clientName.trim() && a.name.trim().toLowerCase() === clientName.trim().toLowerCase() &&
        clientEmail.trim() && a.email.trim().toLowerCase() === clientEmail.trim().toLowerCase();
    }) || null;
  }, [accounts, clientPhone, clientName, clientEmail, qiOutletCountry]);

  const autoDiscountRule = React.useMemo(
    () => getBestDiscountRule(matchedAccount, discountRules || [], subtotal - discTotal, outletId),
    [matchedAccount, discountRules, subtotal, discTotal, outletId]
  );
  const autoDiscountAmount = autoDiscountRule
    ? computeAutoDiscount(matchedAccount, discountRules || [], subtotal - discTotal, outletId)
    : 0;

  // Universally transliterates ANY loyalty rule name (VIP, Gold, Loyal, Summer, etc.) phonetically
  useEffect(() => {
    if (!bilingualEnabled || !autoDiscountRule?.label) return;
    const raw = autoDiscountRule.label.trim();
    const numMatch = raw.match(/\d+$/);
    const baseWords = raw.replace(/\d+$/, '').trim();
    const trailingDigits = numMatch ? ` ${numMatch[0]}` : '';

    if (!baseWords) {
      translatedValuesRef.current = { ...translatedValuesRef.current, label_loyalty: raw };
      setTranslatedValues(prev => ({ ...prev, label_loyalty: raw }));
      return;
    }

    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(baseWords)}&tl=${quickInvLang}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {}
    })
      .then(r => r.json())
      .then(data => {
        const translit = data.transliterated && data.transliterated.toLowerCase() !== baseWords.toLowerCase()
          ? data.transliterated
          : baseWords;
        const finalVal = `${translit}${trailingDigits}`.trim();
        translatedValuesRef.current = { ...translatedValuesRef.current, label_loyalty: finalVal };
        setTranslatedValues(prev => ({ ...prev, label_loyalty: finalVal }));
      })
      .catch(() => {
        translatedValuesRef.current = { ...translatedValuesRef.current, label_loyalty: raw };
        setTranslatedValues(prev => ({ ...prev, label_loyalty: raw }));
      });
  }, [bilingualEnabled, autoDiscountRule?.label, quickInvLang]);
// Trigger translation when loyalty discount rule matches (declared here where autoDiscountRule exists)
  useEffect(() => {
    if (!bilingualEnabled || !autoDiscountRule?.label) return;
    clearTimeout(autoTranslateQITimer.current);
    autoTranslateQITimer.current = setTimeout(() => {
      handleQuickInvTranslateRef.current();
    }, 800);
  }, [bilingualEnabled, autoDiscountRule?.label]);
  // Discount reduces the TAXABLE VALUE, not just the final total
  const totalDiscountAmount = discTotal + autoDiscountAmount;
  const discountRatio = subtotal > 0 ? Math.min(1, totalDiscountAmount / subtotal) : 0;

  const productTaxTotal = itemTaxBreakdowns.reduce((s, b, i) => {
    const rate = getEffectiveTaxRate(lineItems[i]);
    return s + (b.base * (1 - discountRatio)) * (rate / 100);
  }, 0);

  const taxableValue = subtotal - totalDiscountAmount;
  const taxTotal = taxLines.reduce((s, t) => s + taxableValue * (t.rate / 100), 0);

  // 'total' is now officially calculated and initialized
  const total = taxableValue + productTaxTotal + taxTotal + shippingCost;

  const quickInvTaxBreakdown = React.useMemo(() => {
    if (productTaxTotal <= 0) return undefined;
    const scheme = qiTaxScheme;
    const combined: Record<string, { rate: number; amount: number }> = {};

    lineItems.filter(li => li.description.trim() && getEffectiveTaxRate(li) > 0).forEach(li => {
      const b = getItemTaxBreakdown(li);
      const rate = getEffectiveTaxRate(li);
      const discountedBase = b.base * (1 - discountRatio);

      const isCustomScheme = scheme.id === 'outlet_custom' || scheme.id === 'custom';
      const b2bComps = isCustomScheme ? ((customTaxScheme as any)?.b2b_components || (scheme as any).b2b_components) : null;
      const hasB2BSplit = isB2B && b2bComps && b2bComps.length > 1;

      if (hasB2BSplit) {
        b2bComps.forEach((c: any) => {
          if (!combined[c.name]) combined[c.name] = { rate: c.rate, amount: 0 };
          combined[c.name].amount += (discountedBase * c.rate / 100);
        });
      } else if (!isB2B && scheme.kind === 'component' && scheme.components && scheme.components.length > 1) {
        const breakdown = computeTaxBreakdown(scheme, discountedBase, { sellerRegion: qiOutletRegion, buyerRegion }, rate);
        breakdown.lines.forEach(l => {
          if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
          combined[l.name].amount += l.amount;
        });
      } else {
        const standardTaxName = li.taxName || customTaxScheme?.name || scheme.components?.[0]?.name || scheme.label.replace(/\s*\(.*?\)/, '') || 'Tax';
        const customLabel = isB2B
          ? (li.b2bTaxName || (customTaxScheme as any)?.b2b_name || (scheme as any).b2b_name || standardTaxName)
          : standardTaxName;
        if (!combined[customLabel]) combined[customLabel] = { rate, amount: 0 };
        combined[customLabel].amount += (discountedBase * rate / 100);
      }
    });

    const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
    return entries.length > 0 ? entries : undefined;
  }, [productTaxTotal, qiTaxScheme, lineItems, discountRatio, isB2B, customTaxScheme, qiOutletRegion, buyerRegion]);

  // SAFE: Now 'total' is initialized, so this effect will never hit the Temporal Dead Zone
  useEffect(() => {
    if (!showAmountInWords || bilingualEnabled) return;
    scheduleTranslate(amountToWords(total, activeCurrency), 'amountInWords');
  }, [total, showAmountInWords, activeCurrency, bilingualEnabled]);

  const filteredAccounts = accounts.filter(a =>
    a.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    a.email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const selectAccount = (acc: ClientAccount) => {
    setSelectedAccountId(acc.id);
    setClientName(acc.name);
    setClientEmail(acc.email || '');
    setClientPhone(acc.phone || '');
    setClientAddress(acc.address || '');
    setClientSearch(acc.name);
    setShowClientDropdown(false);
    setIncludeFields({
      name: Boolean(acc.name),
      email: Boolean(acc.email),
      phone: Boolean(acc.phone),
      address: Boolean(acc.address),
      taxId: Boolean(clientGSTIN && isValidTaxIdString(clientGSTIN)),
    });
    scheduleTransliterate(acc.name, 'clientName');
  };

  const lastAutoFilledPhone = useRef<string>('');
  useEffect(() => {
    const normalized = normalizePhoneForMatching(clientPhone, qiOutletCountry);

    if (normalized.length < 5) {
      if (lastAutoFilledPhone.current && clientName) {
        setSelectedAccountId('');
        setClientName('');
        setClientEmail('');
        setClientAddress('');
        setClientSearch('');
      }
      lastAutoFilledPhone.current = '';
      return;
    }
    if (lastAutoFilledPhone.current === normalized) return;

    const matches = accounts.filter(a => {
      const accNormalized = normalizePhoneForMatching(a.phone || '', qiOutletCountry);
      return accNormalized && accNormalized.startsWith(normalized);
    });

    if (matches.length === 1) {
      const match = matches[0];
      lastAutoFilledPhone.current = normalized;
      setSelectedAccountId(match.id);
      setClientName(match.name);
      setClientEmail(match.email || '');
      setClientAddress(match.address || '');
      setClientSearch(match.name);
    }
  }, [clientPhone, accounts, clientName, qiOutletCountry]);

  const addLineItem = () => setLineItems(prev => [...prev, { id: `li-${Date.now()}`, description: '', qty: 1, rate: 0 }]);

  const handleScanAdd = (
  description: string,
  qty: number,
  rate: number,
  _barcode?: string,
  taxInfo?: { taxRate?: number; b2bTaxRate?: number; taxInclusive?: boolean }
) => {
  // 🔒 Fast QR Badge Scanner for Staff / Cashier / Doctor / Receptionist
  if (description.startsWith('CASHIER_TOKEN:') || description.startsWith('STAFF_BADGE:')) {
    const token = description.replace(/^(CASHIER_TOKEN:|STAFF_BADGE:)/, '').trim();

    const applyStaffName = (name: string, role?: string) => {
      setDynamicValues(prev => ({
        ...prev,
        staff_name: name,
      }));
      showToast(`👤 Verified Staff: ${name}${role ? ` (${role})` : ''}`, '#16a34a');
      if (bilingualEnabled) {
        clearTimeout(autoTranslateQITimer.current);
        handleQuickInvTranslateRef.current();
      }
    };

    if (token.startsWith('ey') || token.length > 40) {
      fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(user => {
          if (user?.name) applyStaffName(user.name, user.role);
        })
        .catch(() => showToast('⚠️ Invalid employee QR badge'));
    } else {
      applyStaffName(token);
    }
    return;
  }

  setLineItems(prev => {
    // If same item already exists, just increment qty
    const existing = prev.findIndex(li =>
      li.description.toLowerCase().trim() === description.toLowerCase().trim()
    );
    if (existing >= 0) {
      return prev.map((li, i) =>
        i === existing ? { ...li, qty: li.qty + qty } : li
      );
    }
    // Remove the empty placeholder row if present
    const withoutEmpty = prev.filter(li => li.description.trim() !== '');
    return [...withoutEmpty, {
      id: `li-${Date.now()}-${Math.random()}`,
      description,
      qty,
      rate,
      taxRate: taxInfo?.taxRate || 0,
      b2bTaxRate: taxInfo?.b2bTaxRate,
      taxInclusive: !!taxInfo?.taxInclusive,
    }];
  });
};


const translateRequestSeq = useRef<Record<string, number>>({});

const autoTranslate = async (text: string, key: string) => {
  if (!text.trim()) return;
  const myTicket = (translateRequestSeq.current[key] || 0) + 1;
  translateRequestSeq.current[key] = myTicket;
  try {
    const tl = quickInvLang;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    // Route through the backend's /translate proxy (see routers/translate.py)
    // instead of calling translate.googleapis.com directly from the
    // browser — the direct call was failing silently, which is why
    // Tax/Discount/Subtotal/Total never got Arabic values while
    // Patient/Age/Items (already using a backend-proxied route) worked.
    const res = await fetch(
      `${BACKEND}/translate?text=${encodeURIComponent(text.trim())}&tl=${tl}`,
      { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
    );
    const data = await res.json();
    const translated = data.translated || text;
    console.log('[TRANSLATE]', { key, text, translated, currentTicket: translateRequestSeq.current[key], myTicket });
    // A newer request for this same key fired since we started —
    // this reply is stale (e.g. an old partial-typed search), drop it.
    if (translateRequestSeq.current[key] !== myTicket) return;
    setTranslatedValues(prev => ({ ...prev, [key]: translated }));
  } catch { /* silent fail */ }
};

const scheduleTranslate = (text: string, key: string) => {
  if (translateTimers.current[key]) clearTimeout(translateTimers.current[key]);
  translateTimers.current[key] = setTimeout(() => autoTranslate(text, key), 800);
};

// Proper nouns (item/brand names, staff names, pharmacist names, client
// names embedded in notes) don't have a real "translation" — Google
// Translate just echoes them back unchanged, or worse, tries to translate
// a brand/drug name as if it were a common word. Transliteration (phonetic
// re-spelling in the target script) is what actually makes these readable.
const transliterateRequestSeq = useRef<Record<string, number>>({});

const autoTransliterate = async (text: string, key: string) => {
  if (!text.trim()) return;
  const myTicket = (transliterateRequestSeq.current[key] || 0) + 1;
  transliterateRequestSeq.current[key] = myTicket;
  try {
    const tl = quickInvLang;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(
      `${BACKEND}/transliterate?text=${encodeURIComponent(text.trim())}&tl=${tl}`,
      { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
    );
    const data = await res.json();
    const transliterated = data.transliterated || text;
    if (transliterateRequestSeq.current[key] !== myTicket) return;
    setTranslatedValues(prev => ({ ...prev, [key]: transliterated }));
  } catch { /* silent fail */ }
};

const scheduleTransliterate = (text: string, key: string) => {
  if (translateTimers.current[key]) clearTimeout(translateTimers.current[key]);
  translateTimers.current[key] = setTimeout(() => autoTransliterate(text, key), 800);
};

useEffect(() => {
  const raf = requestAnimationFrame(() => {
    const node = previewCanvasRef.current;
    if (!node) return;
    const tableInner = node.querySelector('[data-elid] .bg-slate-50.shadow-inner') as HTMLElement | null;
    const tableOuter = tableInner ? (tableInner.closest('[data-elid]') as HTMLElement | null) : null;
    if (!tableOuter) return;
    const top = parseFloat(tableOuter.style.top || '0');
    const bottom = top + tableOuter.getBoundingClientRect().height;
    if (Math.abs(bottom - (measuredTableBottom ?? 0)) > 2) {
      setMeasuredTableBottom(bottom);
    }
  });
  return () => cancelAnimationFrame(raf);
});

useEffect(() => {
  if (!bilingualEnabled) return;
  lineItems.forEach(li => {
    if (li.description.trim()) scheduleTranslate(li.description, `item_${li.id}`);
  });
  customColumns.forEach(col => {
    lineItems.forEach(li => {
      const val = (li as any)[col.key] ?? (li as any)[col.header] ?? (col.key === 'cat_tax_name' ? (li.taxName || li.b2bTaxName) : undefined);
      if (val && String(val).trim()) {
        const clean = String(val).trim();
        // Skip scheduling a translate/transliterate call for pure
        // numeric/percentage values (e.g. "5%") — nothing to translate,
        // and it just echoes back producing a duplicate line in the UI.
        if (/^[\d.,%\s]+$/.test(clean)) return;
        scheduleTranslate(clean, `custom_${li.id}_${col.key}`);
        scheduleTransliterate(clean, `tax_name_${clean}`);
      }
    });
  });
}, [quickInvLang, bilingualEnabled, customColumnsSignature]);
const itemNamesSignature = lineItems.map(li => `${li.id}:${li.description}`).join('|');
useEffect(() => {
  if (!bilingualEnabled) return;
  lineItems.forEach(li => {
    if (li.description.trim()) {
      scheduleTranslate(li.description, `item_${li.id}`);
    }
  });
}, [itemNamesSignature, bilingualEnabled, quickInvLang]);
// Translate tax/discount labels and the static "Subtotal"/"Total" row
// labels whenever they change OR the target language changes. Before,
// this only ran once on mount, so tax/discount stayed stuck in whatever
// language was active at first load, and Subtotal/Total had no
// translation entry at all — nothing for the receipt to fall back to,
// which is why the printed thermal receipt showed them in English only.
// Tax/duty label acronyms and region-specific loanwords (VAT, GST, Zakat,
// etc.) read better transliterated than translated — translating "GST"
// literally produces a long descriptive phrase nobody actually uses on a
// receipt, and "Zakat" is already an Arabic word so translating it is
// circular. Generic labels like "Tax"/"Discount" have a clean standard
// Arabic word, so those still go through real translation.
const TRANSLITERATE_LABELS = /^(vat|gst|zakat|tds|tcs|cess|cgst|sgst|igst|hst|pst|qst)$/i;

useEffect(() => {
  if (!bilingualEnabled) return;
  taxLines.forEach((t, i) => {
    if (!t.label) return;
    if (TRANSLITERATE_LABELS.test(t.label.trim())) {
      scheduleTransliterate(t.label, `tax_${i}`);
    } else {
      scheduleTranslate(t.label, `tax_${i}`);
    }
  });
  discountLines.forEach((d, i) => {
    if (!d.label) return;
    if (TRANSLITERATE_LABELS.test(d.label.trim())) {
      scheduleTransliterate(d.label, `discount_${i}`);
    } else {
      scheduleTranslate(d.label, `discount_${i}`);
    }
  });
  scheduleTranslate('Subtotal', 'label_subtotal');
  scheduleTranslate('Total', 'label_total');
  scheduleTranslate('Shipping', 'label_shipping');
}, [quickInvLang, taxLines.map(t => t.label).join('|'), discountLines.map(d => d.label).join('|')]);

const removeLineItem = (id: string) => { if (lineItems.length > 1) setLineItems(prev => prev.filter(li => li.id !== id)); };
  const updateLineItem = (id: string, field: keyof LineItem, value: any) =>
    setLineItems(prev => {
      const updated = prev.map(li => (li.id === id ? { ...li, [field]: value } : li));
      const isLastRow = prev[prev.length - 1]?.id === id;
      if (field === 'description' && isLastRow && String(value).trim()) {
        return [...updated, { id: `li-${Date.now()}-${Math.random()}`, description: '', qty: 1, rate: 0 }];
      }
      return updated;
    });

  // Only the template's own invoice/tracking QR PLACEHOLDER should be
  // stripped before save (the backend attaches its own secure, real QR
  // after create). A blanket `el.type !== 'qr'` filter here also deleted
  // genuinely separate, user-added QRs (e.g. "Scan to Pay Net30"),
  // leaving their caption text behind with no QR image under it.
  const isPlaceholderQr = (qrEl: any, allElements: any[]) =>
    allElements.some((other: any) =>
      other.type === 'text' &&
      (other.textContent === 'Scan to view invoice' || other.textContent === 'Scan to track your order') &&
      Math.abs((other.y || 0) - ((qrEl.y || 0) + (qrEl.height || 0))) < 40 &&
      Math.abs((other.x || 0) - (qrEl.x || 0)) < (qrEl.width || 0) + 40
    );

   const handleReset = () => {
    translatedTemplateRef.current = null;
    patchedTemplateRef.current = null;
    setQuickInvTranslatedTemplate(null);
    setClientName(''); setClientEmail(''); setClientPhone(''); setClientAddress('');
    setClientSearch(''); setSelectedAccountId('');
    setClientGSTIN(''); setBuyerRegion(''); setGstinAutoDetected(false);
    setIncludeFields({
      name: true,
      phone: true,
      email: true,
      address: true,
      taxId: true,
    });
    // Without this, a customer whose phone number was already
    // auto-filled once this session won't auto-fill again after the form
    // resets for the next bill — the ref still remembers those exact
    // digits and the "already filled" guard silently skips the match.
    lastAutoFilledPhone.current = '';
    setInvoiceDate(today); setDueDate(due); setNotes('');
    setTaxLines([{ label: 'Tax', rate: 0 }]);
    setDiscountLines([{ label: 'Discount', rate: 0 }]);
    setShippingCost(0);
    setLineItems([{ id: `li-${Date.now()}`, description: '', qty: 1, rate: 0 }]);
    setDynamicValues({});
setInvoiceNumberOverride('');
setInvoiceStatus('paid'); 
setPaymentMode('cash'); 
setShowManualDateTime(false);
setShowDueDate(false);
const _now = new Date();
setInvoiceDate(_now.toLocaleDateString('en-CA', { timeZone: userTimezone }));
setInvoiceTime(_now.toLocaleTimeString('en-US', { timeZone: userTimezone, hour: '2-digit', minute: '2-digit', hour12: true }));
setSaved(false);
  };
// Auto-generate backend counters for serial fields detected in template
const autoGenerateSerialFields = async (tmpl: ActiveTemplate): Promise<Record<string, string>> => {
  const generated: Record<string, string> = {};
  const allText = tmpl.elements
    .filter(el => el.type === 'text' && el.textContent)
    .map(el => el.textContent!).join('\n');

  try {
    const { SERIAL_TYPES, getNextSerialNumber } = await import('../utils/invoiceNumber');

    // Some serial-type keys collide with plain-English words that show up
    // in unrelated sentences (e.g. "Frame warranty: 6 months."). For those,
    // require a more specific phrase before treating it as a request for
    // an auto-generated serial.
    const DETECTION_OVERRIDES: Record<string, RegExp> = {
      warranty: /\bwarranty\s*(no\.?|number|card)\s*:/i,
    };

    for (const serialType of SERIAL_TYPES) {
      // Build key variants: 'order' also matches 'order no', 'order_no', 'order number'
      const keyVariants = [
        serialType.key,
        serialType.key.replace(/_/g, ' '),
        serialType.key + ' no',
        serialType.key + '_no',
        serialType.key + ' number',
      ];

      const appears = DETECTION_OVERRIDES[serialType.key]
        ? DETECTION_OVERRIDES[serialType.key].test(allText)
        : keyVariants.some(k =>
            new RegExp(`\\b${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b\\s*:`, 'i').test(allText)
          );
      if (!appears) continue;

      try {
        const number = await getNextSerialNumber(serialType.key);
        // Store under all key variants so patchText finds it
        keyVariants.forEach(k => {
          generated[k.replace(/\s+/g, '_')] = number;
        });
        generated[serialType.key] = number;
      } catch {
        const prefix = serialType.prefix.replace(/-$/, '');
        const fallback = `${prefix}-${String(Math.floor(Math.random() * 99999)).padStart(serialType.digits, '0')}`;
        keyVariants.forEach(k => { generated[k.replace(/\s+/g, '_')] = fallback; });
        generated[serialType.key] = fallback;
      }
    }
  } catch { /* api not available */ }

  return generated;
};
  const detectDocumentType = (tmpl: ActiveTemplate): 'invoice' | 'receipt' => {
    const text = tmpl.elements
      .filter(el => el.type === 'text' && el.textContent)
      .map(el => el.textContent!.toLowerCase())
      .join(' ');
    const name = (tmpl.name || '').toLowerCase();
    const isReceipt =
      tmpl.pageSize === 'THERMAL_80MM' ||
      /receipt|pharmacy|clinic|bill no|rx-|dispensary|medical store|pos bill|cash memo/.test(text) ||
      /receipt|pharmacy|clinic|medical|thermal|pos/.test(name);
    return isReceipt ? 'receipt' : 'invoice';
  };

  const handlePrintPreview = async () => {
    setIsPrinting(true);
    try {
      if (bilingualEnabled) {
        const _tplTableCheck: any = translatedTemplateRef.current?.elements?.find((e: any) => e.type === 'table');
        const _tplColsCheck = _tplTableCheck?.columns || [];
        // A custom column added (or given a new value) after the last
        // translate pass has no arabicHeader in the cached template yet —
        // printing from that stale cache was silently dropping the whole
        // Arabic line for that column. Force a fresh translate whenever
        // ANY current custom column isn't fully represented in the cache,
        // not just when the cache is completely empty.
        const _hasAllCustomCols = customColumns.every(cc =>
          _tplColsCheck.some((tc: any) => tc.key === cc.key && tc.arabicHeader)
        );
        // Column headers being translated isn't enough — each line item's
        // own custom-column VALUE (e.g. Tax Name "rihan" -> "ريحان") is
        // translated separately, keyed by that specific line item's id.
        // A row added/edited after the last translate pass would pass the
        // headers-only check above but still have no cached Arabic value
        // for THIS row, silently dropping that row's Arabic line entirely.
        const _hasAllCustomCellValues = customColumns.every(cc =>
          lineItems.every(li => {
            const val = (li as any)[cc.key] ?? (li as any)[cc.header];
            if (!val || !String(val).trim()) return true;
            const cleanVal = String(val).trim();
            if (/^[\d.,%\s]+$/.test(cleanVal)) return true; // numeric — nothing to translate
            return !!(
              translatedValuesRef.current[`custom_${li.id}_${cc.key}`] ||
              translatedValuesRef.current[`custom_${li.id}_${cc.key.replace(/^cat_/, '')}`] ||
              translatedValuesRef.current[`tax_name_${cleanVal}`]
            );
          })
        );
        if (!translatedTemplateRef.current || !quickInvTranslatedTemplate || !_hasAllCustomCols || !_hasAllCustomCellValues) {
          await handleQuickInvTranslate();
        }
      }
      const pageSizeKey = selectedTemplate?.pageSize || 'A4';
      const isThermal = pageSizeKey === 'THERMAL_80MM' || pageSizeKey === 'THERMAL_58MM';
      const printPageCss = PRINT_PAGE_CSS[pageSizeKey] || 'A4';

    if (isThermal) {
  // Build Template Fields (Patient, Age, Power R/L/PD, etc.) once —
  // shared by both BT and USB payloads below. Mirrors the same
  // dynamicFields + dynamicValues + translatedValues + dynamicFieldLabelsAr
  // lookup already used by the preview/PDF path, so what's typed in the
  // form is what actually reaches the physical receipt.
  const printDynamicFields = dynamicFields
    .filter(f => dynamicValues[f.key]?.trim())
    .map(f => ({
      label: f.label,
      value: dynamicValues[f.key].trim(),
      labelAr: dynamicFieldLabelsAr[f.key],
      valueAr: translatedValues[f.key],
    }));

  // Product-level tax (per-item tax_rate/tax_inclusive) — printed as one
  // blended-rate line by printerService.ts, same as POSView.
  const _printProductTaxTotal = productTaxTotal;

  // Subtotal/Total/Shipping Arabic labels — translatedValues['label_subtotal']
  // etc. come from a DEBOUNCED background effect (scheduleTranslate, 800ms
  // + network round-trip) that may not have resolved yet if Print is
  // clicked soon after editing. Don't trust it silently — use the cached
  // value if present, otherwise resolve it fresh and awaited right here so
  // the receipt never prints with a missing Arabic total.
  // "Subtotal"/"Total"/"Shipping" never change per language — translate
  // each ONCE EVER per language and cache permanently in localStorage.
  // First print in a new language pays one network call; every print
  // after that (including this one, if already cached) is instant —
  // no wait at the counter, no dependency on the debounced background
  // translate having finished in time.
  const _cacheKey = (label: string) => `cloudairambo_label_cache_${quickInvLang}_${label}`;
  const _token = getStoredToken();
  const _headers = _token ? { Authorization: `Bearer ${_token}` } : {};
  const _getCachedLabel = async (label: string) => {
    const cached = localStorage.getItem(_cacheKey(label));
    if (cached) return cached;
    try {
      const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(label)}&tl=${quickInvLang}`, { headers: _headers });
      const data = await res.json();
      const translated = data.translated || label;
      localStorage.setItem(_cacheKey(label), translated);
      return translated;
    } catch { return label; }
  };
  // Read the synchronous ref, not the `translatedValues` state variable —
  // that variable is a snapshot captured when handlePrintPreview started
  // running; any translate pass that completed DURING this same call
  // (via the await handleQuickInvTranslate() above) updated the ref
  // immediately but only scheduled a state update for the NEXT render.
  const _tvNow = translatedValuesRef.current;

  const _printProductTaxLabelAr = productTaxTotal > 0 && quickInvLang !== 'en'
    ? (_tvNow['label_tax'] || await _getCachedLabel('Total Tax'))
    : undefined;
  const [_subtotalAr, _totalAr, _shippingAr, _itemAr, _qtyAr, _amtAr] = await Promise.all([
    _tvNow['label_subtotal'] || _getCachedLabel('Subtotal'),
    _tvNow['label_total'] || _getCachedLabel('Total'),
    _tvNow['label_shipping'] || _getCachedLabel('Shipping'),
    _tvNow['label_item'] || _getCachedLabel('Item'),
    _tvNow['label_qty'] || _getCachedLabel('Qty'),
    _tvNow['label_amt'] || _getCachedLabel('Amt'),
  ]);
  const printRowLabelsAr = {
    subtotal: _subtotalAr,
    total: _totalAr,
    shipping: _shippingAr,
    item: _itemAr,
    qty: _qtyAr,
    amt: _amtAr,
    tax: _printProductTaxLabelAr,
  };

  // Amount in Words — was never resolved for the thermal print path at
  // all (only PDF/preview ever called amountToWords). Resolve fresh here
  // if the toggle is on, plus its translation, same debounce-safe pattern
  // as everything else on this receipt.
  const _amountInWordsText = showAmountInWords ? amountToWords(total, activeCurrency) : undefined;
  const _amountInWordsAr = showAmountInWords && quickInvLang !== 'en'
    ? (_tvNow['amountInWords'] || await _getCachedLabel(_amountInWordsText || ''))
    : undefined;

  // Tax/discount labels rely on a DEBOUNCED background effect that may not
  // have resolved yet — resolve fresh and awaited here, same pattern as
  // subtotal/total above, so they never silently print English-only.
  const _taxLabelsAr = await Promise.all(
    taxLines.map((t, i) => _tvNow[`tax_${i}`] || _getCachedLabel(t.label))
  );
  const _discountLabelsAr = await Promise.all(
    discountLines.map((d, i) => _tvNow[`discount_${i}`] || _getCachedLabel(d.label))
  );

  // Don't trust the debounced background transliteration here — resolve
  // fresh and awaited so the client name Arabic line never silently
  // drops just because Print was clicked before scheduleTransliterate's
  // 800ms timer + network round trip finished.
  let _clientLineAr: string | undefined;
  if (clientName.trim() && clientName.trim().toLowerCase() !== 'walk-in customer') {
    try {
      const res = await fetch(
        `${BACKEND}/transliterate?text=${encodeURIComponent(clientName.trim())}&tl=${quickInvLang}`,
        { headers: _headers }
      );
      const data = await res.json();
      const transliterated = data.transliterated || '';
      // Only treat it as real bilingual content if it actually differs —
      // a failed/no-op transliteration just echoes the name back.
      if (transliterated && transliterated.trim().toLowerCase() !== clientName.trim().toLowerCase()) {
        _clientLineAr = transliterated;
      }
    } catch (e) {
      console.warn('Client name transliteration failed:', e);
    }
  }

  const _tplTable: any = (quickInvTranslatedTemplate || selectedTemplate)?.elements.find((e: any) => e.type === 'table');
  // When a bilingual translate pass has already run, _tplTable.columns
  // already contains customColumns merged in (with real arabicHeader
  // values) via handleQuickInvTranslate's placeCustomColumns call.
  // placeCustomColumns has no dedup, so re-adding the raw customColumns
  // here would append a SECOND, untranslated copy of each column (e.g.
  // "Tax Name"), and _qiOverflow could end up reading the duplicate
  // (no arabicHeader) instead of the translated one — silently dropping
  // the Arabic line. Only add columns not already present, same guard
  // POSView's buildPosColumns uses.
  const _qiExistingKeys = new Set((_tplTable?.columns || []).map((c: any) => c.key));
  const _qiAllCols: any[] = placeCustomColumns(
    _tplTable?.columns || [],
    customColumns
      .filter(c => !_qiExistingKeys.has(c.key))
      .map(c => ({ ...c, width: 12 }))
  );
  const _qiInline = _qiAllCols.slice(0, 5);
  const _qiOverflow = _qiAllCols.slice(5);
  const _qiHdr = (c: any) => c.originalHeader || c.header || '';
  const _qiItems = lineItems.filter(li => li.description.trim());
  const _qiCell = (li: any, c: any, idx: number) => {
    if (c.key === 'description') return li.description;
    if (c.key === 'qty') return String(li.qty);
    if (c.key === 'rate') return `${sym}${li.rate.toFixed(2)}`;
    if (c.key === 'total') return `${sym}${(li.qty * li.rate).toFixed(2)}`;
    if (['sno', 'serial', 'index'].includes(c.key)) return String(idx + 1);
    return String(li[c.key] ?? '');
  };
  const _qiPrintCols = _qiInline.length
    ? _qiInline.map((c: any) => ({
        header: _qiHdr(c),
        headerAr: bilingualEnabled ? c.arabicHeader : undefined,
        align: c.align,
        width: c.width,
        primary: c.key === 'description',
      }))
    : undefined;
  const _qiCells = _qiItems.map((li, i) => _qiInline.map((c: any) => _qiCell(li, c, i)));
  const _qiExtraLines = _qiItems.map((li, i) =>
    _qiOverflow
      .map((c: any) => { const v = _qiCell(li, c, i); return v ? `${_qiHdr(c)}: ${v}` : ''; })
      .filter(Boolean)
      .join('  ')
  );
  // Resolve any overflow column's Arabic header that's STILL missing
  // (translate call above failed, or a fresh customColumn was added in
  // the same tick) instead of silently dropping that column's Arabic line.
  const _qiOverflowHeaderAr: Record<string, string> = {};
  if (bilingualEnabled) {
    await Promise.all(_qiOverflow.map(async (c: any) => {
      _qiOverflowHeaderAr[c.key] = c.arabicHeader || await _getCachedLabel(_qiHdr(c));
    }));
  }
  const _qiExtraLinesAr = bilingualEnabled
    ? _qiItems.map((li, i) =>
        _qiOverflow
          .map((c: any) => {
            const raw = _qiCell(li, c, i);
            const headerAr = _qiOverflowHeaderAr[c.key];
            const ar = _tvNow[`custom_${li.id}_${c.key}`] ||
              _tvNow[`custom_${li.id}_${c.key.replace(/^cat_/, '')}`] ||
              _tvNow[`tax_name_${raw}`];
            return raw && headerAr && ar ? `${headerAr}: ${ar}` : '';
          })
          .filter(Boolean)
          .join('  ')
      )
    : undefined;

  const _printTaxBreakdown = quickInvTaxBreakdown?.map(item => ({
    ...item,
    nameAr: taxBreakdownAr[item.name],
  }));

  if (useBTPrinter) {
    const btPrinted = await printReceiptBT({
      shopName: authUser?.name || 'RECEIPT',
      invoiceNumber: invoiceNumberOverride.trim() || 'PENDING',
      date: invoiceDate,
      clientName: clientName || 'Walk-in Customer',
      clientLineAr: _clientLineAr,
      clientPhone: getCleanE164Phone(clientPhone, qiOutletCountry || 'IN'),
      clientEmail: clientEmail.includes('@') ? clientEmail : '',
      buyerTaxId: clientGSTIN.trim(),
      buyerTaxLabel: taxIdMeta.taxLabel || 'Tax ID',
      itemExtraLines: _qiExtraLines,
      itemExtraLinesAr: _qiExtraLinesAr,
      tableColumns: _qiPrintCols,
      itemCells: _qiCells,
      items: lineItems
        .filter(li => li.description.trim())
        .map(li => ({
          description: li.description,
          qty: li.qty,
          rate: li.rate,
          arabicName: bilingualEnabled ? translatedValues[`item_${li.id}`] : undefined,
        })),
      taxLines: taxLines.map((t, i) => ({ ...t, labelAr: _taxLabelsAr[i] })),
      discountLines: discountLines.map((d, i) => ({ ...d, labelAr: _discountLabelsAr[i] })),
      shippingCost,
      sym,
      notes,
      arabicNumerals: quickInvLang === 'ar',
      dynamicFields: printDynamicFields,
      rowLabelsAr: printRowLabelsAr,
      amountInWords: _amountInWordsText,
      amountInWordsAr: _amountInWordsAr,
      productTaxTotal: _printProductTaxTotal,
      netSubtotal: subtotal,
      autoDiscountAmount: autoDiscountRule?.visibleOnBill ? autoDiscountAmount : 0,
      autoDiscountLabel: autoDiscountRule?.label || 'Loyalty Discount',
      taxBreakdown: _printTaxBreakdown,
    });
    if (btPrinted) return;
  }
  if (useUSBPrinter) {
    const usbPrinted = await printReceiptUSB({
      shopName: authUser?.name || 'RECEIPT',
      invoiceNumber: invoiceNumberOverride.trim() || 'PENDING',
      date: invoiceDate,
      clientName: clientName || 'Walk-in Customer',
      clientLineAr: _clientLineAr,
      clientPhone: getCleanE164Phone(clientPhone, qiOutletCountry || 'IN'),
      clientEmail: clientEmail.includes('@') ? clientEmail : '',
      buyerTaxId: clientGSTIN.trim(),
      buyerTaxLabel: taxIdMeta.taxLabel || 'Tax ID',
      itemExtraLines: _qiExtraLines,
      tableColumns: _qiPrintCols,
      itemCells: _qiCells,
      items: lineItems
        .filter(li => li.description.trim())
        .map(li => ({
          description: li.description,
          qty: li.qty,
          rate: li.rate,
          arabicName: bilingualEnabled ? translatedValues[`item_${li.id}`] : undefined,
        })),
      taxLines: taxLines.map((t, i) => ({ ...t, labelAr: _taxLabelsAr[i] })),
      discountLines: discountLines.map((d, i) => ({ ...d, labelAr: _discountLabelsAr[i] })),
      shippingCost,
      sym,
      notes,
      arabicNumerals: quickInvLang === 'ar',
      dynamicFields: printDynamicFields,
      rowLabelsAr: printRowLabelsAr,
            productTaxTotal: _printProductTaxTotal,
      amountInWords: _amountInWordsText,
      amountInWordsAr: _amountInWordsAr,
      taxBreakdown: _printTaxBreakdown,
    });

    if (usbPrinted) return;
  }
        if (!previewCanvasRef.current) return;

const pageCss = await getPageCss();
const printWindow = window.open('', '_blank', 'width=420,height=800');
if (!printWindow) {
  alert('Allow pop-ups to print.');
  return;
}

printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print</title><style>
${pageCss}
*{margin:0;padding:0;box-sizing:border-box;}
body{background:#fff;font-family:system-ui,sans-serif;}
@media print{@page{size:${printPageCss};margin:0;}}
</style></head><body>${previewCanvasRef.current.outerHTML}</body></html>`);

printWindow.document.close();
setTimeout(() => {
  printWindow.focus();
  printWindow.print();
}, 800);

return;
      }

      // Everything else — A4, Letter, A5, Legal, and label sizes
      // (LABEL_3X2, LABEL_4X6) — prints from the live canvas preview,
      // each with its own correctly-sized @page rule so the browser
      // doesn't scale/crop a small label to A4 or vice versa.
      if (!previewCanvasRef.current) return;
      if (useUSBPrinter) {
        if (!previewCanvasRef.current.id) previewCanvasRef.current.id = 'qi-print-canvas';
        await printElementUSB(previewCanvasRef.current.id);
        return;
      }
      const pageCss = await getPageCss();
      const printWindow = window.open('', '_blank', 'width=820,height=1000');
      if (!printWindow) { alert('Allow pop-ups to print.'); return; }
      printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print</title><style>
${pageCss}
*{margin:0;padding:0;box-sizing:border-box;}
body{background:#fff;font-family:system-ui,sans-serif;}
@media print{@page{size:${printPageCss};margin:0;}}
</style></head><body>${previewCanvasRef.current.outerHTML}</body></html>`);
      printWindow.document.close();
      setTimeout(() => { printWindow.focus(); printWindow.print(); }, 800);
    } catch (e: any) {
      alert(`Print failed: ${e.message || e}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const buildFullLineItem = (li: LineItem) => {
    const b = getItemTaxBreakdown(li);
    const p = catalogProducts.find(cp =>
      (li.description && cp.name?.trim().toLowerCase() === li.description.trim().toLowerCase()) ||
      ((li as any).barcode && cp.barcode === (li as any).barcode)
    );

    const customData: Record<string, string> = {};
    customColumns.forEach(col => {
      const direct = (li as any)[col.key] ?? (li as any)[col.header] ?? (li as any)[col.id || ''];
      const val = (direct != null && String(direct).trim() !== '')
        ? String(direct).trim()
        : (getCatalogColValue(p, col) ?? '');

      customData[col.key] = val;
      customData[col.header] = val;
      if (col.id) customData[col.id] = val;
    });

    return {
      id: li.id,
      description: li.description,
      qty: li.qty,
      unit_price: li.rate,
      rate: li.rate,
      amount: li.qty * li.rate,
      total: li.qty * li.rate,
      tax_rate: getEffectiveTaxRate(li),
      taxRate: getEffectiveTaxRate(li),
      tax_inclusive: !!li.taxInclusive,
      taxInclusive: !!li.taxInclusive,
      tax_amount: parseFloat(b.tax.toFixed(2)),
      taxName: li.taxName || '',
      arabicDescription: bilingualEnabled ? (translatedValuesRef.current[`item_${li.id}`] || '') : '',
      arabicName: bilingualEnabled ? (translatedValuesRef.current[`item_${li.id}`] || '') : '',
      ...customData,
    };
  };

  const handleGenerate = async () => {
    console.log('STATUS AT GENERATE:', invoiceStatus);
    if (!authUser && !isEditMode) return alert('Please sign in.'); 
    if (!clientName.trim()) return alert('Enter a client name.');
    if (lineItems.every(li => !li.description.trim())) return alert('Add at least one line item.');

    // MANDATORY FOR B2B: Enforce Tax ID entry
    if (isB2B && !clientGSTIN.trim()) {
      alert(`⚠️ Buyer ${taxIdMeta.taxLabel || 'Tax ID'} is mandatory for B2B invoices. Please enter it in the Buyer Tax ID field.`);
      return;
    }
    const myGeneration = ++saveGenerationRef.current;
    const translatedTemplateSnapshot = quickInvTranslatedTemplate;
    const bilingualLayoutSnapshot = bilingualLayout;
    setSaved(false); // clear any stale "Updated!"/"Saved!" the moment a new save actually starts
    setSaving(true);
    try {
      if (bilingualEnabled) {
        clearTimeout(autoTranslateQITimer.current);
        clearTimeout(clientTranslateTimer.current);
        const translatedResult = await handleQuickInvTranslate();
        if (!translatedResult && !translatedTemplateRef.current) {
          setSaving(false);
          alert('Translation is still processing or encountered an issue. Please try again.');
          return;
        }
      }
      // Snapshot the Arabic labels for Subtotal/Total/Shipping/Tax/Discount
      // right now, from the synchronous ref — these get baked directly
      // onto the saved table element below so BillsView (a separate
      // session with no translatedValues in memory) can reprint them
      // bilingual instead of silently falling back to English.
      const _tvForSave = translatedValuesRef.current;
      const rowLabelsArForSave = {
        subtotal: _tvForSave['label_subtotal'] || '',
        total: _tvForSave['label_total'] || '',
        shipping: _tvForSave['label_shipping'] || '',
        item: _tvForSave['label_item'] || '',
        qty: _tvForSave['label_qty'] || '',
        amt: _tvForSave['label_amt'] || '',
        tax: _tvForSave['label_tax'] || '',
      };
      const taxLinesArForSave = taxLines.map((t, i) => _tvForSave[`tax_${i}`] || '');
      const discountLinesArForSave = discountLines.map((d, i) => _tvForSave[`discount_${i}`] || '');
      const discountLinesArPayload = [
        ...discountLines
          .filter(d => d.label && d.rate > 0 && !d._isAuto)
          .map(d => ({ label: d.label, labelAr: discountLinesArForSave[discountLines.indexOf(d)] || '' })),
        ...((autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0)
          ? [{ label: autoDiscountRule.label || 'Loyalty Discount', labelAr: _tvForSave['label_loyalty'] || '' }]
          : []),
      ];
      const cleanPhone = getCleanE164Phone(clientPhone, qiOutletCountry || 'IN');
      const phoneDigits = cleanPhone.replace(/\D/g, '');
      const activeStaffName = (
        dynamicValues.staff_name ||
        authUser?.name ||
        'Staff'
      ).trim();
      const dynamicCust = (
        dynamicValues.patient ||
        dynamicValues.guest ||
        dynamicValues.client ||
        dynamicValues.customer ||
        dynamicValues.student ||
        dynamicValues.pet ||
        ''
      ).trim();
      const activeCust = (includeFields.name && clientName.trim())
        ? clientName.trim()
        : (dynamicCust || 'Walk-in Customer');
      const template = allTemplates.find(t => t.id === selectedTemplateId) || allTemplates[0];
      console.log('TEMPLATE PAGE SIZE:', JSON.stringify(template.pageSize));
      const translatedTemplateSnapshotForSave = translatedTemplateRef.current;
const useBilingual = bilingualEnabled && !!translatedTemplateSnapshotForSave;
const sourceTemplate = useBilingual ? translatedTemplateSnapshotForSave : template;
let elements: CanvasElement[] = JSON.parse(JSON.stringify(sourceTemplate.elements));
if (useBilingual) {
  elements = elements.map((el: any) => ({ ...el, translatedLang: quickInvLang }));
}
      // Patch table rows with line items first
      let tableGrowthDelta = 0;
let tableOriginalBottom = 0;
let tableOriginalTop = 0;
elements = elements.map(el => {
  if (el.type !== 'table') return el;
  tableOriginalBottom = (el.y || 0) + (el.height || 0);
  tableOriginalTop = (el.y || 0);
  const newRows = lineItems.filter(li => li.description.trim()).map((li, i) => {
    const rawB2bName = li.b2bTaxName || (li as any).b2b_tax_name;
    const effectiveTaxName = isB2B
      ? (rawB2bName || (customTaxScheme as any)?.b2b_name || `${li.taxName || 'Tax'}4`)
      : (li.taxName || 'Tax');

    const resolveVal = (col: { key: string; header: string; id?: string }) => {
      const userVal = (li as any)[col.key] ?? (li as any)[col.header] ?? (li as any)[col.id || ''];
      if (userVal != null && String(userVal).trim() !== '') return String(userVal).trim();

      const p = catalogProducts.find(cp =>
        (li.description && cp.name?.trim().toLowerCase() === li.description.trim().toLowerCase()) ||
        ((li as any).barcode && cp.barcode === (li as any).barcode)
      );
      return getCatalogColValue(p, col) ?? '';
    };

    const resolveArVal = (col: { key: string; header: string }) => {
      const raw = resolveVal(col);
      return _tvForSave[`custom_${li.id}_${col.key}`] ||
             _tvForSave[`custom_${li.id}_${col.key.replace(/^cat_/, '')}`] ||
             _tvForSave[`custom_${li.id}_${col.header}`] ||
             _tvForSave[`tax_name_${raw}`] ||
             '';
    };

    return {
      id: `r-${Date.now()}-${i}`,
      values: {
        description: li.description,
        qty: li.qty,
        rate: li.rate,
        total: li.qty * li.rate,
        taxRate: getEffectiveTaxRate(li),
        taxName: effectiveTaxName,
        tax_name: effectiveTaxName,
        b2bTaxRate: isB2B ? (li.b2bTaxRate ?? (li as any).b2b_tax_rate) : undefined,
        b2bTaxName: isB2B ? effectiveTaxName : undefined,
        taxInclusive: !!li.taxInclusive,
        arabicDescription: bilingualEnabled ? (translatedValuesRef.current[`item_${li.id}`] || '') : '',
        // Save BOTH col.key ('shelf_no') and col.header ('Shelf No') so the PDF renderer never misses it
        ...Object.fromEntries(
          customColumns.flatMap(col => [
            [col.key, resolveVal(col)],
            [col.header, resolveVal(col)],
            [`arabic_${col.key}`, resolveArVal(col)],
            [`arabic_${col.header}`, resolveArVal(col)],
            [`arabicContent_${col.key}`, resolveArVal(col)],
            [`arabicContent_${col.header}`, resolveArVal(col)],
          ])
        ),
      },
    };
  });
  const defaultRowCount = el.rows?.length || 0;
  const rowDelta = newRows.length - defaultRowCount;
  const taxCount = taxLines.filter(t => t.rate > 0).length;
  const discCount = discountLines.filter(d => d.rate > 0).length;
  const minTableHeight = calcTableHeight(
  newRows.length,
  taxCount,
  discCount,
  shippingCost > 0,
  !!translatedTemplateRef.current,
  (PAGE_CONFIGS[template.pageSize || 'A4'] || PAGE_CONFIGS['A4']).width
);
  const expandedHeight = minTableHeight;
  tableGrowthDelta = minTableHeight - (el.height || 0);
  const manualDiscounts = discountLines
    .filter(d => d.rate > 0 && !d._isAuto && !/loyalty/i.test(d.label || ''))
    .map((d) => {
      const idx = discountLines.indexOf(d);
      const labelAr = discountLinesArForSave[idx] || _tvForSave[`discount_${idx}`] || '';
      return { label: d.label, rate: d.rate, translatedLabel: labelAr, labelAr };
    });

  const allDiscounts = [...manualDiscounts];
  if (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0) {
    const impliedRate = subtotal > 0 ? (autoDiscountAmount / subtotal) * 100 : 0;
    allDiscounts.push({ 
      label: autoDiscountRule.label || 'Loyalty Discount', 
      rate: parseFloat(impliedRate.toFixed(4)), 
      amount: autoDiscountAmount, 
      _isAuto: true 
    });
  }
  // Same fix as buildCwElements: col.width is a PERCENTAGE of the table,
  // not pixels — appending a column without rebalancing the others pushes
  // the row's total width over 100%, which the flex-shrink default then
  // visually compresses back down, squashing every column.
  const existingSaveCols = (el.columns || []).filter((c: any) => c.key === mainColKey || !hiddenColumns.includes(c.key));
  const existingSaveKeys = new Set(existingSaveCols.map((c: any) => c.key));
  const newSaveColsToAdd = customColumns.filter(col => !existingSaveKeys.has(col.key));
  let rebalancedSaveCols = existingSaveCols;
  if (newSaveColsToAdd.length > 0) {
    const perNewColWidth = 12;
    const totalNewWidth = perNewColWidth * newSaveColsToAdd.length;
    const existingTotalWidth = existingSaveCols.reduce((s: number, c: any) => s + (c.width || 0), 0) || 100;
    const shrinkFactor = Math.max(0, (100 - totalNewWidth)) / existingTotalWidth;
    rebalancedSaveCols = existingSaveCols.map((c: any) => ({ ...c, width: (c.width || 0) * shrinkFactor }));
  }
  const newSaveTableCols = newSaveColsToAdd.map(col => ({ id: col.id, key: col.key, header: col.header, align: col.align, width: 12, after: (col as any).after }));
  return {
    ...el,
    columns: fitThermalCols(placeCustomColumns(rebalancedSaveCols, newSaveTableCols), (PAGE_CONFIGS[template.pageSize || 'A4'] || PAGE_CONFIGS['A4']).width),
    height: expandedHeight,
    rows: newRows,
    taxes: taxLines.filter(t => t.rate > 0).map((t) => {
      const idx = taxLines.indexOf(t);
      const labelAr = taxLinesArForSave[idx] || _tvForSave[`tax_${idx}`] || '';
      return { label: t.label, rate: t.rate, translatedLabel: labelAr, labelAr };
    }),
    discounts: allDiscounts,
    shippingCost: shippingCost > 0 ? shippingCost : undefined,
    buyer_tax_id: clientGSTIN.trim() || undefined,
    buyerTaxId: clientGSTIN.trim() || undefined,
    // Baked-in Arabic labels — persisted on the table element itself so
    // a later reprint (BillsView, a fresh page load) has these without
    // needing this component's in-memory translatedValues.
    rowLabelsAr: bilingualEnabled ? rowLabelsArForSave : undefined,
    taxesAr: bilingualEnabled
      ? taxLines.filter(t => t.rate > 0).map(t => taxLinesArForSave[taxLines.indexOf(t)] || '')
      : undefined,
    discountsAr: bilingualEnabled
      ? discountLines.filter(d => d.rate > 0).map(d => discountLinesArForSave[discountLines.indexOf(d)] || '')
      : undefined,
  };
});

      // `total` (state above) was computed from the PRECISE, unrounded
      // autoDiscountAmount — but the loyalty discount just got saved into
      // the table element with its rate ROUNDED to 2 decimals (allDiscounts
      // above). Anything that later recomputes the total from the saved
      // elements (PDF render, WhatsApp caption, a future re-fetch) uses
      // that rounded rate and lands on a slightly different number than
      // `total`. Recompute here from the exact table element that's about
      // to be saved, so invoice.amount is guaranteed to match whatever
      // any future recomputation from these same elements produces.
      const _finalTableEl = elements.find((el: any) => el.type === 'table');
      let finalTotal = total;
      if (_finalTableEl) {
        let _subtotal = 0;
        const _lineBases: { base: number; taxRate: number }[] = [];
        (_finalTableEl.rows || []).forEach((r: any) => {
          const qty = parseFloat(String(r.values.qty)) || 0;
          const rate = parseFloat(String(r.values.rate)) || 0;
          const lineTotal = qty * rate;
          const rawB2b = r.values.b2bTaxRate ?? r.values.b2b_tax_rate;
          const taxRate = isB2B && rawB2b !== undefined && rawB2b !== null
            ? parseFloat(String(rawB2b)) || 0
            : parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
          const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
          const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
          _lineBases.push({ base, taxRate });
          _subtotal += base;
        });
        const _discount = ((_finalTableEl as any).discounts || []).reduce((s: number, d: any) => s + _subtotal * (d.rate / 100), 0);
        const _discountRatio = _subtotal > 0 ? Math.min(1, _discount / _subtotal) : 0;
        const _productTax = _lineBases.reduce((s, lb) => s + (lb.base * (1 - _discountRatio)) * (lb.taxRate / 100), 0);
        const _taxableValue = _subtotal - _discount;
        const _tax = ((_finalTableEl as any).taxes || []).reduce((s: number, t: any) => s + _taxableValue * (t.rate / 100), 0);
        const _shipping = _finalTableEl.shippingCost || 0;
        finalTotal = _taxableValue + _productTax + _tax + _shipping;
      }

     // Push every sibling element sitting at/below the table's ORIGINAL
      // (pre-growth) bottom down by however much the table just grew —
      // mirrors the same push buildCwElements does for the live preview.
      // Without this, a template's baked-in QR/notes/stamp stays at its
      // original y (sized for the template's sample rows) and ends up
      // floating inside the now-taller table once real line items push
      // the row count past what the template shipped with.
     // Removed: no longer pushing sibling elements here — handleExportPDF
     // in App.tsx now does this single push using real DOM-measured height.

console.log('QI PUSH DEBUG', {
  tableGrowthDelta,
  tableOriginalTop,
  tableOriginalBottom,
  qrElement: elements.find(el => el.textContent?.includes('Scan to Pay') || (el.type === 'qr')),
});

      // Auto-detect invoice vs receipt
      const documentType = detectDocumentType(template);

      // Auto-generate backend serial fields (Order No, Ref No, Token etc)
      const backendSerials = await autoGenerateSerialFields(template);
      const defaultStaff = authUser?.name?.trim() || 'Staff';

      const mergedDynamicValues = {
        ...backendSerials,
        staff_name: dynamicValues.staff_name || defaultStaff,
        ...dynamicValues,
        date: invoiceDate,
        time: invoiceTime,
      };

      if (isEditMode && editInvoice && onUpdateInvoice) {
        // EDIT MODE — takes the exact clean layout from the live screen preview
        const activeStaffName = (
          dynamicValues.staff_name ||
          authUser?.name ||
          'Staff'
        ).trim();

        const finalInvoiceNumber = editInvoice.invoiceNumber;
        const realInvoiceQrValue = `${window.location.origin}/invoice/view/${editInvoice.publicToken || ''}`;
        const QRCodeLib = await import('qrcode');
        const realInvoiceQrDataUrl = await QRCodeLib.default.toDataURL(realInvoiceQrValue, { width: 200, margin: 2 });

        const previewSnapshot = livePreviewElementsRef.current || [];
        const previewQrEl = previewSnapshot.find((el: any) => el.id === 'preview-invoice-qr');
        const previewQrLabelEl = previewSnapshot.find((el: any) => el.id === 'preview-invoice-qr-label');

        const sanitizedBase = previewSnapshot
          .filter((el: any) => !String(el.id || '').startsWith('preview-invoice-qr') && !String(el.id || '').startsWith('preview-tracking-qr'))
          .map((el: any) => {
            // Force the element's translatedLang to the current selection so old 'ar' doesn't contaminate Hindi
            const baseEl = bilingualEnabled ? { ...el, translatedLang: quickInvLang } : el;
            if (baseEl.type === 'signature') {
              return {
                ...baseEl,
                signatureName: (baseEl.signatureName || activeStaffName).replace(/\{staff_name\}/gi, activeStaffName),
              };
            }
            if (baseEl.type === 'barcode' && baseEl.barcodeValue) {
              return { ...baseEl, barcodeValue: baseEl.barcodeValue.replace(/\{invoice_number\}/g, finalInvoiceNumber) };
            }
            if (baseEl.type !== 'text' || !baseEl.textContent) return baseEl;
            let t = baseEl.textContent
              .replace(/\{invoice_number\}/g, finalInvoiceNumber)
              .replace(/\{staff_name\}/gi, activeStaffName)
              .replace(/\{date\}/g, invoiceDate)
              .replace(/\{client_name\}/g, clientName || '')
              .replace(/\{client_address\}/g, clientAddress || '');
            let ar = baseEl.arabicContent
              ? baseEl.arabicContent
                  .replace(/\{invoice_number\}/g, finalInvoiceNumber)
                  .replace(/\{staff_name\}/gi, translatedValues['staffName_translit'] || activeStaffName)
                  .replace(/\{date\}/g, invoiceDate)
              : undefined;
            return { ...baseEl, textContent: t, arabicContent: ar };
          })
          .concat(
            showInvoiceQR && previewQrEl ? [{
              id: `auto-invoice-qr-${editInvoice.id}`,
              type: 'qr',
              _auto: 'invoice',
              x: previewQrEl.x,
              y: previewQrEl.y,
              width: previewQrEl.width,
              height: previewQrEl.height,
              align: autoQrPosition,
              qrValue: realInvoiceQrValue,
              imageDataUrl: realInvoiceQrDataUrl,
              zIndex: 999,
              opacity: 100,
              rotation: 0,
            }] : [],
            showInvoiceQR && previewQrLabelEl ? [{
              id: `auto-invoice-qr-${editInvoice.id}-label`,
              type: 'text',
              _auto: 'invoice_label',
              x: previewQrLabelEl.x,
              y: previewQrLabelEl.y,
              width: previewQrLabelEl.width,
              height: previewQrLabelEl.height,
              textContent: 'Scan to view invoice',
              arabicContent: bilingualEnabled ? (translatedValuesRef.current['qr_caption_invoice'] || translatedValues['qr_caption_invoice'] || previewQrLabelEl.arabicContent || '') : undefined,
              textAlign: 'center',
              fontSize: autoQrLabelSize,
              zIndex: 999,
              opacity: 100,
              rotation: 0,
            }] : []
          );

        let sanitizedElements = sanitizedBase.map((el: any) =>
          el.type === 'table' && quickInvTaxBreakdown && quickInvTaxBreakdown.length > 0
            ? {
                ...el,
                taxBreakdown: quickInvTaxBreakdown.map(t => ({
                  name: t.name,
                  nameAr: bilingualEnabled
                    ? (taxBreakdownAr[t.name] || translatedValuesRef.current[`tax_name_${t.name}`])
                    : undefined,
                  rate: t.rate,
                  amount: t.amount,
                })),
              }
            : el
        );

        // Bake includeFields directly into elements[0] so the public scan-to-invoice page honors unticked fields
        if (sanitizedElements.length > 0) {
          sanitizedElements[0] = {
            ...sanitizedElements[0],
            includeFields: { ...includeFields },
            include_fields: { ...includeFields },
          };
        }

        // Build latest line items payload for the backend database
        const newLineItems = lineItems.filter(li => li.description.trim()).map(li => {
          const b = getItemTaxBreakdown(li);
          return {
            description: li.description,
            qty: li.qty,
            unit_price: li.rate,
            amount: li.qty * li.rate,
            tax_rate: getEffectiveTaxRate(li),
            tax_inclusive: !!li.taxInclusive,
            tax_amount: parseFloat(b.tax.toFixed(2)),
          };
        });

        await onUpdateInvoice(editInvoice.id, sanitizedElements, finalTotal, {
          clientName: (includeFields.name ? clientName : '').trim(),
          client_name: (includeFields.name ? clientName : '').trim(),
          clientEmail: (includeFields.email ? clientEmail : '').trim(),
          client_email: (includeFields.email ? clientEmail : '').trim(),
          clientPhone: (cleanPhone || clientPhone).trim(),
          client_phone: (cleanPhone || clientPhone).trim(),
          clientAddress: (includeFields.address ? clientAddress : '').trim(),
          client_address: (includeFields.address ? clientAddress : '').trim(),
          staff_name: activeStaffName,
          cashier_name: activeStaffName,
          updated_by_name: authUser?.name || 'Staff',
          buyer_tax_id: isValidTaxIdString(clientGSTIN) ? clientGSTIN.trim() : null,
          buyerTaxId: isValidTaxIdString(clientGSTIN) ? clientGSTIN.trim() : null,
          clientGSTIN: isValidTaxIdString(clientGSTIN) ? clientGSTIN.trim() : '',
          status: invoiceStatus as any,
          currency: activeCurrency,
          isBilingual: Boolean(bilingualEnabled),
          is_bilingual: Boolean(bilingualEnabled),
          translated_lang: bilingualEnabled ? quickInvLang : null,
          translatedLang: bilingualEnabled ? quickInvLang : null,
          qr_position: autoQrPosition,
          dynamic_values_ar: bilingualEnabled ? translatedValuesRef.current : {},
          dynamic_field_labels_ar: bilingualEnabled ? dynamicFieldLabelsAr : {},
          line_items: newLineItems,
          items: newLineItems,
          subtotal: subtotal,
          tax_total: productTaxTotal + taxTotal,
          discount_total: totalDiscountAmount,
        } as any);

        // Sync directly to the backend database to ensure public scan-to-invoice page updates immediately
        try {
          const jwt = localStorage.getItem('cloudairambo_jwt') || '';
          const syncRes = await fetch(`${BACKEND}/invoices/${editInvoice.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
            body: JSON.stringify({
              client_name: (includeFields.name ? clientName : '').trim(),
              client_email: (includeFields.email ? clientEmail : '').trim(),
              client_phone: (cleanPhone || clientPhone).trim(),
              client_address: (includeFields.address ? clientAddress : '').trim(),
              buyer_tax_id: (includeFields.taxId && isValidTaxIdString(clientGSTIN)) ? clientGSTIN.trim() : null,
              include_fields: { ...includeFields },
              amount: finalTotal,
              line_items: newLineItems,
              items: newLineItems,
              subtotal: subtotal,
              tax_total: productTaxTotal + taxTotal,
              discount_total: totalDiscountAmount,
              status: invoiceStatus.toLowerCase(),
              is_bilingual: Boolean(bilingualEnabled),
              translated_lang: bilingualEnabled ? quickInvLang : null,
              show_amount_in_words: showAmountInWords,
              amount_in_words: showAmountInWords ? amountToWords(finalTotal, activeCurrency) : null,
              amount_in_words_translated: (bilingualEnabled && showAmountInWords) ? (translatedValuesRef.current['amountInWords'] || '') : null,
              dynamic_values_ar: bilingualEnabled ? translatedValuesRef.current : {},
              dynamic_field_labels_ar: bilingualEnabled ? dynamicFieldLabelsAr : {},
              elements: sanitizedElements,
            }),
          });
          if (!syncRes.ok) {
            const errBody = await syncRes.text();
            console.error('[QI EDIT SYNC FAILED]', syncRes.status, errBody);
            showToast(`⚠️ Public invoice not updated (${syncRes.status})`);
          }
        } catch (e) {
          console.warn('Direct backend update sync failed:', e);
        }

        onAddLog('QUICK_INVOICE_UPDATED', `Updated invoice ${finalInvoiceNumber} for ${clientName}`);
        if (outletDisplayToken) {
          const jwt = localStorage.getItem('cloudairambo_jwt') || '';
          fetch(`${BACKEND}/display/push/${outletDisplayToken}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
            body: JSON.stringify({ bill_id: editInvoice.id, register_id: registerId }),
          }).catch(() => {});
        }
        await postLog('QUICK_INVOICE_UPDATED', `Invoice ${finalInvoiceNumber} updated for ${clientName} | Status: ${invoiceStatus}`);
        setSaving(false);
        setSaved(true);

        const updatedBillForModal = {
          ...editInvoice,
          id: editInvoice.id,
          invoiceNumber: finalInvoiceNumber,
          includeFields: { ...includeFields },
          include_fields: { ...includeFields },
          clientName: (includeFields.name ? clientName : '').trim(),
          client_name: (includeFields.name ? clientName : '').trim(),
          clientEmail: (includeFields.email ? clientEmail : '').trim(),
          client_email: (includeFields.email ? clientEmail : '').trim(),
          clientPhone: (includeFields.phone ? (cleanPhone || clientPhone) : '').trim(),
          client_phone: (includeFields.phone ? (cleanPhone || clientPhone) : '').trim(),
          clientAddress: (includeFields.address ? clientAddress : '').trim(),
          client_address: (includeFields.address ? clientAddress : '').trim(),
          buyerTaxId: (includeFields.taxId && isValidTaxIdString(clientGSTIN)) ? clientGSTIN.trim() : null,
          buyer_tax_id: (includeFields.taxId && isValidTaxIdString(clientGSTIN)) ? clientGSTIN.trim() : null,
          staff_name: activeStaffName,
          cashier_name: activeStaffName,
          cashierName: activeStaffName,
          updated_by_name: authUser?.name || 'Staff',
          amount: finalTotal,
          currency: activeCurrency,
          status: invoiceStatus,
          isBilingual: Boolean(bilingualEnabled),
          is_bilingual: Boolean(bilingualEnabled),
          translated_lang: bilingualEnabled ? quickInvLang : null,
          translatedLang: bilingualEnabled ? quickInvLang : null,
          elements: sanitizedElements,
          line_items: newLineItems,
          qr_position: autoQrPosition,
          amountInWordsAr: useBilingual ? (translatedValuesRef.current['amountInWords'] || '') : undefined,
        } as InvoiceRecord;

        if (onPostSave) {
          onPostSave(updatedBillForModal);
        }

        let updatePdfBlob: Blob | undefined;
        try {
          updatePdfBlob = await onExportPDF(updatedBillForModal, true);
          if (updatePdfBlob && autoDownloadPdfOnSave) {
            const url = URL.createObjectURL(updatePdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Invoice_${finalInvoiceNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 3000);
          }
        } catch (e) {
          console.warn('PDF generation on update failed:', e);
        }

        // 📲 Auto-Send Updated Invoice via WhatsApp (Just like POS)
        if (phoneDigits && onSendInvoiceWhatsApp) {
          try {
            await onSendInvoiceWhatsApp(updatedBillForModal, cleanPhone);
            postLog('INVOICE_WHATSAPP_SENT', `Updated invoice ${finalInvoiceNumber} sent via WhatsApp to ${clientName} | ${cleanPhone}`);
            showToast(`✅ Updated WhatsApp sent to ${cleanPhone}`, '#16a34a');
          } catch (e: any) {
            console.warn('WhatsApp update send failed:', e);
          }
        }

        // ✉️ Auto-Send Updated Invoice via Email (Just like POS)
        if (clientEmail.includes('@')) {
          try {
            let pdfBase64: string | undefined;
            if (updatePdfBlob) {
              pdfBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(updatePdfBlob!);
              });
            }

            const jwt = localStorage.getItem('cloudairambo_jwt') || '';
            const activeCompany = companyName || authUser?.name || 'Our Company';

            const emailRes = await fetch(`${BACKEND}/email/send-invoice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
              body: JSON.stringify({
                invoice_id: Number(editInvoice.id),
                to_email: clientEmail,
                attach_pdf: true,
                pdf_base64: pdfBase64,
                pdf_filename: `${finalInvoiceNumber}.pdf`,
                subject: `Updated Invoice: ${finalInvoiceNumber} — ${activeCompany}`,
                message: `Hello ${clientName},\n\nYour invoice #${finalInvoiceNumber} has been updated. The latest PDF is attached.\n\nThank you,\n${activeCompany}`,
              }),
            });

            if (emailRes.ok) {
              postLog('INVOICE_EMAIL_SENT', `Updated invoice ${finalInvoiceNumber} emailed to ${clientName} | ${clientEmail}`);
              showToast(`✅ Updated Email sent to ${clientEmail}`, '#16a34a');
            }
          } catch (e) {
            console.warn('Email update send failed:', e);
          }
        }

        onEditClose?.();
        return;
      }

      // CREATE MODE — build customer block cleanly with active includeFields
      const saveClientBlock = buildClientBlock(
        {
          name: clientName,
          phone: cleanPhone || clientPhone,
          email: clientEmail,
          address: clientAddress,
          taxId: clientGSTIN,
        },
        includeFields,
        Boolean(bilingualEnabled && useBilingual),
        {
          nameTr: translatedValuesRef.current['clientName'] || translatedValues['clientName'],
          addressTr: translatedValuesRef.current['clientAddress'] || translatedValues['clientAddress'],
          taxLabelTr: dynamicFieldLabelsAr['tax_id'],
        }
      );

      elements = elements.map(el =>
        resolveElementTokens(el, {
          invoiceNumber: invoiceNumberOverride.trim() || 'PENDING',
          staffName: activeStaffName,
          staffNameAr: translatedValues['staffName_translit'] || activeStaffName,
          clientDetailsEn: saveClientBlock.en,
          clientDetailsTr: saveClientBlock.tr,
          clientNameEn: includeFields.name ? clientName : '',
          notes,
        })
      );

      // Auto-expand background card shape and push table down before saving
      elements = reflowClientBlockAndShapes(elements);

      const { invoicesApi } = await import('../utils/api');
      const amountPaidOnSave = paymentMode === 'cash' ? finalTotal : 0;

      const savedInv = await invoicesApi.create({
        client_name: (includeFields.name ? clientName : '').trim(),
        client_email: (includeFields.email ? clientEmail : '').trim(),
        client_phone: (includeFields.phone ? cleanPhone : '').trim(),
        client_address: (includeFields.address ? clientAddress : '').trim(),
        staff_name: activeStaffName,
        cashier_name: activeStaffName,
        created_by_name: authUser?.name || 'Staff',
        amount: finalTotal,
        amount_paid: amountPaidOnSave,
        balance_due: Math.max(0, finalTotal - amountPaidOnSave),
        status: invoiceStatus,
        currency: activeCurrency,
        outlet_id: outletId || null,
        register_id: registerId,
        seller_country: qiOutletCountry,
        seller_region: qiOutletRegion || null,
        buyer_region: buyerRegion || taxIdMeta.detectedRegion || null,
        buyer_country: taxIdMeta.countryCode || qiOutletCountry,
        buyer_tax_id: clientGSTIN.trim() || null,
        elements: JSON.parse(JSON.stringify(elements)).map((el: any) => {
          if (bilingualEnabled && translatedTemplateRef.current) return el;
          const { arabicContent, ...rest } = el;
          return rest;
        }),
        page_size: template.pageSize || 'A4',
        line_items: lineItems.filter(li => li.description.trim()).map(buildFullLineItem),
        document_type: documentType,
        payment_mode: paymentMode,
        is_bilingual: Boolean(bilingualEnabled && useBilingual),
        translated_lang: bilingualEnabled ? quickInvLang : null,
        show_amount_in_words: showAmountInWords,
        amount_in_words: showAmountInWords ? amountToWords(total, activeCurrency) : null,
        amount_in_words_translated: (bilingualEnabled && showAmountInWords) ? (translatedValuesRef.current['amountInWords'] || '') : null,
        dynamic_values_ar: !bilingualEnabled ? {} : translatedValuesRef.current,
        discount_lines_ar: bilingualEnabled ? discountLinesArPayload : [],
        dynamic_field_labels_ar: bilingualEnabled ? dynamicFieldLabelsAr : {},
        show_invoice_qr: showInvoiceQR,
        show_tracking_qr: showTrackingQR,
        qr_position: autoQrPosition,
        qr_size: autoQrSize,
        qr_label_size: autoQrLabelSize,
        tracking_qr_position: trackingQrPosition,
        tracking_qr_size: trackingQrSize,
        tracking_qr_label_size: trackingQrLabelSize,
        ...(invoiceNumberOverride.trim() ? { invoice_number: invoiceNumberOverride.trim() } : {}),
      });

      const finalInvoiceNumber = invoiceNumberOverride.trim() || savedInv.invoice_number;
      mergedDynamicValues.invoice_number = finalInvoiceNumber;

// Generate the real invoice QR ourselves, the same way POSView does —
// same URL shape the backend itself uses (_invoice_qr_url in invoices.py:
// `${FRONTEND_URL}/invoice/view/${public_token}`). Don't wait on the
// backend's _auto elements merging into savedInv.elements in time for the
// thermal+bilingual snapshot render below — that race is what produced
// the faded "Invoice QR — generated on save" placeholder in the PDF.
const realInvoiceQrValue = `${window.location.origin}/invoice/view/${savedInv.public_token}`;
const QRCodeLib = await import('qrcode');
const realInvoiceQrDataUrl = await QRCodeLib.default.toDataURL(realInvoiceQrValue, { width: 200, margin: 2 });
// Keep the backend-assigned number in the live Quick Invoice state
// so preview + thermal USB + subsequent print all use the same number.
setInvoiceNumberOverride(finalInvoiceNumber);
setDynamicValues(prev => ({
  ...prev,
  invoice_number: finalInvoiceNumber,
}));

// UPI/card — show the QR the instant we have an invoice id, instead of
// waiting on the element-persist PUT and activity logs below. Those still
// run, just after the modal is already on screen.
if (paymentMode === 'upi' || paymentMode === 'card') {
  try {
    const gwRes = await fetch(`${BACKEND}/payments/bill/${savedInv.id}`);
    const gwData = gwRes.ok ? await gwRes.json() : null;
    const payLink = gwData?.payment_link || `${window.location.origin}/pay/bill/${savedInv.id}`;
    const QRCode = await import('qrcode');
    const qrDataUrl = await QRCode.default.toDataURL(payLink, { width: 260, margin: 2 });
    setPaymentWaitModal({ invoiceId: String(savedInv.id), invoiceNumber: finalInvoiceNumber, link: payLink, qr: qrDataUrl });
    if (outletDisplayToken) {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      fetch(`${BACKEND}/display/push/${outletDisplayToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ bill_id: savedInv.id, register_id: registerId }),
      }).catch(() => {});
    }
  } catch (e) {
    console.warn('Failed to fetch payment link:', e);
    alert('Invoice saved, but could not generate a payment link. You can send one manually from Bills.');
    handleReset();
    setSaved(true);
  }
}

// Auto-save new customer to Accounts directory (skip existing matches), or bump visit/spend stats
      if (clientName.trim()) {
        const myNormPhone = normalizePhoneForMatching(cleanPhone, qiOutletCountry);
        const existingMatch = accounts.find(a => {
          const aNormPhone = normalizePhoneForMatching(a.phone || '', qiOutletCountry);
          return (myNormPhone && aNormPhone && myNormPhone === aNormPhone) ||
                 (a.name.trim().toLowerCase() === clientName.trim().toLowerCase() && a.email.trim().toLowerCase() === clientEmail.trim().toLowerCase());
        });
        if (!existingMatch && onAddAccount && cleanPhone) {
          onAddAccount({
            name: clientName.trim(),
            email: clientEmail.trim(),
            phone: cleanPhone,
            company: '',
            address: clientAddress.trim(),
            visitCount: 1,
            totalSpend: total,
          });
        } else if (existingMatch && onUpdateAccount) {
          onUpdateAccount({
            ...existingMatch,
            visitCount: (existingMatch.visitCount || 0) + 1,
            totalSpend: (existingMatch.totalSpend || 0) + total,
          });
        }
      }
if (translatedTemplateRef.current) {
  translatedTemplateRef.current = {
    ...translatedTemplateRef.current,
    elements: translatedTemplateRef.current.elements.map((el: any) => {
      if (el.type === 'signature') {
        const translitCashier = translatedValues['staffName_translit'] || activeStaffName;
        return {
          ...el,
          signatureName: (el.signatureName || activeStaffName).replace(/\{staff_name\}/gi, activeStaffName),
          arabicSignatureName: (el.arabicSignatureName || translitCashier).replace(/\{staff_name\}/gi, translitCashier),
        };
      }
      if (el.type !== 'text') return el;
      // FSI (U+2066) ... PDI (U+2069) — Unicode bidi isolate marks. They tell
      // the renderer "this chunk is an independent LTR run," so an English
      // invoice number or Latin date dropped into Arabic text keeps its own
      // internal order instead of getting visually split/reordered around
      // the surrounding RTL text (the "half here, half there" bug).
      const ltrIsolate = (s: string) => `\u2066${s}\u2069`;

      const patchInvNum = (t: string, isArabicSide: boolean = false) => {
        const invNo = isArabicSide ? ltrIsolate(finalInvoiceNumber) : finalInvoiceNumber;
        const d = isArabicSide ? ltrIsolate(invoiceDate) : invoiceDate;
        return t
          .replace(/\{invoice_number\}/g, invNo)
          .replace(/\{date\}/g, d)
          .replace(/\{client_name\}/g, isArabicSide ? (translatedValues['clientName'] || clientName) : clientName)
          .replace(/\{client_address\}/g, isArabicSide ? (translatedValues['clientAddress'] || clientAddress) : clientAddress);
      };

      // Fill in dynamic field values (Patient, Power R/L/PD, etc.) on
      // BOTH sides — this ref is a separate clone from `elements` used
      // in CREATE MODE, so it never received mergedDynamicValues until
      // now. This is what the physical thermal/BT/USB auto-print
      // actually renders from.
      const patchDynamicVals = (t: string, isArabicSide: boolean, matchedKeysOut?: Set<string>) => {
        let out = t;
        for (const [key, rawValue] of Object.entries(mergedDynamicValues)) {
          const value = String(rawValue || '').trim();
          if (!value) continue;
          const labelPattern = key.replace(/_/g, '[\\s\\/\\.\\(\\)#_-]*');
          const fillValue = isArabicSide ? (translatedValues[key] || value) : value;
          let matched = false;

          if (isArabicSide) {
            const arLabel = dynamicFieldLabelsAr[key];
            if (arLabel) {
              const escLabel = arLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const before = out;
              out = out.replace(
                new RegExp(`(${escLabel})[ \\t]*:?[ \\t]*(?:\\[[^\\]]*\\]|_{2,}|[-—]{1,3}|[^\\n]*?)(?=[ \\t]{2,}\\S|\\n|$)`, 'g'),
                (_m: string, l: string) => `${l}: ${fillValue}`
              );
              if (out !== before) matched = true;
            }
          }

          if (!matched) {
            const before = out;
            out = out.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_m: string, l: string) => `${l}: ${fillValue}`);
            out = out.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*_{2,}`, 'gi'), (_m: string, l: string) => `${l}: ${fillValue}`);
            out = out.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*[-—]{1,3}`, 'gi'), (_m: string, l: string) => `${l}: ${fillValue}`);
            if (out !== before) matched = true;
          }

          // Compound labels (e.g. "Power R") whose prefix word gets
          // translated away, leaving only the tail marker ("R") intact
          // in the Arabic text with no link back to it via the full
          // label pattern. Fall back to matching just that tail word.
          if (!matched && isArabicSide) {
            const fullLabel = dynamicFields.find(f => f.key === key)?.label || '';
            const words = fullLabel.trim().split(/\s+/).filter(Boolean);
            const tail = words[words.length - 1];
            if (tail && tail !== fullLabel && /^[A-Za-z0-9]{1,4}$/.test(tail)) {
              const escTail = tail.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const before = out;
              out = out.replace(new RegExp(`\\b(${escTail})\\b\\s*:\\s*\\[[^\\]]*\\]`, 'g'), (_m: string, l: string) => `${l}: ${fillValue}`);
              out = out.replace(new RegExp(`\\b(${escTail})\\b\\s*:\\s*_{2,}`, 'g'), (_m: string, l: string) => `${l}: ${fillValue}`);
              out = out.replace(new RegExp(`\\b(${escTail})\\b\\s*:\\s*[-—]{1,3}`, 'g'), (_m: string, l: string) => `${l}: ${fillValue}`);
              if (out !== before) matched = true;
            }
          }

          if (matched && matchedKeysOut) matchedKeysOut.add(key);
        }
        return out;
      };

      // FINAL PASS — position-based fallback for whatever `patchDynamicVals`
      // above couldn't text-match. Must run as ONE pass across ALL fields,
      // not per-field: two fields sharing the same original line (e.g.
      // "Patient: sdfv   Age: dsfv" on one line) each resolve to the SAME
      // lineIdx, so patching them one at a time — overwrite line, then
      // overwrite line again for the next field — silently discards
      // whichever field got written first. This is exactly why "Patient"
      // was blank while "Age" printed fine: Age was processed after
      // Patient and stomped the whole line.
      const runFinalLinePass = (t: string, isArabicSide: boolean, alreadyMatchedKeys: Set<string>): string => {
        if (!isArabicSide) return t;
        const originalLines = (el.textContent || '').split('\n');

        // Group every STILL-unmatched field by which original line its
        // label appears on, preserving left-to-right order within the line.
        const lineToFields = new Map<number, { key: string; label: string; index: number }[]>();
        for (const [key, rawValue] of Object.entries(mergedDynamicValues)) {
          if (alreadyMatchedKeys.has(key)) continue;
          const value = String(rawValue || '').trim();
          if (!value) continue;
          const fullLabel = dynamicFields.find(f => f.key === key)?.label || '';
          if (!fullLabel) continue;
          const escFullLabel = fullLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const lineIdx = originalLines.findIndex((l: string) =>
            new RegExp(`\\b${escFullLabel}\\b\\s*:`, 'i').test(l)
          );
          if (lineIdx === -1) continue;
          const labelIndexInLine = originalLines[lineIdx].search(new RegExp(`\\b${escFullLabel}\\b\\s*:`, 'i'));
          if (!lineToFields.has(lineIdx)) lineToFields.set(lineIdx, []);
          lineToFields.get(lineIdx)!.push({ key, label: fullLabel, index: labelIndexInLine });
        }

        if (lineToFields.size === 0) return t;

        const outLines = t.split('\n');
        lineToFields.forEach((fields, lineIdx) => {
          if (outLines[lineIdx] === undefined) return;
          // Keep left-to-right order matching the original line's layout.
          fields.sort((a, b) => a.index - b.index);
          outLines[lineIdx] = fields
            .map(f => {
              const rawValue = String(mergedDynamicValues[f.key] || '').trim();
              const fillValue = translatedValues[f.key] || rawValue;
              const arLabel = dynamicFieldLabelsAr[f.key] || f.label;
              return `${arLabel}: ${fillValue}`;
            })
            .join('   ');
        });
        return outLines.join('\n');
      };

      let newText = el.textContent ? patchInvNum(el.textContent, false) : el.textContent;
      let newArabic = el.arabicContent ? patchInvNum(el.arabicContent, true) : el.arabicContent;
      if (newText) newText = patchDynamicVals(newText, false);
      if (newArabic) {
        const matchedKeys = new Set<string>();
        newArabic = patchDynamicVals(newArabic, true, matchedKeys);
        newArabic = runFinalLinePass(newArabic, true, matchedKeys);
      }

      return {
        ...el,
        textContent: newText,
        arabicContent: newArabic,
      };
    }),
  };
}
      // NOW patch text elements with the real invoice number from backend.
      // Template-baked QR placeholders are legacy scaffolding — the
      // backend's _auto QR (secure, token-based) merged in below is the
      // only QR that should reach the final bill. Drop this one instead
      // of patching a fallback value into it, or it renders as a second,
      // dead QR alongside the real one.
      // Also drop the template's own static QR caption text — same issue
      // as the qr element itself: it sits at whatever y the template
      // designer originally placed it, not next to the real QR, so it
      // was rendering after "Bill No"/"Goods once sold" instead of right
      // under the backend's correctly-positioned _auto_label.
      elements = elements.filter(el =>
        (el.type !== 'qr' || !isPlaceholderQr(el, elements)) &&
        !(el.type === 'text' && (el.textContent === 'Scan to view invoice' || el.textContent === 'Scan to track your order'))
      );
      elements = elements.map(el => {
        if (el.type === 'signature') {
          const sig = (el.signatureName || activeStaffName).replace(/\{staff_name\}/gi, activeStaffName);
          const text = (el.textContent || activeStaffName).replace(/\{staff_name\}/gi, activeStaffName);
          return {
            ...el,
            signatureName: sig,
            textContent: text,
          };
        }
        if (el.type === 'barcode' && el.barcodeValue) {
          return { ...el, barcodeValue: el.barcodeValue.replace(/\{invoice_number\}|\bPENDING\b/g, finalInvoiceNumber) };
        }
        if (el.type !== 'text' || !el.textContent) return el;
        let t = el.textContent;
        const isArabic = /[\u0600-\u06FF]/.test(t);
        t = patchDynamicFields(t, mergedDynamicValues);
        t = t
          .replace(/\{invoice_number\}|\bPENDING\b/g, finalInvoiceNumber)
          .replace(/\{staff_name\}/gi, activeStaffName)
          .replace(/(Patient|Member|Student|Customer|Client|Pet|Guest):\s*\[Name\]/gi, `$1: ${activeCust}`)
          .replace(/\[(Name|Patient|Client|Customer|Guest|Member|Student|Pet)\]/gi, activeCust)
          .replace(/\[Name\]/gi, activeCust)
          .replace(/\{date\}/g, invoiceDate)
          .replace(/\{client_name\}/g, clientName || '')
          .replace(/\{client_address\}/g, clientAddress || '');

        // Mirror dynamic field values into arabicContent too — this was
        // never happening before, which is why translated labels like
        // "الطاقة R:" kept their placeholder underscores/brackets even
        // after the English side ("Power R:") got the real value.
        if (el.arabicContent && mergedDynamicValues) {
          let arT = el.arabicContent;
          arT = patchDynamicFields(arT, mergedDynamicValues);
          for (const [key, value] of Object.entries(mergedDynamicValues)) {
            if (!value) continue;
            const labelPattern = key.replace(/_/g, '[\\s\\/\\.\\(\\)#]*');
            const arLabel = (dynamicFieldLabelsAr as any)?.[key];
            const arValue =
  (dynamicValues[key] && /^\d+$/.test(String(dynamicValues[key]).trim()) && quickInvLang === 'ar')
    ? toArabicIndicDigits(String(dynamicValues[key]).trim())
    : (translatedValues as any)?.[key] || value;

            // If we know the translated label text, fill whatever
            // placeholder (or nothing) follows it.
            if (arLabel) {
              const escLabel = arLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              arT = arT.replace(
                new RegExp(`(${escLabel})[ \\t]*:?[ \\t]*(?:\\[[^\\]]*\\]|_{2,}|[-—]{1,3}|[^\\n]*?)(?=[ \\t]{2,}\\S|\\n|$)`, 'g'),
                (_m: string, l: string) => `${l}: ${arValue}`
              );
            }

            // Fallback: label was left untranslated (still English) inside arabicContent.
            arT = arT.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_: string, l: string) => `${l}: ${arValue}`);
            arT = arT.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*_{2,}`, 'gi'), (_: string, l: string) => `${l}: ${arValue}`);
            arT = arT.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*[-—]{1,3}`, 'gi'), (_: string, l: string) => `${l}: ${arValue}`);
          }
          el = { ...el, arabicContent: arT };
        }

        // Mirror {staff_name} replacement into arabicContent
        if (el.arabicContent) {
          const translitCashier = translatedValues['staffName_translit'] || activeStaffName;
          el.arabicContent = el.arabicContent
            .replace(/\{invoice_number\}/g, finalInvoiceNumber)
            .replace(/\{staff_name\}/gi, translitCashier)
            .replace(/\{date\}/g, invoiceDate);
        }

     
        if (notes && (t.toLowerCase().startsWith('notes:') || t.toLowerCase().startsWith('terms:'))) {
          t = `Notes:\n${notes}`;
        }
        const waNum = (authUser as any)?.outlet_whatsapp_number || (window as any).__outlet_wa_number;
        const isClientBlock2 = t.toLowerCase().startsWith('bill to:') || 
                               t.toLowerCase().startsWith('client:') ||
                               t.toLowerCase().startsWith('patient:') ||
                               t.toLowerCase().startsWith('customer:');
        if (waNum && !isClientBlock2) {
          if (t.toLowerCase().includes('whatsapp')) {
            t = t.replace(/WhatsApp:\s*[\+\d\s\-]*/gi, `WhatsApp: ${waNum}`);
          } else if (
            t.toLowerCase().includes('mob:') ||
            t.toLowerCase().includes('mobile:') ||
            t.toLowerCase().includes('phone:') ||
            t.toLowerCase().includes('contact:') ||
            t.toLowerCase().includes('call us:')
          ) {
            t = t.replace(/(\+91[\s\-]?\d{5}[\s\-]?\d{5}|\b\d{10}\b)/g, waNum);
          }
        }
        return { ...el, textContent: t };
      });

      const backendAutoElsForBill = (savedInv.elements || []).filter((el: any) => el._auto);
      const filteredAutoElsForBill = backendAutoElsForBill.filter((el: any) => {
        if (el._auto === 'invoice' || el._auto === 'invoice_label') return showInvoiceQR;
        if (el._auto === 'tracking' || el._auto === 'tracking_label') return showTrackingQR;
        return true;
      });
      // The backend's own _auto label elements never carry arabicContent —
      // same issue already fixed for the thermal print path (see
      // bilingualAutoEls in sendPostPaymentDeliverables below). Patch it
      // here too, or the QR caption saved to this bill stays English-only.
      const patchedAutoElsForBill = filteredAutoElsForBill.map((el: any) => {
        if (el._auto === 'invoice_label') return { ...el, arabicContent: translatedValuesRef.current['qr_caption_invoice'] || translatedValues['qr_caption_invoice'] || '' };
        if (el._auto === 'tracking_label') return { ...el, arabicContent: translatedValuesRef.current['qr_caption_tracking'] || translatedValues['qr_caption_tracking'] || '' };
        return el;
      });
     const elementsForBill = translatedTemplateSnapshotForSave
  ? elements.map((el: any) => {
      const translatedEl =
        translatedTemplateSnapshotForSave?.elements?.find(
          (tel: any) => tel.id === el.id
        );

      if (!translatedEl) return el;

      return {
        ...el,
        // el.arabicContent is already correctly patched (real invoice #,
        // date, dynamic fields) — don't overwrite it with the stale,
        // pre-patch snapshot text, or every placeholder comes back.
        ...(el.type === 'table'
          ? {
              columns: el.columns?.map((col: any, i: number) => ({
                ...col,
                arabicHeader:
                  translatedEl.columns?.[i]?.arabicHeader ??
                  col.arabicHeader,
              })),
            }
          : {}),
      };
    })
  : elements;

const overlapResolvedElementsForBill =
  enforceNoTableOverlap(elementsForBill);

// Ground truth for layout is what the live preview last rendered
// (livePreviewElementsRef). Only patch in what wasn't known until after
// save: the real invoice number, and the backend's real secure QR —
// swapped in at the exact position the preview's placeholder occupied,
// so nothing shifts. Only applies to the non-bilingual preview (the
// bilingual path renders through BilingualInvoiceRenderer, which this
// ref does not capture).
const useLivePreviewSnapshot = !translatedTemplateSnapshotForSave && livePreviewElementsRef.current;
if (useLivePreviewSnapshot) {
  const previewSnapshot = livePreviewElementsRef.current || [];
  const previewQrEl = previewSnapshot.find((el: any) => el.id === 'preview-invoice-qr');
  const previewQrLabelEl = previewSnapshot.find((el: any) => el.id === 'preview-invoice-qr-label');
  const realQrEl = patchedAutoElsForBill.find((el: any) => el._auto === 'invoice');
  const realQrLabelEl = patchedAutoElsForBill.find((el: any) => el._auto === 'invoice_label');

  // `elements` above already went through the full Invoice #/PO #/Bill No/
  // Due Date/dynamic-field regex patching — that's the correct, real content.
  // The preview snapshot is only trustworthy for POSITION (layout), never
  // for text — its own patch logic only knew how to swap one exact
  // placeholder string, so any field that didn't match verbatim (a
  // template's baked-in sample PO#/invoice#/dates) shipped unpatched.
  const patchedById = new Map(elements.map((el: any) => [el.id, el]));

  var finalSafeElementsFromPreview = previewSnapshot
    .filter((el: any) => !String(el.id || '').startsWith('preview-invoice-qr') && !String(el.id || '').startsWith('preview-tracking-qr'))
    .map((el: any) => {
      if (el.type === 'signature') {
        const sig = (el.signatureName || activeStaffName).replace(/\{staff_name\}/gi, activeStaffName);
        const text = (el.textContent || activeStaffName).replace(/\{staff_name\}/gi, activeStaffName);
        return { ...el, signatureName: sig, textContent: text };
      }
      if (el.type !== 'text') return el;
      const patchedEl = patchedById.get(el.id);
      let content = patchedEl?.textContent || el.textContent || '';
      content = content
        .replace(/\{invoice_number\}|\bPENDING\b/g, finalInvoiceNumber)
        .replace(/\{staff_name\}/gi, activeStaffName)
        .replace(/(Patient|Member|Student|Customer|Client|Pet|Guest):\s*\[Name\]/gi, `$1: ${activeCust}`)
        .replace(/\[(Name|Patient|Client|Customer|Guest|Member|Student|Pet)\]/gi, activeCust)
        .replace(/\[Name\]/gi, activeCust);

      return {
        ...el,
        textContent: content,
        arabicContent: patchedEl?.arabicContent || el.arabicContent,
      };
    })
    .concat(
      // Give these a stable, unique id if the backend didn't set one —
      // both realQrEl and realQrLabelEl otherwise carry id:undefined,
      // which collides in every downstream Map/lookup keyed by el.id
      // (PublicInvoiceView's row-clamping being the visible symptom).
      realQrEl && previewQrEl
        ? [{ ...realQrEl, id: realQrEl.id || `auto-invoice-qr-${savedInv.id}`, x: previewQrEl.x, y: previewQrEl.y, width: previewQrEl.width, height: previewQrEl.height }]
        : [],
      realQrLabelEl && previewQrLabelEl
        ? [{ ...realQrLabelEl, id: realQrLabelEl.id || `auto-invoice-qr-${savedInv.id}-label`, x: previewQrLabelEl.x, y: previewQrLabelEl.y, width: previewQrLabelEl.width, height: previewQrLabelEl.height }]
        : []
    );
}

// Amount-in-Words was only ever added to patchedTemplateRef.current
// (used solely for the on-screen auto-download), never to the elements
// actually saved/persisted here — which is what WhatsApp send and any
// later re-fetch of this invoice draw from. Build it into the SAVED
// element set too, or it silently disappears from every export except
// the immediate auto-download.
if (showAmountInWords) {
  const tableElForBill = overlapResolvedElementsForBill.find((el: any) => el.type === 'table');
  if (tableElForBill) {
    const amountWords = amountToWords(total, activeCurrency);
    const tableBottomForBill = (tableElForBill.y || 0) + (tableElForBill.height || 0);
    const totalsClearance = 12;
    const wordsHeight = Math.max(30, amountInWordsStyle.fontSize * 3);

    overlapResolvedElementsForBill.forEach((el: any) => {
      if (el.type === 'table') return;
      if ((el.y || 0) >= (tableElForBill.y || 0)) {
        el.y = (el.y || 0) + totalsClearance + wordsHeight;
      }
    });

    overlapResolvedElementsForBill.push({
      id: 'pdf-amount-in-words',
      type: 'text',
      x: 20,
      y: tableBottomForBill + totalsClearance,
      width: Math.max(100, (PAGE_CONFIGS[template.pageSize || 'A4'] || PAGE_CONFIGS['A4']).width - 40),
      height: wordsHeight,
      zIndex: 998,
      opacity: 100,
      rotation: 0,
      textContent: `Amount in Words: ${amountWords}`,
      arabicContent: translatedTemplateRef.current ? (translatedValues['amountInWords'] || '') : undefined,
      fontFamily: amountInWordsStyle.fontFamily,
      fontSize: amountInWordsStyle.fontSize,
      textAlign: amountInWordsStyle.textAlign,
      fontStyle: amountInWordsStyle.fontStyle,
      fontWeight: amountInWordsStyle.fontWeight,
      color: amountInWordsStyle.color,
    });
  }
}

const finalSafeElements = useLivePreviewSnapshot
  ? finalSafeElementsFromPreview
  : [
      ...overlapResolvedElementsForBill,
      ...repositionAutoGroups(
        overlapResolvedElementsForBill,
        patchedAutoElsForBill,
        {
          invoice: autoQrOffset,
          tracking: trackingQrOffset,
        }
      ),
    ];

// Only inject the real scannable link and QR image data into the auto invoice QR:
finalSafeElements.forEach((el: any) => {
  const isAutoInvoiceQr = el._auto === 'invoice' || 
                          el.id === 'preview-invoice-qr' || 
                          el.id?.startsWith('auto-invoice-qr-');

  // ONLY overwrite the auto-injected invoice QR — NEVER touch template custom QRs (Net30 / UPI / Payment)
  if (el.type === 'qr' && isAutoInvoiceQr) {
    el.qrValue = realInvoiceQrValue;
    el.imageDataUrl = realInvoiceQrDataUrl;
  }
  if (el.type === 'table' && quickInvTaxBreakdown && quickInvTaxBreakdown.length > 0) {
    el.taxBreakdown = quickInvTaxBreakdown.map(t => ({
      name: t.name,
      nameAr: (bilingualEnabled ? (taxBreakdownAr[t.name] || translatedValuesRef.current[`tax_name_${t.name}`]) : undefined),
      rate: t.rate,
      amount: t.amount
    }));
  }
});

// Bake includeFields directly into elements[0] so the public scan-to-invoice page honors unticked fields
if (finalSafeElements.length > 0) {
  finalSafeElements[0] = {
    ...finalSafeElements[0],
    includeFields: { ...includeFields },
    include_fields: { ...includeFields },
  };
}

const fullLineItemsList = lineItems.filter(li => li.description.trim()).map(buildFullLineItem);

const newBill = {
  id: String(savedInv.id),
  invoiceNumber: finalInvoiceNumber,
  includeFields: { ...includeFields },
  include_fields: { ...includeFields },
  line_items: fullLineItemsList,
  items: fullLineItemsList,
  clientName: (includeFields.name ? clientName : '').trim(),
  client_name: (includeFields.name ? clientName : '').trim(),
  clientNameAr: translatedValuesRef.current['clientName'] || translatedValues['clientName'] || undefined,
  client_name_ar: translatedValuesRef.current['clientName'] || translatedValues['clientName'] || undefined,
  clientEmail: (includeFields.email ? clientEmail : '').trim(),
  client_email: (includeFields.email ? clientEmail : '').trim(),
  clientPhone: (includeFields.phone ? cleanPhone : '').trim(),
  client_phone: (includeFields.phone ? cleanPhone : '').trim(),
  clientAddress: (includeFields.address ? clientAddress : '').trim(),
  client_address: (includeFields.address ? clientAddress : '').trim(),
  clientAddressAr: translatedValuesRef.current['clientAddress'] || translatedValues['clientAddress'] || undefined,
  client_address_ar: translatedValuesRef.current['clientAddress'] || translatedValues['clientAddress'] || undefined,
  // 👤 Include staff audit metadata in frontend state
  staff_name: activeStaffName,
  cashier_name: activeStaffName,
  cashierName: activeStaffName,
  created_by_name: authUser?.name || 'Staff',
  buyer_tax_id: clientGSTIN.trim() || null,
  buyerTaxId: clientGSTIN.trim() || null,
  clientGSTIN: clientGSTIN.trim() || '',
  amount: finalTotal,
  amountPaid: amountPaidOnSave,
  currency: activeCurrency,
  status: invoiceStatus,
  payment_mode: paymentMode,
  createdAt: savedInv.created_at || new Date().toISOString(),
  elements: finalSafeElements,
  pageSize: template.pageSize || 'A4',
  isBilingual: useBilingual,
  showAmountInWords,
  sellerCountry: qiOutletCountry,
  sellerRegion: qiOutletRegion || null,
  buyerRegion: buyerRegion || null,
  publicToken: savedInv.public_token || undefined,
  // The immediate post-save auto-download PDF renders from THIS object,
  // not a backend refetch — without this field, renderBilingualInvoiceToPdf
  // always fell back to 'center' regardless of what was picked here.
  qr_position: autoQrPosition,

dynamicValues: {
  ...dynamicValues,
  invoice_number: finalInvoiceNumber,
},

  dynamic_values_ar: !useBilingual ? {} : {
  ...translatedValues,
  ...Object.fromEntries(
    dynamicFields
      .filter(f => dynamicValues[f.key]?.trim())
      .map(f => [
        f.key,
        /^\d+$/.test(dynamicValues[f.key].trim()) && quickInvLang === 'ar'
          ? toArabicIndicDigits(dynamicValues[f.key].trim())
          : translatedValues[f.key] || dynamicValues[f.key].trim()
      ])
  ),
},
  dynamic_field_labels_ar: dynamicFieldLabelsAr,
} as InvoiceRecord;

// invoicesApi.create() above was called BEFORE finalInvoiceNumber/
// mergedDynamicValues existed, so the backend's stored `elements` still
// has raw "[Name]"/"__"/"{invoice_number}" placeholders. Persist the
// patched version now so a fresh fetch (e.g. BillsView's Download PDF)
// doesn't reproduce the unpatched template.
try {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const elementsToPersist = finalSafeElements.map((el: any) => {
    if (!bilingualEnabled) {
      const { arabicContent, translatedLang, ...rest } = el;
      if (rest.type === 'table') {
        const cleanTable = { ...rest };
        delete cleanTable.rowLabelsAr;
        delete cleanTable.taxesAr;
        delete cleanTable.discountsAr;
        if (cleanTable.columns) {
          cleanTable.columns = cleanTable.columns.map((c: any) => {
            const { arabicHeader, ...colRest } = c;
            return colRest;
          });
        }
        if (cleanTable.rows) {
          cleanTable.rows = cleanTable.rows.map((r: any) => {
            const cleanVals = { ...r.values };
            delete cleanVals.arabicDescription;
            delete cleanVals.arabicName;
            Object.keys(cleanVals).forEach(k => {
              if (k.startsWith('arabic_') || k.startsWith('arabicContent_')) {
                delete cleanVals[k];
              }
            });
            return { ...r, values: cleanVals };
          });
        }
        if (cleanTable.taxBreakdown) {
          cleanTable.taxBreakdown = cleanTable.taxBreakdown.map((t: any) => ({
            name: t.name,
            rate: t.rate,
            amount: t.amount,
          }));
        }
        return cleanTable;
      }
      return rest;
    }
    return el;
  });

  await fetch(`${BACKEND}/invoices/${savedInv.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({
      buyer_tax_id: clientGSTIN.trim() || null,
      is_bilingual: Boolean(bilingualEnabled),
      translated_lang: bilingualEnabled ? quickInvLang : null,
      dynamic_values_ar: bilingualEnabled ? translatedValuesRef.current : {},
      dynamic_field_labels_ar: bilingualEnabled ? dynamicFieldLabelsAr : {},
      discount_lines_ar: bilingualEnabled ? discountLinesArPayload : [],
      show_amount_in_words: showAmountInWords,
      amount_in_words: showAmountInWords ? amountToWords(total, activeCurrency) : null,
      amount_in_words_translated: (bilingualEnabled && showAmountInWords) ? (translatedValuesRef.current['amountInWords'] || '') : null,
      line_items: fullLineItemsList,
      items: fullLineItemsList,
      elements: elementsToPersist,
    }),
  });
} catch (e) {
  console.warn('Failed to persist patched elements after save:', e);
}

      setBills(prev => [newBill, ...prev]);
      onAddLog('QUICK_INVOICE_CREATED', `Quick invoice ${finalInvoiceNumber} created for ${clientName}`);
      await postLog('QUICK_INVOICE_CREATED', `Invoice ${finalInvoiceNumber} for ${clientName} via Quick Invoice`);

      setSaving(false);
      // NOTE: onPostSave (the "Invoice Saved!" popup) is intentionally NOT
      // called here anymore. For card/UPI it used to fire immediately,
      // opening at the same time as the payment-wait QR modal below —
      // two modals stacked on an invoice that isn't even paid yet. It's
      // now called only for cash (further down) or once payment is
      // actually confirmed, inside sendPostPaymentDeliverables.

const sendPostPaymentDeliverables = async () => {
let pdfBlobForSend: Blob | undefined;
// Auto-print via puppeteer — use already-patched elements directly
try {
  const isThermal = (template.pageSize || '') === 'THERMAL_80MM';
const computedPdfHeight = previewPageBehavior === 'auto_expand'
  ? undefined // set below once cwData exists
  : PAGE_CONFIGS[template.pageSize || 'A4'].height;
const patchedTemplate = { ...template, elements };
// Merge backend _auto elements (QRs injected by backend after save)
const backendAutoEls = (savedInv.elements || []).filter((el: any) => el._auto);
const filteredAutoEls = backendAutoEls.filter((el: any) => {
  if (el._auto === 'invoice' || el._auto === 'invoice_label') return showInvoiceQR;
  if (el._auto === 'tracking' || el._auto === 'tracking_label') return showTrackingQR;
  return true;
});

// Patch invoice number into bilingual template elements


// If bilingual, patchedTemplateRef uses the already-patched translatedTemplateRef
if (bilingualEnabled && translatedTemplateRef.current) {
  const overlapResolvedBilingual = enforceNoTableOverlap(translatedTemplateRef.current.elements);
  // The backend's own QR-label elements never carry arabicContent —
  // patch in the pre-translated captions here so the actual saved/printed
  // PDF matches what the preview shows, not just the preview.
  const bilingualAutoEls = filteredAutoEls.map((el: any) => {
    if (el._auto === 'invoice_label') return { ...el, arabicContent: translatedValuesRef.current['qr_caption_invoice'] || translatedValues['qr_caption_invoice'] || '' };
    if (el._auto === 'tracking_label') return { ...el, arabicContent: translatedValuesRef.current['qr_caption_tracking'] || translatedValues['qr_caption_tracking'] || '' };
    return el;
  });
  // For thermal receipts specifically, don't trust the backend's
  // filteredAutoEls timing at all — replace its invoice QR element's
  // qrValue with the real one we generated ourselves above (same URL
  // POSView and the backend itself both use), and its image render (via
  // POSReceiptRenderer's FlowElement) will produce a real, scannable QR
  // instead of the "no real backend QR found" dummy fallback.
   const bilingualAutoElsFixed = bilingualAutoEls.map((el: any) => {
    const isAutoInvoiceQr = el._auto === 'invoice' || el.id === 'preview-invoice-qr' || el.id?.startsWith('auto-invoice-qr-');
    return isAutoInvoiceQr && el.type === 'qr'
      ? { ...el, qrValue: realInvoiceQrValue, imageDataUrl: realInvoiceQrDataUrl }
      : el;
  });
  // The backend's `_auto: 'invoice_label'` caption may not have merged
  // into savedInv.elements yet by the time this runs (same race already
  // documented above for the QR image itself). If it's missing, the QR
  // image still renders (we generated it ourselves) but the "Scan to
  // view invoice" caption silently disappears from the PDF. Synthesize
  // the caption locally, same fallback philosophy as the QR pixel fix
  // just above, instead of depending on backend timing.
  const hasInvoiceLabelEl = bilingualAutoElsFixed.some((el: any) => el._auto === 'invoice_label');
  console.log('[LABEL FIX DEBUG] before synth:', {
    hasInvoiceLabelEl,
    allAutoTypes: bilingualAutoElsFixed.map((el: any) => ({ id: el.id, _auto: el._auto, type: el.type })),
  });
  if (showInvoiceQR && !hasInvoiceLabelEl) {
    const invoiceQrEl = bilingualAutoElsFixed.find((el: any) => el._auto === 'invoice');
    if (invoiceQrEl) {
      bilingualAutoElsFixed.push({
        id: `${invoiceQrEl.id}-label`,
        type: 'text',
        _auto: 'invoice_label',
        x: (invoiceQrEl.x || 0) - 40,
        y: (invoiceQrEl.y || 0) + (invoiceQrEl.height || 0) + 4,
        width: (invoiceQrEl.width || 0) + 80,
        height: 16,
        zIndex: 999,
        opacity: 100,
        rotation: 0,
        textContent: 'Scan to view invoice',
        arabicContent: translatedValuesRef.current['qr_caption_invoice'] || translatedValues['qr_caption_invoice'] || '',
        fontSize: autoQrLabelSize,
        textAlign: 'center',
        color: '#000000',
        fontWeight: '800',
        fontFamily: 'Inter',
      });
    }
  }
  console.log('[LABEL FIX DEBUG] after synth, before reposition:', {
    allAutoTypes: bilingualAutoElsFixed.map((el: any) => ({ id: el.id, _auto: el._auto, type: el.type })),
  });
  const repositioned = repositionAutoGroups(overlapResolvedBilingual, bilingualAutoElsFixed, { invoice: autoQrOffset, tracking: trackingQrOffset });
  console.log('[LABEL FIX DEBUG] after reposition:', {
    repositioned: repositioned.map((el: any) => ({ id: el.id, _auto: el._auto, type: el.type, x: el.x, y: el.y })),
  });
  patchedTemplateRef.current = {
    ...translatedTemplateRef.current,
    elements: [...overlapResolvedBilingual, ...repositioned],
  };
  // Both the thermal and non-thermal bilingual PDF paths capture the live
  // preview DOM (previewCanvasRef.current.outerHTML) right after this —
  // that DOM renders off quickInvTranslatedTemplate, which still holds the
  // pre-save state with only a real backend QR merged in if we push it
  // here. Push the merged, real-QR version into state and wait a render
  // tick for BOTH cases, so what gets captured always has the real QR
  // instead of a blank/placeholder box.
  translatedTemplateRef.current = patchedTemplateRef.current;
  setQuickInvTranslatedTemplate(patchedTemplateRef.current);
  // Don't guess a fixed delay — wait for the actual re-render to land,
  // confirmed by checking the DOM contains real invoice QR content
  // (not a stale/placeholder previous render), up to a safety cap.
  const waitForRealRender = async () => {
    for (let i = 0; i < 20; i++) {
      await new Promise(resolve => setTimeout(resolve, 50));
      const node = previewCanvasRef.current;
      if (node && node.querySelector('img[alt="QR code"]')) {
        // Give one more frame for caption text nodes to paint alongside it.
        await new Promise(resolve => setTimeout(resolve, 50));
        return;
      }
    }
  };
  await waitForRealRender();
  console.log('[PDF CAPTURE DEBUG] liveHtml snippet:', previewCanvasRef.current?.innerHTML.includes('Scan to view invoice'));
} else {  // Was independently rebuilding elements from patchedTemplate here
  // (Amount-in-Words push, QR repositioning, discount/tax on the table)
  // — a SEPARATE construction from finalSafeElements, which is what
  // actually got saved as newBill.elements and sent to WhatsApp. Any
  // divergence between the two builds (rounding, a push applied in one
  // but not the other, discount ratio computed slightly differently)
  // meant the auto-downloaded PDF total could permanently disagree with
  // both the saved invoice record and the WhatsApp PDF/caption. Reuse
  // the exact same elements instead of reconstructing them, so there is
  // only one source of truth for what "this invoice" contains.
  let finalPdfElements: any[] = JSON.parse(JSON.stringify(finalSafeElements));

  // Add "Amount in Words" to the actual downloaded PDF
  if (false && showAmountInWords) {
    const tableEl = finalPdfElements.find((el: any) => el.type === 'table');

    if (tableEl) {
      const amountWords = amountToWords(total, activeCurrency);

      // tableEl.height ALREADY includes the totals box — calcTableHeight()
      // bakes totalsBoxHeight into the table's height (see
      // TABLE_HEIGHT_DEBUG/calcTableHeight above). tableBottom is
      // therefore already the real bottom edge of table+totals; adding
      // another totals-sized gap here double-counted that space and
      // pushed Amount-in-Words far enough down to land AFTER Notes
      // instead of directly under the table, unlike the live preview
      // (which correctly uses tableBottom + a small fixed gap only).
      const tableBottom =
  (tableEl.y || 0) +
  (tableEl.height || 0);

const totalsClearance = 12; // small fixed gap, matches live preview

const amountWordsY = tableBottom + totalsClearance;

      const wordsHeight = Math.max(
        30,
        amountInWordsStyle.fontSize * 3
      );

      const amountWordsEl: any = {
        id: 'pdf-amount-in-words',
        type: 'text',
        // Marks this element as positioned by ESTIMATE (calcTableHeight),
        // not real DOM measurement. App.tsx's handleExportPDF re-measures
        // the table for real and will snap this back to the true bottom
        // before grouping/sorting — see the belowElementsRaw map there.
        _measuredAnchor: 'afterTable',

        x: 20,
        y: amountWordsY,

        width: Math.max(
          100,
          PAGE_CONFIGS[template.pageSize || 'A4'].width - 40
        ),

        height: wordsHeight,

        zIndex: 998,
        opacity: 100,
        rotation: 0,

        textContent: `Amount in Words: ${amountWords}`,

        fontFamily: amountInWordsStyle.fontFamily,
        fontSize: amountInWordsStyle.fontSize,
        textAlign: amountInWordsStyle.textAlign,
        fontStyle: amountInWordsStyle.fontStyle,
        fontWeight: amountInWordsStyle.fontWeight,
        color: amountInWordsStyle.color,
      };

      // Push everything below the table downward to make room for
      // Amount in Words — mirrors the live preview's push logic (see
      // the `amountInWordsPushStart` block in the JSX render below).
      // IMPORTANT: the threshold is tableBottom, not amountWordsY.
      // amountWordsY is already computed as tableBottom + clearance —
      // using it as the push threshold meant elements already sitting
      // right at that position (like the template's baked-in QR) never
      // got pushed, so Amount in Words landed AFTER the QR instead of
      // before it. Pushing from tableBottom clears the whole area right
      // after the table, and Amount in Words fills that gap first.
      const amountWordsPushAmount = totalsClearance + wordsHeight;
      // Anchor to the table's TOP (tableEl.y), not tableBottom. tableBottom
      // grows with row count, but QR/Notes/etc. keep whatever y the
      // template originally baked in for a short sample table — at high
      // row counts tableBottom can grow PAST those stale template y
      // values, making `el.y >= tableBottom` false and silently skipping
      // the push for exactly the elements that most need it. Any non-table
      // sibling positioned below the table's top is, by definition, meant
      // to render after it — so it must always be pushed, unconditionally.
      finalPdfElements.forEach((el: any) => {
        if (el.type === 'table') return;
        if ((el.y || 0) >= (tableEl.y || 0)) {
          el.y = (el.y || 0) + amountWordsPushAmount;
        }
      });

      finalPdfElements.push(amountWordsEl);
    }
  }

  patchedTemplateRef.current = {
    ...patchedTemplate,
    elements: finalPdfElements, // already includes _auto QR elements — no repositioning needed
  };
}
console.log('backendAutoEls:', backendAutoEls.map((el: any) => ({ _auto: el._auto, type: el.type, qrValue: el.qrValue })));
console.log('DEBUG bilingual print check:', { hasTranslatedRef: Boolean(translatedTemplateRef.current), elementsWithArabic: translatedTemplateRef.current && translatedTemplateRef.current.elements ? translatedTemplateRef.current.elements.filter((el) => el.arabicContent).length : 0 });

  // Unified PDF pipeline: uses the fast backend renderer (App.tsx -> getInvoicePdfBlob)
  // for all invoices (bilingual and standard), ensuring identical output to Bills view
  const blob = await onExportPDF(newBill, true);
  if (blob) {
    pdfBlobForSend = blob;
    if (autoDownloadPdfOnSave) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${finalInvoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    }
  }
} catch (e) {
  console.warn('Auto-print failed:', e);
}



  // Load user's custom message templates for this branch
  const templates = await loadMessageTemplates(outletId);
  const activeCompany = companyName || authUser?.name || 'Our Company';
  const templateVars = {
    name: clientName,
    invoice: finalInvoiceNumber,
    amount: total,
    currency: sym,
    date: invoiceDate,
    company: activeCompany,
    phone: clientPhone,
    email: clientEmail,
  };

  // Send Email — same endpoint + attach-PDF pattern POS uses
  if (clientEmail.includes('@')) {
    try {
      let emailSubject = applyTemplate(templates.email_subject, templateVars);
      let emailBodyText = applyTemplate(templates.email_body, templateVars);
      if (activeCompany) {
        emailSubject = emailSubject.replace(/CloudAIRambo/gi, activeCompany);
        emailBodyText = emailBodyText.replace(/CloudAIRambo/gi, activeCompany);
      }
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';

      let pdfBase64: string | undefined;
      let pdfFilename: string | undefined;
      if (pdfBlobForSend) {
        try {
          pdfBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlobForSend!);
          });
          pdfFilename = `${finalInvoiceNumber}.pdf`;
        } catch (e) {
          console.warn('PDF base64 conversion failed:', e);
        }
      }

      const emailRes = await fetch(`${BACKEND}/email/send-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
        body: JSON.stringify({
          invoice_id: Number(savedInv.id),
          to_email: clientEmail,
          attach_pdf: true,
          pdf_base64: pdfBase64,
          pdf_filename: pdfFilename,
          subject: emailSubject,
          message: emailBodyText,
        }),
      });
      let emailData: any = {};
      try { emailData = await emailRes.json(); } catch {}
      if (emailRes.ok && emailData.success !== false) {
        postLog('INVOICE_EMAIL_SENT', `Invoice ${finalInvoiceNumber} emailed to ${clientName} | ${clientEmail}`);
        showToast(`✅ Email sent to ${clientEmail}`, '#16a34a');
      } else {
        const reason = emailData.error || emailData.detail || `Email send failed (${emailRes.status})`;
        showToast(`⚠️ Email not sent: ${reason}`);
        console.warn('Email send failed:', reason);
      }
    } catch (e: any) {
      console.warn('Email send failed:', e);
      showToast(`⚠️ Email not sent: ${e?.message || 'Network error — could not reach the server.'}`);
    }
  }

 // Send WhatsApp — delegate to the same working function the manual
// popup button uses (onSendInvoiceWhatsApp, wired up in App.tsx). That
// path never sends outlet_id at all and lets the backend resolve the
// connected outlet from the user's own session — exactly how the
// manual button already succeeds. Duplicating this logic locally with
// an explicit outlet_id was the actual bug: the outletId prop here can
// point at a different outlet than the one WhatsApp is connected to.
if (phoneDigits && onSendInvoiceWhatsApp) {
  try {
    console.log('[WA-AUTO] delegating to onSendInvoiceWhatsApp prop...');
    await onSendInvoiceWhatsApp(newBill, cleanPhone);
    console.log('[WA-AUTO] onSendInvoiceWhatsApp resolved without throwing');
    showToast(`✅ WhatsApp sent to ${cleanPhone}`, '#16a34a');
  } catch (e: any) {
    console.error('[WA-AUTO] onSendInvoiceWhatsApp threw:', e, e?.message);
    showToast(`⚠️ WhatsApp not sent: ${e?.message || 'unknown error'}`);
  }
}

};

if (paymentMode === 'upi' || paymentMode === 'card') {
  // QR already fetched and shown right after savedInv was created above —
  // just wire up what fires once the gateway confirms payment.
  pendingPostPaymentRef.current = async () => {
    const realNumber = confirmedInvoiceNumberRef.current;
    if (realNumber && realNumber !== finalInvoiceNumber) {
      // Swap the PENDING placeholder baked into everything at save-time
      // for the real number the backend just assigned on confirmation.
      newBill.invoiceNumber = realNumber;
      newBill.elements = newBill.elements.map((el: any) => {
        if (el.type === 'barcode' && el.barcodeValue) {
          return { ...el, barcodeValue: el.barcodeValue.split(finalInvoiceNumber).join(realNumber) };
        }
        if (el.type === 'text') {
          return {
            ...el,
            textContent: el.textContent?.split(finalInvoiceNumber).join(realNumber),
            arabicContent: el.arabicContent?.split(finalInvoiceNumber).join(realNumber),
          };
        }
        return el;
      });
      mergedDynamicValues.invoice_number = realNumber;
      if (translatedTemplateRef.current) {
        translatedTemplateRef.current = {
          ...translatedTemplateRef.current,
          elements: translatedTemplateRef.current.elements.map((el: any) =>
            el.type === 'text'
              ? {
                  ...el,
                  textContent: el.textContent?.split(finalInvoiceNumber).join(realNumber),
                  arabicContent: el.arabicContent?.split(finalInvoiceNumber).join(realNumber),
                }
              : el
          ),
        };
      }
      setInvoiceNumberOverride(realNumber);
      setDynamicValues(prev => ({ ...prev, invoice_number: realNumber }));
    }
    await sendPostPaymentDeliverables();
    if (onPostSave) onPostSave(newBill);
  };
} else {
  // Cash is already fully paid at this point — show the popup and lock
  // the button immediately instead of waiting on PDF/email/WhatsApp.
  if (onPostSave) onPostSave(newBill);
  setShowUpgradeNudge(null);
  if (myGeneration === saveGenerationRef.current) setSaved(true); // stale if the form moved into edit mode meanwhile

  // PDF/email/WhatsApp happen in the background — don't block the popup on them.
  sendPostPaymentDeliverables().catch(e => console.warn('Post-payment deliverables failed:', e));
  // No auto-reset timer — the form stays exactly as-is after save so the
  // user can keep adding items / editing. It only clears when they
  // explicitly hit Reset (RotateCcw button) or start a fresh invoice.
  const toast = document.createElement('div');
  toast.innerText = `✅ Invoice ${finalInvoiceNumber} saved for ${clientName}`;
  toast.style.cssText = `
    position:fixed; bottom:28px; left:50%; transform:translateX(-50%);
    background:#16a34a; color:#fff; padding:12px 24px; border-radius:12px;
    font-size:13px; font-weight:700; z-index:9999;
    box-shadow:0 8px 24px rgba(22,163,74,0.4);
    transition: opacity 0.4s ease;
  `;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => {
      if (document.body.contains(toast)) document.body.removeChild(toast);
    }, 400);
  }, 3000);
}
    } catch (err: any) {
      console.error('[QI GENERATE/UPDATE FAILED]', err);
      const msg = err.message || '';
      if (msg.includes('Monthly invoice limit')) {
        setShowUpgradeNudge('You\'ve reached your monthly invoice limit. Upgrade to create more.');
      } else {
        alert(`Failed to generate: ${msg}`);
      }
      setSaving(false);
    }
  };

 // (selectedTemplate and isThermalTemplate moved above preview auto-scale)



const isCustomTemplate = customTemplates.some(t => t.id === selectedTemplateId);
const hasArabicContent = React.useMemo(() => {
  const source = quickInvTranslatedTemplate || selectedTemplate;
  if (!source) return false;
  const result = source.elements.some((el: any) => 
    el.arabicContent || 
    el.columns?.some((c: any) => c.arabicHeader)
  );
  console.log('DEBUG hasArabicContent', {
    usingTranslatedTemplate: !!quickInvTranslatedTemplate,
    result,
    tableCols: source.elements.find((el: any) => el.type === 'table')?.columns,
  });
  return result;
}, [selectedTemplateId, quickInvTranslatedTemplate]);
// Auto-detect if template uses date/due date fields
const templateNeedsDate = React.useMemo(() => {
  if (!selectedTemplate) return true;
  const text = selectedTemplate.elements
    .filter(el => el.type === 'text' && el.textContent)
    .map(el => el.textContent!.toLowerCase()).join(' ');
  return /\bdate\b/.test(text);
}, [selectedTemplateId]);

const templateNeedsDueDate = React.useMemo(() => {
  if (!selectedTemplate) return true;
  const text = selectedTemplate.elements
    .filter(el => el.type === 'text' && el.textContent)
    .map(el => el.textContent!.toLowerCase()).join(' ');
  return /due\s*date/.test(text);
}, [selectedTemplateId]);

  const dynamicFields = React.useMemo(() => {
    if (!selectedTemplate) return [];
    const fields: {key: string; label: string; placeholder: string}[] = [];
    const seen = new Set<string>();

    // Skip fixed accounting words & staff roles, but ALLOW Patient, Guest, Pet, Student as custom fields
    const SKIP = /^(bill to$|client$|customer$|date$|due date|time|invoice|receipt|notes|terms|po$|bill no|from|sender|currency|gstin|dl number|scan|thank|total|subtotal|amount|tax|discount|shipping|payment|bank|account|ifsc|swift|upi|vat|website|http|fssai|www|reg no|license|waiter|cashier|server|operator|staff|doctor|barista)/i;

    const allText = selectedTemplate.elements
      .filter(el => el.type === 'text' && el.textContent && !/[\u0600-\u06FF]/.test(el.textContent))
      .map(el => el.textContent!)
      .join('\n');

    const normalized = allText
      // Only remove the staff token ({staff_name}), keep Patient: [Name], Pet: [Name] so they become template fields!
      .replace(/[A-Za-z\s\/\.\(\)#]{1,25}?:\s*\{staff_name\}/gi, '')
  // Normalize "Label-" or "Label:-" → "Label:" so dash-separated labels get detected
  .replace(/^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*[-:]+\s*$/gm, '$1:')
  .replace(/^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:-\s*/gm, '$1: ')
  .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:]{0,40}?)\s{2,}(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n')
  .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:|]{0,40}?)\s*\|\s*(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n')
  .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:•]{0,40}?)\s*•\s*(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n');
    // Pattern 1: "Label: [Placeholder]" — explicit bracket placeholder
    const bracketPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\[([^\]]{0,40})\]/gm;
    let m;
    while ((m = bracketPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = m[2].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 1 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) {
        seen.add(key);
        fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` });
      }
    }

    // Pattern 2: "Label: __" or "Label: ___" — underscore placeholder
    // {0,25}? (was {1,25}?) — allows single-character labels like "L"
    // (optometry PD/R/L fields). Length check relaxed to match.
    const underscorePattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{0,25}?)\s*:\s*_{2,}/gm;
    while ((m = underscorePattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 1 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) {
        seen.add(key);
        fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` });
      }
    }
// Pattern 2b: "Label: -" or "Label: —" — dash placeholder
    const dashPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*[-—]{1,3}\s*$/gm;
    while ((m = dashPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    // Pattern 2c: "Label: ..." or "Label: …" — ellipsis placeholder
    const ellipsisPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\.{2,}|…\s*$/gm;
    while ((m = ellipsisPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    // Pattern 2d: "Label: ####" or "Label: ???" or "Label: ***" — symbol placeholders
    const symbolPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*[#?*~x]{2,}\s*$/gm;
    while ((m = symbolPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
    }

    // Pattern 2e: "Label: {placeholder}" — curly brace placeholder
    const curlyPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\{([^}]{0,40})\}/gm;
    while ((m = curlyPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = m[2].trim();
      // Skip system tokens so they don't show as dummy fields
      if (/^(staff_name|invoice_number|date|time)$/i.test(rawValue)) continue;
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` }); }
    }

    // Pattern 2f: "Label: <placeholder>" — angle bracket placeholder  
    const anglePattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*<([^>]{0,40})>/gm;
    while ((m = anglePattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = m[2].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` }); }
    }
    // Pattern 3: "Label: " with nothing after (bare label)
    const barePattern = /^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*$/gm;
    while ((m = barePattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) {
        seen.add(key);
        fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` });
      }
    }

    // Pattern 4: "Label: short-value" — baked-in values (codes, names, short strings)
    const bakedPattern = /^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n\[\]_]{1,50}?)$/gm;
    while ((m = bakedPattern.exec(normalized)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = m[2].trim();
      if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
      // Skip long sentences (real content not a field)
      const wordCount = rawValue.split(/\s+/).filter(Boolean).length;
      if (wordCount > 4) continue;
      // Skip address-like values
      if (/\d{5,}|floor|suite|avenue|street|road|nagar|layout|bangalore|mumbai|delhi|london|new york/i.test(rawValue)) continue;
      // Skip values that are clearly company/static info
      if (/^(inc|ltd|llc|pvt|corp|co\.|gstin|fssai|dl:|mob:|www\.|http)/i.test(rawValue)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) {
        seen.add(key);
        fields.push({
          key,
          label: rawLabel,
          placeholder: rawValue,
        });
      }
    }

    // Pattern 5: Known field keywords even if not on own line
    const KNOWN = ['deliver to','deliver','ship to','recipient','from','sender','age','gender','table no','table',
  'reg','ref no','ref','ward','bed','diagnosis','order no','order',
  'seat','batch','expiry','mrp','hsn',
  'serial','token','ticket','room','floor no','section','zone','counter','shift',
  'vehicle','driver','captain','member','loyalty','points','coupon','voucher',
  'booking','reservation','check in','check out','pax','covers','delivery','rider',
  'phone number','phone','mobile','mob no','tel','fax','email','email id',
  'address','adress','addres','addr','contact','whatsapp','website',
];
    for (const keyword of KNOWN) {
      const keywordRegex = new RegExp(
        `\\b(${keyword.replace(/\s/g, '[\\s\\.]*')})\\s*:\\s*([^\\n:]{0,40}?)(?=\\s{2,}[A-Za-z]|\\n|$)`,
        'gi'
      );
      while ((m = keywordRegex.exec(normalized)) !== null) {
        const rawLabel = m[1].trim();
        const rawValue = (m[2] || '').trim();
        if (SKIP.test(rawLabel)) continue;
        const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
        if (!seen.has(key)) {
          seen.add(key);
          const displayLabel = rawLabel.replace(/\b\w/g, c => c.toUpperCase());
          fields.push({
            key,
            label: displayLabel,
            placeholder: rawValue && !/^\[|^_+$/.test(rawValue) ? rawValue : `Enter ${displayLabel}`,
          });
        }
      }
    }

    // Step 6: compromise NLP — catches field labels the regex missed
    try {
      const doc = nlp(normalized);
      const nounPhrases: string[] = doc.nouns().out('array') as string[];
      for (const phrase of nounPhrases) {
        const clean = phrase.trim().toLowerCase();
        if (clean.split(' ').length > 2) continue;
        // Only if it actually appears as "Label:" in the text
        const labelRegex = new RegExp(
          `\\b${clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`,
          'i'
        );
        if (!labelRegex.test(allText)) continue;
        if (SKIP.test(clean)) continue;
        const key = clean.replace(/\s+/g, '_');
        if (!seen.has(key)) {
          seen.add(key);
          const displayLabel = phrase.trim().replace(/\b\w/g, c => c.toUpperCase());
          fields.push({ key, label: displayLabel, placeholder: `Enter ${displayLabel}` });
        }
      }
    } catch { /* compromise failed silently */ }

    return fields;
  }, [selectedTemplateId]);

  const [dynamicValues, setDynamicValues] = useState<Record<string, string>>({});

  // Staff/pharmacist names need transliteration, not translation — keep
  // them fresh whenever the user, target language, or pharmacist field changes.
  // (Must live after the dynamicValues state declaration above — referencing
  // it any earlier in this component hits the temporal dead zone.)
  useEffect(() => {
    const authorName = authUser?.name?.trim() || 'Staff';
    scheduleTransliterate(authorName, 'staffName_translit');
  }, [authUser?.name, quickInvLang]);

  // Pre-fill dynamic values from template's existing content when template changes
  useEffect(() => {
    if (!selectedTemplate) return;
    const prefilled: Record<string, string> = {};
    const allText = selectedTemplate.elements
      .filter(el => el.type === 'text' && el.textContent)
      .map(el => el.textContent!)
      .join('\n');

    const pairPattern = /([A-Za-z][A-Za-z\s\/\.\(\)#]{1,25}?)\s*:\s*([^:\n]{0,40}?)(?=\s{2,}[A-Za-z]|\n|$)/g;
    let match;
    while ((match = pairPattern.exec(allText)) !== null) {
      const rawLabel = match[1].trim();
      const rawValue = (match[2] || '').trim();
      const skip = /^(bill to|client|date|due date|time|invoice|receipt|notes|terms|po|bill no|from|sender|attn|attention|currency|settlement|creation|gstin|dl|scan|thank|hsr|layout|http|www|total|subtotal|amount|vat|tax|discount|shipping|payment|bank|account|ifsc|swift|upi)/i;
      if (skip.test(rawLabel)) continue;
      if (rawLabel.length < 1 || /\d/.test(rawLabel)) continue;
      const wordCount = rawValue.split(/\s+/).filter(Boolean).length;
      if (wordCount > 5) continue;
      if (/floor|suite|ave|street|road|nagar|layout|\d{5,}/i.test(rawValue)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\/\.\(\)#]+/g, '_');
      // Only prefill if there's an actual value (not a placeholder)
      if (rawValue && !/^\[|^__|^\{/.test(rawValue)) {
        prefilled[key] = rawValue;
      }
    }
     const loggedInAuthor = authUser?.name?.trim() || 'Staff';
    prefilled['staff_name'] = loggedInAuthor;
    setDynamicValues(prefilled);
  }, [selectedTemplateId, authUser?.name]);
 const handleSetQIDefaultTemplate = async () => {
  if (!outletId) return alert('Select an outlet first.');
  const template = allTemplates.find(t => t.id === selectedTemplateId);
  if (!template) return;
  setSavingQIDefaultTemplate(true);
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/outlets/${outletId}/default-template`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ template_id: selectedTemplateId }),
    });
    if (!res.ok) throw new Error('Failed to set default');
    setOutletDefaultTemplateId(selectedTemplateId);
    setDefaultTemplateStar(selectedTemplateId);
    if (outletId) {
      localStorage.setItem(`cloudairambo_qi_default_tpl_${outletId}`, selectedTemplateId);
    }
  } catch (e: any) {
    alert(`Failed to set default template: ${e.message}`);
  } finally {
    setSavingQIDefaultTemplate(false);
  }
};

const qiToolbarDivider: React.CSSProperties = {
  width: 1,
  alignSelf: 'stretch',
  background: 'rgba(255,255,255,0.25)',
  margin: '0 2px',
};

const qiToolbarBtn = (active = false, disabled = false): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  height: 36,
  padding: '0 12px',
  borderRadius: 10,
  border: active ? '1px solid rgba(255,255,255,0.55)' : '1px solid rgba(255,255,255,0.18)',
  background: active ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.14)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  whiteSpace: 'nowrap',
  transition: 'background 0.12s ease',
});

const formPanel = (
  <div style={{
    width: isMobile ? '100%' : panelWidth,
    minWidth: isMobile ? 0 : 380,
    maxWidth: isMobile ? 'none' : 750,
    height: isMobile ? '100dvh' : '100%',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    background: '#fff',
    flexShrink: 0,
    borderRight: !isMobile && formSide === 'left' ? '1px solid #e5e7eb' : 'none',
    borderLeft: !isMobile && formSide === 'right' ? '1px solid #e5e7eb' : 'none',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transform: isMobile ? 'none' : `translate(${panelOffset.x}px, ${panelOffset.y}px)`,
    transition: panelDragging.current ? 'none' : 'transform 0.1s ease',
    zIndex: 10,
    paddingBottom: isMobile ? 80 : 0,
  }}>
  {/* Resize handle */}
  <div
    onMouseDown={e => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = panelWidth;
      const onMove = (ev: MouseEvent) => setPanelWidth(Math.max(380, Math.min(750, startW + (formSide === 'left' ? 1 : -1) * (ev.clientX - startX))));
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }}
    style={{
      display: isMobile ? 'none' : 'block',
      position: 'absolute', [formSide === 'left' ? 'right' : 'left']: 0,
      top: 0, bottom: 0, width: 5, cursor: 'ew-resize', zIndex: 10,
      background: 'transparent',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = '#6366f1')}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
  />
      {/* Header */}
      <div
  style={{ padding: '16px 20px', background: 'linear-gradient(135deg,#3b82f6,#9333ea)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Zap size={18} color="#fff" />
            <span style={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>
              {isEditMode ? `Edit — ${editInvoice?.invoiceNumber}` : 'Quick Invoice'}
            </span>
            {(authUser as any)?.outlet_name_display || resolvedOutletName || outletId ? (
              <span style={{
                fontSize: 12, fontWeight: 700, color: '#fff',
                background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)',
                padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap',
              }}>
                🏪 {(authUser as any)?.outlet_name_display || resolvedOutletName || `Outlet #${outletId}`}
              </span>
            ) : (
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#fecaca',
                background: 'rgba(239,68,68,0.25)', border: '1px solid rgba(239,68,68,0.4)',
                padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap',
              }}>
                ⚠ No outlet selected
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none' }}>

            {/* Reset */}
            <button onClick={handleReset} title="Reset form" style={qiToolbarBtn()}>
              <RotateCcw size={16} />
            </button>

            {/* Bilingual Toggle */}
            <button
              onClick={() => {
                if (!perms.canUseBilingual) { alert('Multiple tax lines is a Pro feature. Upgrade your plan to add more.'); return; }
                const next = !bilingualEnabled;
                setBilingualEnabled(next);
                localStorage.setItem('cloudairambo_qi_bilingual_enabled', String(next));
                clearTimeout(autoTranslateQITimer.current);
                if (next) {
                  if (lineItems.some(li => li.description.trim())) handleQuickInvTranslate();
                } else {
                  setQuickInvTranslatedTemplate(null);
                  translatedTemplateRef.current = null;
                  patchedTemplateRef.current = null;
                  setTranslatedValues({});
                  translatedValuesRef.current = {};
                }
              }}
              title={bilingualEnabled ? 'Bilingual invoice: ON' : 'Bilingual invoice: OFF'}
              style={{
                ...qiToolbarBtn(bilingualEnabled),
                minWidth: isMobile ? 42 : 72,
                padding: '0 10px',
                justifyContent: 'center',
              }}
            >
              🌐 {isMobile ? '' : (bilingualEnabled ? 'ON' : 'OFF')}
            </button>

            {/* Language Selection */}
            <select
              value={quickInvLang}
              onChange={e => {
                const nextLang = e.target.value;
                setQuickInvLang(nextLang);
                localStorage.setItem('cloudairambo_qi_lang', nextLang);
                // Clear old language cache so Arabic digits don't contaminate Hindi/other languages
                setTranslatedValues({});
                translatedValuesRef.current = {};
                setQuickInvTranslatedTemplate(null);
                translatedTemplateRef.current = null;
              }}
              style={{
                fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.14)', color: '#fff', height: 36,
                padding: '0 8px', borderRadius: 10, cursor: 'pointer', outline: 'none', minWidth: 62,
              }}
            >
              {[
                ['af','Afrikaans'],['sq','Albanian'],['am','Amharic'],['ar','Arabic'],['hy','Armenian'],
                ['az','Azerbaijani'],['eu','Basque'],['be','Belarusian'],['bn','Bengali'],['bs','Bosnian'],
                ['bg','Bulgarian'],['ca','Catalan'],['ceb','Cebuano'],['ny','Chichewa'],['zh','Chinese (Simplified)'],
                ['zh-TW','Chinese (Traditional)'],['co','Corsican'],['hr','Croatian'],['cs','Czech'],['da','Danish'],
                ['nl','Dutch'],['en','English'],['eo','Esperanto'],['et','Estonian'],['tl','Filipino'],
                ['fi','Finnish'],['fr','French'],['fy','Frisian'],['gl','Galician'],['ka','Georgian'],
                ['de','German'],['el','Greek'],['gu','Gujarati'],['ht','Haitian Creole'],['ha','Hausa'],
                ['haw','Hawaiian'],['iw','Hebrew'],['hi','Hindi'],['hmn','Hmong'],['hu','Hungarian'],
                ['is','Icelandic'],['ig','Igbo'],['id','Indonesian'],['ga','Irish'],['it','Italian'],
                ['ja','Japanese'],['jw','Javanese'],['kn','Kannada'],['kk','Kazakh'],['km','Khmer'],
                ['rw','Kinyarwanda'],['ko','Korean'],['ku','Kurdish (Kurmanji)'],['ky','Kyrgyz'],['lo','Lao'],
                ['la','Latin'],['lv','Latvian'],['lt','Lithuanian'],['lb','Luxembourgish'],['mk','Macedonian'],
                ['mg','Malagasy'],['ms','Malay'],['ml','Malayalam'],['mt','Maltese'],['mi','Maori'],
                ['mr','Marathi'],['mn','Mongolian'],['my','Myanmar (Burmese)'],['ne','Nepali'],['no','Norwegian'],
                ['or','Odia (Oriya)'],['ps','Pashto'],['fa','Persian'],['pl','Polish'],['pt','Portuguese'],
                ['pa','Punjabi'],['ro','Romanian'],['ru','Russian'],['sm','Samoan'],['gd','Scots Gaelic'],
                ['sr','Serbian'],['st','Sesotho'],['sn','Shona'],['sd','Sindhi'],['si','Sinhala'],
                ['sk','Slovak'],['sl','Slovenian'],['so','Somali'],['es','Spanish'],['su','Sundanese'],
                ['sw','Swahili'],['sv','Swedish'],['tg','Tajik'],['ta','Tamil'],['tt','Tatar'],
                ['te','Telugu'],['th','Thai'],['tr','Turkish'],['tk','Turkmen'],['ug','Uyghur'],
                ['uk','Ukrainian'],['ur','Urdu'],['uz','Uzbek'],['vi','Vietnamese'],['cy','Welsh'],
                ['xh','Xhosa'],['yi','Yiddish'],['yo','Yoruba'],['zu','Zulu'],['ak','Akan'],
                ['bho','Bhojpuri'],['doi','Dogri'],['dv','Dhivehi'],['ee','Ewe'],['gn','Guarani'],
                ['ilo','Ilocano'],['kri','Krio'],['ckb','Kurdish (Sorani)'],['lg','Luganda'],['mai','Maithili'],
                ['lus','Mizo'],['om','Oromo'],['qu','Quechua'],['sa','Sanskrit'],['nso','Sepedi'],
                ['ti','Tigrinya'],['ts','Tsonga'],
              ].map(([code, name]) => (
                <option key={code} value={code} style={{color:'#111'}}>{code.toUpperCase()} — {name}</option>
              ))}
            </select>

            {/* Re-translate button if bilingual */}
            {bilingualEnabled && (
              <button
                onClick={() => {
                  clearTimeout(autoTranslateQITimer.current);
                  handleQuickInvTranslate();
                }}
                disabled={isQuickInvTranslating}
                title="Translate invoice"
                style={{ ...qiToolbarBtn(), opacity: isQuickInvTranslating ? 0.6 : 1, padding: '0 6px' }}
              >
                {isQuickInvTranslating ? '...' : '⚡'}
              </button>
            )}

            {/* Revert if translated */}
            {quickInvTranslatedTemplate && (
              <button
                onClick={() => {
                  setQuickInvTranslatedTemplate(null);
                  translatedTemplateRef.current = null;
                  patchedTemplateRef.current = null;
                }}
                title="Revert to original"
                style={{ ...qiToolbarBtn(), background: 'rgba(248,113,113,0.28)', borderColor: 'rgba(248,113,113,0.4)', padding: '0 6px' }}
              >
                ↩
              </button>
            )}

            {/* Desktop Only Tools: Display & Stack toggle */}
            {!isMobile && (
              <>
                <button
                  onClick={() => window.open(outletDisplayToken ? `/display?token=${outletDisplayToken}&register_id=${registerId}` : '#', '_blank')}
                  disabled={!outletDisplayToken}
                  title={outletDisplayToken ? 'Open Counter Display' : 'No display token'}
                  style={{ ...qiToolbarBtn(false, !outletDisplayToken), width: 36, height: 36, padding: 0 }}
                >
                  <Monitor size={18} color="#fff" />
                </button>
                {quickInvTranslatedTemplate && (
                  <button
                    onClick={() => {
                      const next = bilingualLayout === 'side' ? 'stacked' : 'side';
                      setBilingualLayout(next);
                      bilingualLayoutRef.current = next;
                    }}
                    title="Toggle layout"
                    style={{ ...qiToolbarBtn(), height: 36, padding: '0 10px' }}
                  >
                    {bilingualLayout === 'side' ? '⇔' : '⇕'}
                  </button>
                )}
                <div style={{ ...qiToolbarDivider, height: 24, alignSelf: 'center', margin: '0 4px' }} />
              </>
            )}

            {/* Print */}
            <button
              onClick={handlePrintPreview}
              disabled={isPrinting}
              title="Print"
              style={{
                ...qiToolbarBtn(),
                width: 36,
                height: 36,
                padding: 0,
                opacity: isPrinting ? 0.6 : 1,
                cursor: isPrinting ? 'not-allowed' : 'pointer',
              }}
            >
              <Printer size={18} color="#fff" />
            </button>

            {/* Print Settings */}
            <button
              onClick={() => setShowPrintSettings(s => !s)}
              title="Settings"
              style={{
                ...qiToolbarBtn(showPrintSettings),
                width: 36,
                height: 36,
                padding: 0,
              }}
            >
              <Settings size={18} color="#fff" />
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>

        {editingPendingInvoice && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>
              ✏️ Editing pending invoice {editingPendingInvoice.invoiceNumber} — click "Update Pending Invoice" when done
            </span>
            <button onClick={() => { setPaymentWaitModal(editingPendingInvoice); setEditingPendingInvoice(null); }}
              style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              Cancel, resume QR
            </button>
          </div>
        )}

        {/* Invoice Usage Widget */}
        {(() => {
  const plan = (authUser as any)?.plan || 'basic';
  const tier = (authUser as any)?.plan_tier || 'individual';
  const posActive = moduleStatuses?.pos === 'active' || moduleStatuses?.pos === 'trial';
  const isUnlimited = plan === 'ultra' || (plan === 'pro' && tier !== 'individual') || posActive;

  // Hooks must run on EVERY render regardless of isUnlimited — bailing
  // out with `return null` before useState/useEffect ran was skipping
  // these hooks on some renders (e.g. once moduleStatuses finishes
  // loading and isUnlimited flips to true), giving React a different
  // hook count than the previous render and crashing the whole tree
  // with "Rendered fewer hooks than expected" (minified error #300).
  const [usage, setUsage] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (isUnlimited) return;
    const jwt = localStorage.getItem('cloudairambo_jwt');
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    fetch(`${BASE}/invoices/monthly-usage`, {
      headers: { Authorization: `Bearer ${jwt}` },
    }).then(r => r.json()).then(d => setUsage(d.count || 0)).catch(() => {});
  }, [isUnlimited]);

  if (isUnlimited) return null;

  const limit = plan === 'pro' ? 30 : tier === 'smb' ? 30 : tier === 'enterprise' ? 50 : 5;

  if (usage === null) return null;
          const pct = Math.min(100, Math.round((usage / limit) * 100));
          const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 10px' }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {usage}/{limit} invoices this month
              </div>
              <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.3s' }} />
              </div>
            </div>
          );
        })()}

        {/* Template */}
        <div>
          <label style={lbl}>Template</label>
          {!canSetQIDefaultTemplate ? (
            <div style={{ ...inp, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f3f4f6', color: '#374151', cursor: 'not-allowed' }}>
              <span>🔒 {allTemplates.find(t => t.id === selectedTemplateId)?.name || 'No default set'}</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} style={{ ...inp, flex: 1 }}>
                  <optgroup label="Ready-Made Presets">
                    {TEMPLATE_PRESETS.map(t => <option key={t.id || t.name} value={t.id || t.name}>{t.name}</option>)}
                  </optgroup>
                  {customTemplates.length > 0 && (
                    <optgroup label="My Templates">
                      {customTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </optgroup>
                  )}
                </select>
                {isCustomTemplate && (
                  <button onClick={() => { if (!confirm('Remove this template?')) return; const tmplName = allTemplates.find(t => t.id === selectedTemplateId)?.name || selectedTemplateId; onRemoveTemplate?.(selectedTemplateId); postLog('TEMPLATE_REMOVED', `Custom template removed: ${tmplName}`); setSelectedTemplateId(allTemplates[0]?.id || ''); }}
                    style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              <button
                onClick={handleSetQIDefaultTemplate}
                disabled={savingQIDefaultTemplate || String(outletDefaultTemplateId) === String(selectedTemplateId)}
                style={{
                  marginTop: 5, width: '100%', fontSize: 10, fontWeight: 700, padding: '5px 8px', borderRadius: 6,
                  border: '1px solid #c7d2fe', cursor: savingQIDefaultTemplate ? 'not-allowed' : 'pointer',
                  background: String(outletDefaultTemplateId) === String(selectedTemplateId) ? '#f0fdf4' : '#eef2ff',
                  color: String(outletDefaultTemplateId) === String(selectedTemplateId) ? '#16a34a' : '#4f46e5',
                }}
              >
                {String(outletDefaultTemplateId) === String(selectedTemplateId)
                  ? '✓ This is the org default'
                  : savingQIDefaultTemplate ? 'Setting...' : 'Set as Default for team'}
              </button>
            </>
          )}
        </div>

       {/* Invoice Number Override */}
<div>
  <label style={lbl}>Invoice / Receipt No</label>
  {isEditMode ? (
    <div>
      <input style={{ ...inp, fontFamily: 'monospace', fontWeight: 700, color: '#3b82f6', letterSpacing: 1, background: '#f8fafc', cursor: 'not-allowed' }}
        value={editInvoice?.invoiceNumber || ''} readOnly />
      <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 3 }}>Locked — assigned on creation</div>
    </div>
  ) : (authUser?.role === 'owner' || authUser?.role === 'admin') ? (
    <div style={{ position: 'relative' }}>
      <input style={{ ...inp, fontFamily: 'monospace', fontWeight: 700, color: '#3b82f6', letterSpacing: 1 }}
        placeholder="Auto-generated (e.g. CLO-00001)"
        value={invoiceNumberOverride}
        onChange={e => setInvoiceNumberOverride(e.target.value)} />
      {invoiceNumberOverride && (
        <button onClick={() => setInvoiceNumberOverride('')}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#9ca3af' }}>×</button>
      )}
      <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 3 }}>Leave blank to auto-assign from backend</div>
    </div>
  ) : (
    <div style={{ ...inp, background: '#f8fafc', color: '#9ca3af', fontSize: 11 }}>
      Auto-assigned on save
    </div>
  )}
</div>

{/* Status */}
<div>
  <label style={lbl}>Status</label>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {(['draft','unpaid','paid','overdue','cancelled'] as const).map(s => {
              const locked = isEditMode ? !perms.canEditInvoice : !perms.canAccessGenerator;
              return (
                <button key={s} onClick={() => !locked && setInvoiceStatus(s)} style={{
                  padding: '4px 10px', borderRadius: 5, border: '1px solid',
                  fontSize: 11, fontWeight: 600,
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked && invoiceStatus !== s ? 0.4 : 1,
                  background: invoiceStatus === s ? (
                    s === 'paid' ? '#10b981' : s === 'overdue' ? '#ef4444' : s === 'cancelled' ? '#6b7280' : s === 'unpaid' ? '#f59e0b' : '#3b82f6'
                  ) : '#fff',
                  color: invoiceStatus === s ? '#fff' : '#6b7280',
                  borderColor: invoiceStatus === s ? 'transparent' : '#e5e7eb',
                  textTransform: 'uppercase',
                }}>{s}</button>
              );
            })}
          </div>
        </div>

        {/* 👤 Staff / Cashier — Locked & Read-only */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Staff / Cashier</label>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '1px 6px', borderRadius: 4 }}>
              🔒 Logged In
            </span>
          </div>
          <div style={{
            ...inp,
            fontSize: 12,
            fontWeight: 700,
            color: '#1e293b',
            background: '#f8fafc',
            border: '1.5px solid #cbd5e1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'not-allowed',
            userSelect: 'none',
          }}>
            <span>👤 {authUser?.name?.trim() || 'Staff'}</span>
            <span style={{ fontSize: 10, color: '#64748b' }}>🔒 Locked</span>
          </div>
        </div>

        {/* Client */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Customer Info</label>
            <button
              type="button"
              onClick={() => {
                setClientName('Walk-in Customer');
                setClientSearch('Walk-in Customer');
                setIncludeFields({ name: true, phone: false, email: false, address: false, taxId: false });
              }}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#2563eb',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 5,
                padding: '2px 8px',
                cursor: 'pointer',
              }}
            >
              👤 Walk-in
            </button>
          </div>

          {/* Individual Checkboxes for each field */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
            {[
              { key: 'name' as const, label: 'Name' },
              { key: 'phone' as const, label: 'Phone' },
              { key: 'email' as const, label: 'Email' },
              { key: 'address' as const, label: 'Address' },
              { key: 'taxId' as const, label: 'Tax ID' },
            ].map(({ key, label }) => (
              <label key={key} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 10, fontWeight: 700, cursor: 'pointer',
                padding: '3px 7px', borderRadius: 6, userSelect: 'none',
                background: includeFields[key] ? '#eff6ff' : '#f8fafc',
                color: includeFields[key] ? '#2563eb' : '#94a3b8',
                border: `1px solid ${includeFields[key] ? '#bfdbfe' : '#e2e8f0'}`,
                transition: 'all 0.15s ease',
              }}>
                <input
                  type="checkbox"
                  checked={includeFields[key]}
                  onChange={e => setIncludeFields(prev => ({ ...prev, [key]: e.target.checked }))}
                  style={{ accentColor: '#2563eb', cursor: 'pointer', margin: 0, width: 12, height: 12 }}
                />
                {label}
              </label>
            ))}
          </div>

          <div style={{ position: 'relative', opacity: includeFields.name ? 1 : 0.4 }}>
            <User size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input style={{ ...inp, paddingLeft: 28 }} placeholder="Search or type client name..."
              value={clientSearch}
              onChange={e => { 
                const val = e.target.value;
                setClientSearch(val); 
                setClientName(val); 
                if (val.trim()) setIncludeFields(prev => ({ ...prev, name: true }));
                setShowClientDropdown(true);
                scheduleTransliterate(val, 'clientName');
              }}
              onFocus={() => setShowClientDropdown(true)}
              onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)} />
            {showClientDropdown && filteredAccounts.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, zIndex: 50, maxHeight: 150, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {filteredAccounts.map(acc => (
                  <div key={acc.id}
                    onPointerDown={e => { e.preventDefault(); selectAccount(acc); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f9fafb' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{acc.name}</div>
                    <div style={{ color: '#9ca3af', fontSize: 11 }}>{acc.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6, marginTop: 6 }}>
            <input style={inp} placeholder="Email" value={clientEmail} onChange={e => {
              const val = e.target.value;
              setClientEmail(val);
              if (val.trim()) setIncludeFields(prev => ({ ...prev, email: true }));
            }} />
           <PhoneInput value={clientPhone} onChange={val => {
             setClientPhone(val);
             if (val && val.trim()) setIncludeFields(prev => ({ ...prev, phone: true }));
           }} />
          </div>
          <input style={{ ...inp, marginTop: 6 }} placeholder="Address" value={clientAddress} onChange={e => { 
            const val = e.target.value;
            setClientAddress(val); 
            if (val.trim()) setIncludeFields(prev => ({ ...prev, address: true }));
            scheduleTransliterate(val, 'clientAddress'); 
          }} />
          {/* Universal Tax ID with Country & State Auto-Detection (matches App) */}
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
                Buyer {taxIdMeta.taxLabel}
              </span>
              <button
                type="button"
                onClick={() => setShowTaxDisclaimerModal(true)}
                style={{
                  background: 'none', border: 'none', color: '#3b82f6', fontSize: 10,
                  fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: 0
                }}
              >
                ℹ️ Legal Disclaimer
              </button>
            </div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                style={{
                  ...inp,
                  paddingRight: 80,
                  fontFamily: 'monospace',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
                placeholder={`Buyer's ${taxIdMeta.taxLabel} (${REGIONAL_TAX_COUNTRIES.has(taxIdMeta.countryCode) ? 'auto-fills state/region' : 'optional'})`}
                maxLength={25}
                value={clientGSTIN}
                onChange={e => {
                  const clean = e.target.value.replace(/[\s\-\.]/g, '').toUpperCase();
                  setClientGSTIN(clean);
                  setForceB2BOverride(false);
                }}
              />
              {taxIdMeta.currentLength > 0 && (
                <div 
                  onClick={() => setShowTaxDisclaimerModal(true)}
                  style={{ position: 'absolute', right: 10, cursor: 'pointer' }}
                  title="Click to view verification disclaimer"
                >
                  {taxIdMeta.isComplete || taxIdMeta.currentLength >= taxIdMeta.expectedLength ? (
                    <span style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: taxIdMeta.isValid ? '#16a34a' : '#ef4444',
                      display: 'flex', alignItems: 'center', gap: 2
                    }}>
                      {taxIdMeta.isValid ? '✓ Valid' : '✗ Invalid'} <span style={{ fontSize: 9, opacity: 0.7 }}>ℹ️</span>
                    </span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>
                      {taxIdMeta.currentLength}/{taxIdMeta.expectedLength}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={{ fontSize: 9, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 4, marginTop: -1, lineHeight: 1.4 }}>
              <span>⚠️ <strong>Do not trust this for legal verification:</strong> This only detects format and character count, and filtering can be wrong. Always verify active business status on official government websites.</span>
            </div>

            {/* Manual B2B override — only offered when there's a real Tax ID
                typed in that our validator is rejecting. Never shown for an
                empty field, and never silently applied — cashier must
                explicitly tick it, and it resets on every edit. */}
            {clientGSTIN.trim().length > 0 &&
              (taxIdMeta.isComplete || taxIdMeta.currentLength >= taxIdMeta.expectedLength) &&
              !taxIdMeta.isValid && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7,
                padding: '7px 10px',
              }}>
                <input
                  type="checkbox"
                  id="forceB2BOverride"
                  checked={forceB2BOverride}
                  onChange={e => setForceB2BOverride(e.target.checked)}
                  style={{ cursor: 'pointer', accentColor: '#f59e0b', flexShrink: 0 }}
                />
                <label htmlFor="forceB2BOverride" style={{ fontSize: 10, fontWeight: 600, color: '#92400e', cursor: 'pointer', lineHeight: 1.4 }}>
                  This Tax ID doesn't validate, but I know it's correct — bill this as B2B anyway (at my own risk).
                </label>
              </div>
            )}

            {/* Detected Country & State Banner */}
            {taxIdMeta.currentLength >= 2 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: (taxIdMeta.isComplete && !taxIdMeta.isValid) ? '#fef2f2' : taxIdMeta.isValid ? '#f0fdf4' : '#eff6ff',
                border: `1px solid ${(taxIdMeta.isComplete && !taxIdMeta.isValid) ? '#fecaca' : taxIdMeta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
                borderRadius: 7,
                padding: '5px 9px',
                gap: 6,
                fontSize: 11,
              }}>
                <span style={{ fontSize: 13 }}>{taxIdMeta.countryFlag}</span>
                <span style={{ fontWeight: 700, color: '#1e293b', flex: 1 }}>
                  {taxIdMeta.countryName}
                  {buyerRegion ? ` · ${buyerRegion}` : ''}
                  <span style={{ color: '#64748b', fontWeight: 500 }}> ({taxIdMeta.taxLabel})</span>
                </span>
                {buyerRegion && (!taxIdMeta.isComplete || taxIdMeta.isValid) && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#16a34a' }}>
                    ✓ State Auto-Set
                  </span>
                )}
              </div>
            )}

            {/* State / Province Picker: Display for regional countries or if region detected */}
            {(REGIONAL_TAX_COUNTRIES.has(taxIdMeta.countryCode) ||
              Boolean(taxIdMeta.detectedRegion) ||
              (!clientGSTIN.trim() && qiTaxScheme?.region_based) ||
              (taxIdMeta.countryCode === qiOutletCountry && qiTaxScheme?.region_based)) && (
              <StatePicker
                countryCode={taxIdMeta.countryCode || qiOutletCountry}
                value={buyerRegion}
                onChange={(v: string) => {
                  setBuyerRegion(v);
                  setGstinAutoDetected(false);
                }}
                placeholder="Buyer's State / Province"
              />
            )}
          </div>
        </div>

        {/* Dynamic Fields from Template */}
        {dynamicFields.length > 0 && (
          <div>
            <label style={lbl}>Template Fields</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dynamicFields.map(f => {
                const isStaff = isStaffField(f.key);
                const defaultStaffVal = isStaff ? (authUser?.name?.trim() || 'Staff') : '';
                const currentValue = isStaff ? (authUser?.name?.trim() || 'Staff') : (dynamicValues[f.key] !== undefined ? dynamicValues[f.key] : defaultStaffVal);

                // Staff is already displayed in the main widget above — skip here
                if (isStaff) return null;

                return (
                  <div key={f.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <label style={{ ...lbl, fontSize: 9, marginBottom: 0 }}>{f.label}</label>
                    </div>
                    <input
                      style={inp}
                      placeholder={f.placeholder}
                      value={currentValue}
                      onChange={e => {
                        const val = e.target.value;
                        setDynamicValues(prev => ({ ...prev, [f.key]: val }));
                        if (bilingualEnabled) {
                          if (!isLiteralFieldValue(val)) {
                            scheduleTransliterate(val, f.key);
                          } else if (quickInvLang === 'ar' && /^\d+$/.test(val.trim())) {
                            setTranslatedValues(prev => ({ ...prev, [f.key]: toArabicIndicDigits(val.trim()) }));
                          } else if (val.trim()) {
                            setTranslatedValues(prev => ({ ...prev, [f.key]: val.trim() }));
                          }
                          clearTimeout(autoTranslateQITimer.current);
                          autoTranslateQITimer.current = setTimeout(() => {
                            handleQuickInvTranslateRef.current();
                          }, 800);
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Timezone picker */}
<div>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
    <label style={lbl}>Timezone</label>
  </div>
  <div style={{ position: 'relative' }}>
    <button onClick={() => setShowTzPicker(s => !s)} style={{ ...inp, textAlign: 'left', cursor: 'pointer', fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>🌐 {userTimezone}</span>
      <span style={{ color: '#9ca3af' }}>▾</span>
    </button>
    {showTzPicker && (
      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <input
          autoFocus
          style={{ ...inp, borderRadius: 0, borderBottom: '1px solid #e5e7eb', fontSize: 11 }}
          placeholder="Search timezone..."
          value={tzSearch}
          onChange={e => setTzSearch(e.target.value)}
        />
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {tzList.filter(tz => tz.toLowerCase().includes(tzSearch.toLowerCase())).slice(0, 80).map(tz => (
            <div key={tz} onClick={() => { setUserTimezone(tz); setShowTzPicker(false); setTzSearch(''); }}
              style={{ padding: '7px 12px', fontSize: 11, cursor: 'pointer', background: tz === userTimezone ? '#eff6ff' : '#fff', color: tz === userTimezone ? '#3b82f6' : '#374151', fontWeight: tz === userTimezone ? 700 : 400 }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
              onMouseLeave={e => (e.currentTarget.style.background = tz === userTimezone ? '#eff6ff' : '#fff')}>
              {tz}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
</div>

{/* Date & Time — auto by default, manual override optional */}
{templateNeedsDate && (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
      <label style={lbl}>Date & Time</label>
      <div style={{ display: 'flex', gap: 5 }}>
        <button onClick={() => {
          const now = new Date();
          setInvoiceDate(now.toLocaleDateString('en-CA', { timeZone: userTimezone }));
          setInvoiceTime(now.toLocaleTimeString('en-US', { timeZone: userTimezone, hour: '2-digit', minute: '2-digit', hour12: true }));
        }} style={{ fontSize: 9, fontWeight: 700, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 7px', cursor: 'pointer' }}>
          ↺ Now
        </button>
        <button onClick={() => setShowManualDateTime(s => !s)} style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 7px', cursor: 'pointer' }}>
          {showManualDateTime ? 'Auto' : '✎ Manual'}
        </button>
      </div>
    </div>

    {/* Auto mode — just shows current value, read-only */}
    {!showManualDateTime ? (
      <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', fontSize: 11, color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>📅 {invoiceDate}</span>
        <span>🕐 {invoiceTime}</span>
      </div>
    ) : (
      /* Manual mode — editable inputs */
      <div style={{ display: 'flex', gap: 6 }}>
        <input type="date" style={{ ...inp, flex: 1 }} value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
        <input style={{ ...inp, width: 110, fontFamily: 'monospace', flexShrink: 0 }} value={invoiceTime} onChange={e => setInvoiceTime(e.target.value)} placeholder="04:32 am" />
      </div>
    )}

    {/* Due Date — optional, user can toggle */}
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" id="showDueDate" checked={showDueDate} onChange={e => setShowDueDate(e.target.checked)}
          style={{ cursor: 'pointer', accentColor: '#3b82f6' }} />
        <label htmlFor="showDueDate" style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' }}>
          Add Due Date
        </label>
      </div>
      {showDueDate && (
        <input type="date" style={{ ...inp, marginTop: 6 }} value={dueDate} onChange={e => setDueDate(e.target.value)} />
      )}
    </div>
  </div>
)}

        {/* Line Items */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Line Items</label>
            <div style={{ display: 'flex', gap: 5 }}>
             <button onClick={addLineItem} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}>
  <Plus size={10} /> Add Row
</button>
<button onClick={() => {
  if (!perms.canUseCustomColumns) {
    setShowUpgradeNudge('Custom columns is a Pro feature.');
    return;
  }
  setShowAddCol(s => !s);
}} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: perms.canUseCustomColumns ? '#7c3aed' : '#9ca3af', background: perms.canUseCustomColumns ? '#f5f3ff' : '#f9fafb', border: `1px solid ${perms.canUseCustomColumns ? '#ddd6fe' : '#e5e7eb'}`, borderRadius: 5, padding: '3px 8px', cursor: perms.canUseCustomColumns ? 'pointer' : 'not-allowed' }}>
  {perms.canUseCustomColumns ? <Plus size={10} /> : '🔒'} Add Col
</button>
{perms.canAddProduct && (
  <button onClick={() => setShowScanner(true)} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#fff', background: '#16a34a', border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}>
    📦 Scan
  </button>
)}
              
            </div>
          </div>
          {showAddCol && (() => {
            const usedKeys = new Set(customColumns.map(c => c.key));
            const restoreBaseOptions = toggleableBase.filter(b => hiddenColumns.includes(b.key));
            const availableCatalogKeys = catalogColumnOptions
              .filter(o => !usedKeys.has(o.key))
              .filter(o => !(o.key.startsWith('cat_tax_b') && (usedKeys.has('cat_tax_b2c') || usedKeys.has('cat_tax_b2b'))))
              .map(o => o.label);

            const addColumn = (label: string) => {
              if (!label.trim()) return;
              const match = catalogColumnOptions.find(o => o.label.toLowerCase() === label.trim().toLowerCase());
              const key = match ? match.key : label.trim().toLowerCase().replace(/\s+/g, '_');
              const newCol = { id: `col-${Date.now()}`, header: match ? match.header : label.trim(), key, align: 'left' as const };
              setCustomColumns(prev => [...prev, newCol]);
              setLineItems(prev => prev.map(li => {
                const p = catalogProducts.find(cp => cp.name.toLowerCase().trim() === li.description.toLowerCase().trim());
                if (!p) return li;
                const val = getCatalogColValue(p, newCol);
                return val ? { ...li, [newCol.key]: val } : li;
              }));
              scheduleTranslate(label.trim(), `col_header_${newCol.id}`);
              setNewColName('');
              setShowAddCol(false);
            };

            return (
              <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 7, padding: '8px 10px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {restoreBaseOptions.length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: 4 }}>
                      Restore Removed Standard Columns (click to show)
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {restoreBaseOptions.map(b => (
                        <button
                          key={b.key}
                          type="button"
                          onClick={() => setHiddenColumns(prev => prev.filter(k => k !== b.key))}
                          style={{
                            background: '#ecfdf5',
                            border: '1px solid #a7f3d0',
                            borderRadius: 5,
                            padding: '3px 8px',
                            color: '#059669',
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                        >
                          + {b.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {availableCatalogKeys.length > 0 && (
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', marginBottom: 3 }}>
                      Existing catalog columns — picking one auto-fills from products
                    </div>
                    <select
                      style={{ ...inp, fontSize: 11 }}
                      value=""
                      onChange={e => { if (e.target.value) addColumn(e.target.value); }}
                    >
                      <option value="">— Select a column —</option>
                      {availableCatalogKeys.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    style={{ ...inp, fontSize: 11, flex: 1 }}
                    placeholder={availableCatalogKeys.length > 0 ? 'Or type a new column name...' : 'Column name (e.g. Batch No)'}
                    value={newColName}
                    onChange={e => setNewColName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addColumn(newColName); }}
                  />
                  <button onClick={() => addColumn(newColName)} style={{ background: '#7c3aed', border: 'none', borderRadius: 5, padding: '5px 10px', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Add</button>
                  <button onClick={() => setShowAddCol(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              </div>
            );
          })()}

          {/* Active Base & Custom Column Chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {toggleableBase.filter(b => !hiddenColumns.includes(b.key)).map(b => (
              <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 7px', fontSize: 11, color: '#475569' }}>
                <span>{b.label}</span>
                <button
                  type="button"
                  title={`Hide ${b.label} column`}
                  onClick={() => setHiddenColumns(prev => [...prev, b.key])}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              </div>
            ))}

            {customColumns.map(col => (
              <div key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#ede9fe', border: '1px solid #ddd6fe', borderRadius: 4, padding: '2px 7px', fontSize: 11, color: '#5b21b6' }}>
                <span>{col.header}</span>
                <select
                  value={(col as any).after || ''}
                  onChange={e => setCustomColumns(prev => prev.map(c => c.id === col.id ? { ...c, after: e.target.value || undefined } : c))}
                  title="Where this column goes in the table"
                  style={{ fontSize: 10, border: '1px solid #ddd6fe', borderRadius: 4, background: '#fff', color: '#5b21b6', padding: '1px 2px', cursor: 'pointer', outline: 'none' }}
                >
                  <option value="">at end</option>
                  {templateTableCols.filter((tc: any) => tc.key === mainColKey || !hiddenColumns.includes(tc.key)).map((tc: any) => (
                    <option key={tc.key} value={tc.key}>after {tc.originalHeader || tc.header || tc.key}</option>
                  ))}
                </select>
                <button onClick={() => setCustomColumns(prev => prev.filter(c => c.id !== col.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 88px 26px', gap: 6, marginBottom: 5 }}>
            {['Description', 'Qty', 'Rate', ''].map(h => <div key={h} style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>{h}</div>)}
          </div>
          {lineItems.map(li => (
            <div key={li.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 88px 26px', gap: 6, alignItems: 'center' }}>
                <div className="qi-product-search-container" style={{ position: 'relative', flex: 1 }}>
  <input
    style={{ ...inp, fontSize: 13, padding: '7px 10px' }}
    placeholder="Item or search catalog..."
    value={li.description}
    onChange={e => {
      const val = e.target.value;
      if (!val.trim()) {
        setLineItems(prev => prev.map(item =>
          item.id === li.id
            ? { ...item, description: '', qty: 1, rate: 0, taxRate: 0, taxInclusive: false }
            : item
        ));
      } else {
        updateLineItem(li.id, 'description', val);
      }
      if (bilingualEnabled) {
        scheduleTranslate(val, `item_${li.id}`);
      }
      setProductDropdownId(li.id);
    }}
    onFocus={() => setProductDropdownId(li.id)}
    onClick={() => setProductDropdownId(li.id)}
  />
  {productDropdownId === li.id && (() => {
    const q = li.description.toLowerCase();
    const matches = catalogProducts.filter(p =>
      p.name.toLowerCase().includes(q) || p.barcode.includes(q)
    );
    if (matches.length === 0) return null;
    return (
      <div 
        className="qi-dropdown-scroll"
        style={{
          position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 8, zIndex: 200,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}
      >
        {matches.map(p => (
          <div key={p.id}
            onPointerDown={e => {
              e.preventDefault();
              updateLineItem(li.id, 'description', p.name);
              updateLineItem(li.id, 'rate', p.rate);
              updateLineItem(li.id, 'taxRate' as any, (p as any).tax_rate || 0);
              updateLineItem(li.id, 'taxName' as any, (p as any).tax_name);
              updateLineItem(li.id, 'b2bTaxRate' as any, (p as any).b2b_tax_rate);
              updateLineItem(li.id, 'b2bTaxName' as any, (p as any).b2b_tax_name);
              updateLineItem(li.id, 'taxInclusive' as any, !!(p as any).tax_inclusive);
              customColumns.forEach(col => {
  const val = getCatalogColValue(p, col);
  if (val !== undefined) {
    updateLineItem(li.id, col.key as any, val);
    updateLineItem(li.id, col.header as any, val);
  }
});
              scheduleTranslate(p.name, `item_${li.id}`);
              setProductDropdownId(null);
            }}
            style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid #f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
            onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{p.name}</div>
              {p.unit && <div style={{ fontSize: 10, color: '#9ca3af' }}>{p.unit}</div>}
            </div>
            <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: 12 }}>{sym}{p.rate.toFixed(2)}</div>
          </div>
        ))}
      </div>
    );
  })()}
</div>
                <input type="number" style={{ ...inp, fontSize: 13, padding: '7px 8px', textAlign: 'center' }} value={li.qty} min={1} onChange={e => updateLineItem(li.id, 'qty', parseFloat(e.target.value) || 1)} />
                <input type="number" style={{ ...inp, fontSize: 13, padding: '7px 10px' }} placeholder="0.00" value={li.rate || ''} onChange={e => updateLineItem(li.id, 'rate', parseFloat(e.target.value) || 0)} />
                <button onClick={() => removeLineItem(li.id)} style={{ background: 'none', border: 'none', cursor: lineItems.length === 1 ? 'not-allowed' : 'pointer', color: lineItems.length === 1 ? '#d1d5db' : '#ef4444', display: 'flex', alignItems: 'center' }} disabled={lineItems.length === 1}>
                  <Trash2 size={12} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 4, alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <input type="number" style={{ ...inp, fontSize: 10, padding: '3px 16px 3px 5px' }} placeholder="Tax %" min={0} max={100}
                    value={li.taxRate || ''}
                    onChange={e => updateLineItem(li.id, 'taxRate' as any, parseFloat(e.target.value) || 0)} />
                  <span style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: '#9ca3af' }}>%</span>
                </div>
                <div
  style={{
    fontSize: 9, fontWeight: 700, borderRadius: 5, border: '1px solid',
    padding: '3px 8px', textAlign: 'center', cursor: 'default', userSelect: 'none',
    opacity: li.taxRate ? 1 : 0.4,
    background: li.taxInclusive ? '#ecfdf5' : '#fff',
    color: li.taxInclusive ? '#059669' : '#6b7280',
    borderColor: li.taxInclusive ? '#a7f3d0' : '#e5e7eb',
  }}
>
  {li.taxInclusive ? '✓ Tax Inclusive' : 'Tax Exclusive'}
</div>
              </div>
              {customColumns.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(customColumns.length, 3)}, 1fr)`, gap: 3 }}>
                  {customColumns.map(col => (
                    <input key={col.id} style={{ ...inp, fontSize: 10, padding: '3px 6px' }}
                      placeholder={col.header}
                      value={(li as any)[col.key] || ''}
                      onChange={e => {
  updateLineItem(li.id, col.key as any, e.target.value);
  scheduleTranslate(e.target.value, `custom_${li.id}_${col.key}`);
}}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Tax Lines */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Tax Lines</label>
            <button onClick={() => {
  if (taxLines.length >= 1 && !perms.canAddMultipleTaxLines) {
    setShowUpgradeNudge('Multiple tax lines is a Pro feature.');
    return;
  }
  setTaxLines(prev => [...prev, { label: 'Tax', rate: 0 }]);
}}
style={{ fontSize: 10, fontWeight: 600, color: '#10b981', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>
  {!perms.canAddMultipleTaxLines && taxLines.length >= 1 ? '🔒 Add Tax' : '+ Add Tax'}
</button>
          </div>
          {taxLines.map((t, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 22px', gap: 4, marginBottom: 4 }}>
              <input style={{ ...inp, fontSize: 11 }} placeholder="Tax label" value={t.label} onChange={e => {
  setTaxLines(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x));
  scheduleTranslate(e.target.value, `tax_${i}`);
}} />
              <div style={{ position: 'relative' }}>
                <input type="number" style={{ ...inp, fontSize: 11, paddingRight: 18 }} placeholder="0" value={t.rate || ''} min={0} max={100} onChange={e => setTaxLines(prev => prev.map((x, j) => j === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x))} />
                <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>%</span>
              </div>
              <button onClick={() => taxLines.length > 1 && setTaxLines(prev => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>

        {/* Discount Lines */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Discount Lines</label>
            <button onClick={() => {
  if (discountLines.length >= 1 && !perms.canAddDiscountLines) {
    setShowUpgradeNudge('Multiple discount lines is a Pro feature.');
    return;
  }
  setDiscountLines(prev => [...prev, { label: 'Discount', rate: 0 }]);
}}
style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>
  {!perms.canAddDiscountLines && discountLines.length >= 1 ? '🔒 Add Discount' : '+ Add Discount'}
</button>
          </div>
          {discountLines.map((d, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 22px', gap: 4, marginBottom: 4 }}>
              <input style={{ ...inp, fontSize: 11 }} placeholder="Discount label" value={d.label} onChange={e => {
  setDiscountLines(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x));
  scheduleTranslate(e.target.value, `discount_${i}`);
}} />
              <div style={{ position: 'relative' }}>
                <input type="number" style={{ ...inp, fontSize: 11, paddingRight: 18 }} placeholder="0" value={d.rate || ''} min={0} max={100} onChange={e => setDiscountLines(prev => prev.map((x, j) => j === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x))} />
                <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>%</span>
              </div>
              <button onClick={() => discountLines.length > 1 && setDiscountLines(prev => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>

        {/* Shipping */}
        <div>
          <label style={lbl}>Shipping Cost</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}>{sym}</span>
            <input type="number" style={{ ...inp, paddingLeft: 22 }} placeholder="0.00" value={shippingCost || ''} min={0} onChange={e => setShippingCost(parseFloat(e.target.value) || 0)} />
          </div>
        </div>

      {/* QR Code Toggles */}
        <div>
          <label style={lbl}>Auto QR Codes</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { key: 'qi_show_invoice_qr', label: 'Scan to view invoice', state: showInvoiceQR, set: setShowInvoiceQR },
              
            ].map(({ key, label, state, set }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{label}</span>
                <div onClick={() => { const next = !state; set(next); localStorage.setItem(key, String(next)); }}
                  style={{ width: 36, height: 20, borderRadius: 99, background: state ? '#3b82f6' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ position: 'absolute', top: 2, left: state ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>QR Size</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => { const v = Math.max(60, autoQrSize - 10); setAutoQrSize(v); localStorage.setItem('qi_auto_qr_size', String(v)); }}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', minWidth: 32, textAlign: 'center' }}>{autoQrSize}px</span>
                <button onClick={() => { const v = Math.min(200, autoQrSize + 10); setAutoQrSize(v); localStorage.setItem('qi_auto_qr_size', String(v)); }}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Label Size</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => { const v = Math.max(6, autoQrLabelSize - 1); setAutoQrLabelSize(v); localStorage.setItem('qi_auto_qr_label_size', String(v)); }}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', minWidth: 32, textAlign: 'center' }}>{autoQrLabelSize}px</span>
                <button onClick={() => { const v = Math.min(20, autoQrLabelSize + 1); setAutoQrLabelSize(v); localStorage.setItem('qi_auto_qr_label_size', String(v)); }}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Position</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {(['left','center','right'] as const).map(p => (
                  <button key={p} onClick={() => { setAutoQrPosition(p); localStorage.setItem('qi_auto_qr_position', p); }}
                    style={{ background: autoQrPosition === p ? '#3b82f6' : '#fff', color: autoQrPosition === p ? '#fff' : '#6b7280', border: '1px solid #e5e7eb', borderRadius: 5, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                    {p === 'left' ? '⬛◻◻' : p === 'center' ? '◻⬛◻' : '◻◻⬛'}
                  </button>
                ))}
                {(autoQrOffset.x !== 0 || autoQrOffset.y !== 0) && (
                  <button onClick={() => { const z = {x:0,y:0}; setAutoQrOffset(z); localStorage.setItem('qi_auto_qr_offset', JSON.stringify(z)); }}
                    style={{ background: '#fee2e2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 5, padding: '3px 7px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                    ↺
                  </button>
                )}
              </div>
            </div>
          </div>

          {false && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Tracking QR</span>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>QR Size</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => { const v = Math.max(60, trackingQrSize - 10); setTrackingQrSize(v); localStorage.setItem('qi_tracking_qr_size', String(v)); }}
                    style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', minWidth: 32, textAlign: 'center' }}>{trackingQrSize}px</span>
                  <button onClick={() => { const v = Math.min(200, trackingQrSize + 10); setTrackingQrSize(v); localStorage.setItem('qi_tracking_qr_size', String(v)); }}
                    style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Label Size</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => { const v = Math.max(6, trackingQrLabelSize - 1); setTrackingQrLabelSize(v); localStorage.setItem('qi_tracking_qr_label_size', String(v)); }}
                    style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', minWidth: 32, textAlign: 'center' }}>{trackingQrLabelSize}px</span>
                  <button onClick={() => { const v = Math.min(20, trackingQrLabelSize + 1); setTrackingQrLabelSize(v); localStorage.setItem('qi_tracking_qr_label_size', String(v)); }}
                    style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Position</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(['left','center','right'] as const).map(p => (
                    <button key={p} onClick={() => { setTrackingQrPosition(p); localStorage.setItem('qi_tracking_qr_position', p); }}
                      style={{ background: trackingQrPosition === p ? '#3b82f6' : '#fff', color: trackingQrPosition === p ? '#fff' : '#6b7280', border: '1px solid #e5e7eb', borderRadius: 5, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                      {p === 'left' ? '⬛◻◻' : p === 'center' ? '◻⬛◻' : '◻◻⬛'}
                    </button>
                  ))}
                  {(trackingQrOffset.x !== 0 || trackingQrOffset.y !== 0) && (
                    <button onClick={() => { const z = {x:0,y:0}; setTrackingQrOffset(z); localStorage.setItem('qi_tracking_qr_offset', JSON.stringify(z)); }}
                      style={{ background: '#fee2e2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 5, padding: '3px 7px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                      ↺
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Nudge Position</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {(['←','→','↑','↓'] as const).map(dir => (
                    <button key={dir} onClick={() => {
                      const delta = 5;
                      const next = { ...trackingQrOffset };
                      if (dir === '←') next.x -= delta;
                      if (dir === '→') next.x += delta;
                      if (dir === '↑') next.y -= delta;
                      if (dir === '↓') next.y += delta;
                      setTrackingQrOffset(next);
                      localStorage.setItem('qi_tracking_qr_offset', JSON.stringify(next));
                    }} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, width: 22, height: 22, cursor: 'pointer', fontSize: 11, color: '#6b7280' }}>{dir}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Auto-download PDF toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Auto-download PDF on save</span>
          <div
            onClick={() => {
              const next = !autoDownloadPdfOnSave;
              setAutoDownloadPdfOnSave(next);
              localStorage.setItem('cloudairambo_qi_autodownload_pdf', String(next));
            }}
            style={{ width: 36, height: 20, borderRadius: 99, background: autoDownloadPdfOnSave ? '#3b82f6' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}
          >
            <div style={{ position: 'absolute', top: 2, left: autoDownloadPdfOnSave ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
          </div>
        </div>

        {/* Amount in Words toggle */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div
            onClick={() => {
              const next = !showAmountInWords;
              setShowAmountInWords(next);
              localStorage.setItem('cloudairambo_qi_amount_in_words', String(next));
            }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', cursor: 'pointer' }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Show Amount in Words</span>
            <div style={{ width: 36, height: 20, borderRadius: 99, background: showAmountInWords ? '#3b82f6' : '#d1d5db', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 2, left: showAmountInWords ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
          {showAmountInWords && (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Font family */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', minWidth: 32 }}>Font</span>
                <select value={amountInWordsStyle.fontFamily} onChange={e => setAmountInWordsStyle(s => ({ ...s, fontFamily: e.target.value }))}
                  style={{ ...inp, fontSize: 11, flex: 1, padding: '4px 6px' }}>
                  {[
                    ['Inter, Arial, sans-serif', 'Inter'],
                    ['Georgia, serif', 'Georgia'],
                    ['Courier New, monospace', 'Courier'],
                    ['"Times New Roman", serif', 'Times New Roman'],
                    ['Arial, sans-serif', 'Arial'],
                    ['Verdana, sans-serif', 'Verdana'],
                    ['Trebuchet MS, sans-serif', 'Trebuchet'],
                    ['"Palatino Linotype", serif', 'Palatino'],
                    ['"Book Antiqua", serif', 'Book Antiqua'],
                    ['Garamond, serif', 'Garamond'],
                    ['"Comic Sans MS", cursive', 'Comic Sans'],
                    ['"Impact", sans-serif', 'Impact'],
                    ['"Lucida Console", monospace', 'Lucida Console'],
                    ['"Tahoma", sans-serif', 'Tahoma'],
                    ['"Century Gothic", sans-serif', 'Century Gothic'],
                  ].map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              {/* Size + color */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', minWidth: 32 }}>Size</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 6px' }}>
                  <button onClick={() => setAmountInWordsStyle(s => ({ ...s, fontSize: Math.max(6, s.fontSize - 1) }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280', padding: '0 2px', lineHeight: 1 }}>−</button>
                  <span style={{ fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{amountInWordsStyle.fontSize}</span>
                  <button onClick={() => setAmountInWordsStyle(s => ({ ...s, fontSize: Math.min(20, s.fontSize + 1) }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280', padding: '0 2px', lineHeight: 1 }}>+</button>
                </div>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', minWidth: 32, marginLeft: 4 }}>Color</span>
                <input type="color" value={amountInWordsStyle.color} onChange={e => setAmountInWordsStyle(s => ({ ...s, color: e.target.value }))}
                  style={{ width: 32, height: 28, border: '1px solid #e5e7eb', borderRadius: 5, cursor: 'pointer', padding: 2, background: '#fff' }} />
              </div>
              {/* Alignment + style */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', minWidth: 32 }}>Align</span>
                {(['left','center','right'] as const).map(a => (
                  <button key={a} onClick={() => setAmountInWordsStyle(s => ({ ...s, textAlign: a }))}
                    style={{ background: amountInWordsStyle.textAlign === a ? '#3b82f6' : '#fff', border: '1px solid #e5e7eb', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: amountInWordsStyle.textAlign === a ? '#fff' : '#6b7280', fontWeight: 600 }}>
                    {a === 'left' ? '⬛◻◻' : a === 'center' ? '◻⬛◻' : '◻◻⬛'}
                  </button>
                ))}
                <button onClick={() => setAmountInWordsStyle(s => ({ ...s, fontWeight: s.fontWeight === 'bold' ? 'normal' : 'bold' }))}
                  style={{ background: amountInWordsStyle.fontWeight === 'bold' ? '#1e293b' : '#fff', border: '1px solid #e5e7eb', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: amountInWordsStyle.fontWeight === 'bold' ? '#fff' : '#6b7280', fontWeight: 'bold' }}>B</button>
                <button onClick={() => setAmountInWordsStyle(s => ({ ...s, fontStyle: s.fontStyle === 'italic' ? 'normal' : 'italic' }))}
                  style={{ background: amountInWordsStyle.fontStyle === 'italic' ? '#1e293b' : '#fff', border: '1px solid #e5e7eb', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12, color: amountInWordsStyle.fontStyle === 'italic' ? '#fff' : '#6b7280', fontStyle: 'italic' }}>I</button>
              </div>
            </div>
          )}
        </div>

        {/* Totals summary */}
        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={row}><span style={{ color: '#6b7280', fontSize: 12 }}>Subtotal</span><span style={{ fontSize: 12, fontWeight: 600 }}>{sym}{subtotal.toFixed(2)}</span></div>
          {productTaxTotal > 0 && quickInvTaxBreakdown && (() => {
            const needsBuyerRegion = qiTaxScheme.region_based && !buyerRegion.trim();
            return (
              <>
                {quickInvTaxBreakdown.map(({ name, rate, amount }) => (
                  <div key={name} style={row}>
                    <span style={{ color: '#7c3aed', fontSize: 11 }}>{name} ({rate}%)</span>
                    <span style={{ fontSize: 11, color: '#7c3aed' }}>+{sym}{amount.toFixed(2)}</span>
                  </div>
                ))}
                {needsBuyerRegion && (
                  <div style={{ fontSize: 9, color: '#f59e0b', fontStyle: 'italic' }}>
                    ⚠ Enter buyer's state above — showing IGST (different-state) by default
                  </div>
                )}
              </>
            );
          })()}
          {taxLines.filter(t => t.rate > 0).map((t, i) => (
            <div key={i} style={row}><span style={{ color: '#6b7280', fontSize: 11 }}>{t.label} ({t.rate}%)</span><span style={{ fontSize: 11 }}>+{sym}{(taxableValue * t.rate / 100).toFixed(2)}</span></div>
          ))}
          {discountLines.filter(d => d.rate > 0).map((d, i) => (
            <div key={i} style={row}><span style={{ color: '#6b7280', fontSize: 11 }}>{d.label} ({d.rate}%)</span><span style={{ fontSize: 11, color: '#ef4444' }}>-{sym}{(subtotal * d.rate / 100).toFixed(2)}</span></div>
          ))}
          {autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0 && (
            <div style={row}>
              <span style={{ color: '#059669', fontSize: 11 }}>{autoDiscountRule.label || 'Loyalty Discount'} 🏷️</span>
              <span style={{ fontSize: 11, color: '#059669' }}>-{sym}{autoDiscountAmount.toFixed(2)}</span>
            </div>
          )}
          {autoDiscountRule && !autoDiscountRule.visibleOnBill && autoDiscountAmount > 0 && (
            <div style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic' }}>✓ Loyalty discount applied (hidden from bill)</div>
          )}
          {shippingCost > 0 && <div style={row}><span style={{ color: '#6b7280', fontSize: 11 }}>Shipping</span><span style={{ fontSize: 11 }}>+{sym}{shippingCost.toFixed(2)}</span></div>}
          <div style={{ ...row, borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 2 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>Total</span>
            <span style={{ fontWeight: 800, fontSize: 15, color: '#3b82f6' }}>{sym}{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
{/* Upgrade nudge */}
      {showUpgradeNudge && (
        <div style={{ margin: '0 18px 12px', background: 'linear-gradient(135deg,#eef2ff,#f5f3ff)', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🔒</span>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#4338ca' }}>{showUpgradeNudge}</div>
              <div style={{ fontSize: 10, color: '#6366f1', marginTop: 2 }}>Available on Pro & Ultra plans</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => setShowUpgradeNudge(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#94a3b8', padding: '2px 6px' }}>
              ✕
            </button>
            <button
              onClick={() => {
                setShowUpgradeNudge(null);
                window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
              }}
              style={{ background: '#6366f1', border: 'none', borderRadius: 7, padding: '5px 12px', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              Upgrade ✨
            </button>
          </div>
        </div>
      )}


      {/* Payment Mode */}
      <div style={{ padding: '0 20px 14px', flexShrink: 0 }}>
        <label style={lbl}>Payment Mode</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {PAY_MODES.map(pm => (
            <button key={pm.key} onClick={() => {
              setPaymentMode(pm.key);
              // Cash is settled the moment it's tendered — default status
              // to Paid so it matches (user can still override manually).
              if (pm.key === 'cash') setInvoiceStatus('paid');
            }} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 8px', borderRadius: 10, border: '2px solid',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
              background: paymentMode === pm.key ? '#3b82f6' : '#f8fafc',
              color: paymentMode === pm.key ? '#fff' : '#64748b',
              borderColor: paymentMode === pm.key ? '#3b82f6' : '#cbd5e1',
            }}>
              {pm.icon} {pm.label}
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>

        <button 
  onClick={() => {
    console.log('[QI BUTTON]', { editingPendingInvoice, isEditMode });
    (editingPendingInvoice ? updatePendingInvoiceAndResume : handleGenerate)();
  }} 
  disabled={saving || (bilingualEnabled && isQuickInvTranslating) || (!isEditMode && !perms.canAccessGenerator) || (isEditMode && !perms.canEditInvoice)} 
  style={{
    width: '100%', padding: '15px 18px', borderRadius: 12, border: 'none',
    background: (bilingualEnabled && isQuickInvTranslating)
      ? '#94a3b8'
      : saved
      ? '#10b981'
      : (editingPendingInvoice || isEditMode)
        ? 'linear-gradient(to right,#f59e0b,#f97316)'
        : 'linear-gradient(to right,#3b82f6,#9333ea)',
    color: '#fff', fontWeight: 800, fontSize: 16,
    cursor: (saving || (bilingualEnabled && isQuickInvTranslating) || (!isEditMode && !perms.canAccessGenerator) || (isEditMode && !perms.canEditInvoice)) ? 'not-allowed' : 'pointer',
    opacity: (saving || (bilingualEnabled && isQuickInvTranslating) || (!isEditMode && !perms.canAccessGenerator) || (isEditMode && !perms.canEditInvoice)) ? 0.6 : 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    boxShadow: (bilingualEnabled && isQuickInvTranslating) ? 'none' : '0 4px 14px rgba(59,130,246,0.3)',
  }}>
  {bilingualEnabled && isQuickInvTranslating
    ? '🌐 Translating invoice...'
    : saved
    ? <><CheckCircle size={15} /> {isEditMode ? 'Updated!' : 'Invoice Saved!'}</>
    : saving
    ? (editingPendingInvoice ? 'Updating pending invoice...' : isEditMode ? 'Updating...' : 'Generating...')
    : editingPendingInvoice
    ? <><Zap size={15} /> Update Pending Invoice — {sym}{total.toFixed(2)}</>
    : <><Zap size={15} /> {isEditMode ? 'Update Invoice' : 'Generate & Save Invoice'}</>}
</button>
      </div>
    </div>
  );

  if (!perms.canAccessGenerator && !isEditMode) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '80px', 
        color: '#9ca3af', 
        fontSize: 14,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        You don't have permission to access the Invoice Generator.
      </div>
    );
  }

  return (
  <div 
    style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'row', 
      overflow: 'hidden', 
      background: '#f0f4f8', 
      position: 'relative',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }} 
    onClick={() => setShowClientDropdown(false)}
  >
   <style>{`
  .qi-dropdown-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .qi-dropdown-scroll::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    display: none;
  }
  .qi-preview-scroll,
  .qi-preview-scroll * {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .qi-preview-scroll::-webkit-scrollbar,
  .qi-preview-scroll *::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    display: none;
  }
  .qi-cw-embed,
  .qi-cw-embed * {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .qi-cw-embed::-webkit-scrollbar,
  .qi-cw-embed *::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    display: none;
  }
  @media (min-width: 768px) {
    html, body {
      overflow: hidden !important;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  }
  html::-webkit-scrollbar, body::-webkit-scrollbar {
    display: none;
    width: 0px;
    height: 0px;
  }
`}</style>
      {isMobile && (
        <button
          onClick={() => setShowMobilePreview(v => !v)}
          style={{
            position: 'fixed',
            bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
            right: 14,
            zIndex: 9999,
            background: showMobilePreview
              ? '#0f172a'
              : 'linear-gradient(135deg, #2563eb, #7c3aed)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 99,
            padding: '9px 16px',
            fontSize: 12,
            fontWeight: 800,
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            backdropFilter: 'blur(4px)',
          }}
        >
          {showMobilePreview ? '✎ Edit Form' : '👁 Full Preview'}
        </button>
      )}

      {(formSide === 'left') && (isMobile ? !showMobilePreview : true) && formPanel}

   {/* Preview Area */}
<div
  ref={previewRef}
  className="qi-preview-scroll"
  style={{
    flex: 1,
    height: '100%',
    minHeight: '100%',
    display: isMobile && !showMobilePreview ? 'none' : 'flex',
    width: isMobile ? '100%' : undefined,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 0,
    background: '#e2e8f0',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  }}
>
        <div style={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        
          {/* Canvas Workspace preview — reused as-is, no edits made to it */}
{selectedTemplate && (() => {
  const previewSourceTemplate = (bilingualEnabled && quickInvTranslatedTemplate) ? quickInvTranslatedTemplate : selectedTemplate;
  const previewDynamicValues = {
    ...dynamicValues,
    invoice_number: invoiceNumberOverride.trim() || dynamicValues.invoice_number || 'PENDING',
    date: invoiceDate,
    time: invoiceTime,
  };
  const cleanManualDiscountLines = discountLines.filter(d => !d._isAuto && !/loyalty/i.test(d.label || ''));
  const previewDiscountLinesRaw = (autoDiscountRule && autoDiscountRule.visibleOnBill && autoDiscountAmount > 0)
    ? [...cleanManualDiscountLines, { label: autoDiscountRule.label || 'Loyalty Discount', rate: parseFloat(((autoDiscountAmount / Math.max(subtotal, 1)) * 100).toFixed(4)), amount: autoDiscountAmount, _isAuto: true }]
    : cleanManualDiscountLines;
  const previewDiscountLines = previewDiscountLinesRaw.map((d: any, i: number) => ({
    ...d,
    translatedLabel: d._isAuto
      ? (translatedValuesRef.current['label_loyalty'] || translatedValues['label_loyalty'] || undefined)
      : (translatedValues[`discount_${i}`] || undefined),
  }));

  // Named GST/VAT component breakdown (CGST/SGST/IGST/etc.) for the
  // thermal bilingual preview (POSReceiptRenderer) — mirrors the same
  // computation the A4 preview's computeTableTotals callback and
  // BilingualInvoiceRenderer already do internally. Without this,
  // POSReceiptRenderer never receives a taxBreakdown prop at all and
  // always falls back to a single blended "Total Tax (X%)" line, even
  // when the outlet's scheme is component-based (CGST+SGST/IGST etc).
  let previewTaxBreakdown: { name: string; rate: number; amount: number }[] | undefined;
  if (qiTaxScheme.kind === 'component' && productTaxTotal > 0) {
    const combined: Record<string, { rate: number; amount: number }> = {};
    lineItems.filter(li => li.description.trim() && getEffectiveTaxRate(li) > 0).forEach(li => {
      const b = getItemTaxBreakdown(li);
      const discountedBase = b.base * (1 - discountRatio);
      const breakdown = computeTaxBreakdown(
        qiTaxScheme, discountedBase,
        { sellerRegion: qiOutletRegion, buyerRegion }, getEffectiveTaxRate(li)
      );
      breakdown.lines.forEach(l => {
        if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
        combined[l.name].amount += l.amount;
      });
    });
    const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
    if (entries.length > 0) previewTaxBreakdown = entries;
  }
     
  const effectivePreviewName = includeFields.name ? (clientName || 'Customer') : '';
  const effectivePreviewEmail = includeFields.email ? clientEmail : '';
  const effectivePreviewPhone = includeFields.phone ? clientPhone : '';
  const effectivePreviewAddress = includeFields.address ? clientAddress : '';
  const effectivePreviewTaxId = includeFields.taxId ? clientGSTIN : '';

  const cwData = buildCwElements(previewSourceTemplate, {
    lineItems: lineItems.map(li => ({ ...li, taxRate: getEffectiveTaxRate(li) })),
    taxLines, discountLines: previewDiscountLines, shippingCost,
    clientName: effectivePreviewName,
    clientEmail: effectivePreviewEmail,
    clientPhone: effectivePreviewPhone,
    clientAddress: effectivePreviewAddress,
    notes,
    buyerTaxId: effectivePreviewTaxId,
    invoiceDate, dueDate, showDueDate,
    showInvoiceQR, autoQrPosition, autoQrSize, autoQrLabelSize, autoQrOffset,
    qrValue: `${window.location.origin}/?i=preview`,
    translatedValues: bilingualEnabled ? translatedValues : {},
    dynamicValues: previewDynamicValues,
    cashierName: authUser?.name || 'Staff',
    customColumns,
    hiddenColumns,
    catalogProducts,
    getCatalogColValue,
  });

  // Placeholder QR boxes the person can drag in the live preview — stacked
  // in the same order the backend uses (repositionAutoGroups), so what's
  // shown here matches where the real QR lands after save.
  const estimatedTableEl = cwData.cwElements.find((e: any) => e.type === 'table');
  const estimatedTableBottom = estimatedTableEl ? (estimatedTableEl.y || 0) + (estimatedTableEl.height || 0) : 0;
  const tableDrift = Math.max(0, (measuredTableBottom ?? estimatedTableBottom) - estimatedTableBottom);

  // tableDrift was only ever applied to the newly-added preview QR below —
  // the template's OWN baked-in elements (its QR, caption, notes, etc.)
  // stayed positioned off the raw 20px/row estimate, which badly
  // undercounts real row height on long tables. That's why one QR sat
  // correctly below the table (drift-corrected) while the template's own
  // QR floated mid-table (never corrected) — two different "bottom of
  // table" values driving two different elements. Apply the same
  // correction here, mirroring the fix already used in renderCwToHtml.
  const isThermalPreview =
  cwData.pageConfig.width <= 302;

const liveTableBottom =
  measuredTableBottom ?? estimatedTableBottom;

const originalFooterElements = cwData.cwElements
  .filter((el: any) => {
    if (el.type === 'table') return false;

    const y = Number(el.y || 0);
    const h = Number(el.height || 0);
    const bottom = y + h;

    // Element belongs to the footer when it starts near/below the
    // designed table bottom OR its box crosses that boundary.
    return (
      y >= estimatedTableBottom - 20 ||
      (
        y >= Number(estimatedTableEl?.y || 0) &&
        bottom > estimatedTableBottom
      )
    );
  })
  .sort(
    (a: any, b: any) =>
      Number(a.y || 0) - Number(b.y || 0)
  );

const thermalFooterY = new Map<string, number>();

if (isThermalPreview && estimatedTableEl) {
  const FOOTER_START_GAP =
    cwData.pageConfig.width <= 220 ? 10 : 12;

  const FOOTER_ITEM_GAP =
    cwData.pageConfig.width <= 220 ? 6 : 8;

  let cursor = liveTableBottom + FOOTER_START_GAP;

  originalFooterElements.forEach((el: any) => {
    const originalY = Number(el.y || 0);
    const originalHeight = Math.max(
      1,
      Number(el.height || 0)
    );

    /*
     * Preserve useful spacing from the template, but never permit
     * thermal footer elements to overlap each other.
     */
    const previous = originalFooterElements[
      originalFooterElements.indexOf(el) - 1
    ];

    let designedGap = FOOTER_ITEM_GAP;

    if (previous) {
      const previousBottom =
        Number(previous.y || 0) +
        Number(previous.height || 0);

      designedGap = Math.max(
        FOOTER_ITEM_GAP,
        originalY - previousBottom
      );
    }

    if (previous) {
      cursor += designedGap;
    }

    thermalFooterY.set(el.id, cursor);

    cursor += originalHeight;
  });
}

const driftedCwElements = cwData.cwElements.map(
  (el: any) => {
    if (el.type === 'table') return el;

    /*
     * Thermal receipts:
     * Footer becomes a real vertical flow after the LIVE table.
     */
    if (
      isThermalPreview &&
      thermalFooterY.has(el.id)
    ) {
      return {
        ...el,
        y: thermalFooterY.get(el.id),
      };
    }

    /*
     * Normal paper sizes keep the existing drift behaviour.
     */
    if (
      tableDrift > 0 &&
      (el.y || 0) >= estimatedTableBottom - 10
    ) {
      return {
        ...el,
        y: (el.y || 0) + tableDrift,
      };
    }

    return el;
  }
);
    const previewQrElements: any[] = [];

  const previewAmountInWordsElements: any[] = [];

// Amount in Words is inserted dynamically after the table.
// Track its position/height so existing template content below that
// position can be pushed down instead of overlapping it.
let amountInWordsPushStart = -1;
let amountInWordsPushAmount = 0;

if (showAmountInWords) {
  const amountWords = amountToWords(total, activeCurrency);

  const tableElement = cwData.cwElements.find(
  (el: any) => el.type === 'table'
);

// Amount in Words must sit directly under the TABLE, not after
// whatever else (QR, Notes) has already been placed below it — those
// need to be pushed further down to make room, not used as the anchor.
amountInWordsPushStart = liveTableBottom;

// Space required for Amount in Words + a safe gap.
// Scale the gap for thermal paper instead of using an A4-only value.
const amountWordsHeight = Math.max(
  30,
  amountInWordsStyle.fontSize * 3
);

const amountWordsGap =
  cwData.pageConfig.width <= 220 ? 8 :   // Thermal 58mm
  cwData.pageConfig.width <= 302 ? 10 :  // Thermal 80mm
  12;                                    // A4/A5/Letter/Legal

amountInWordsPushAmount = amountWordsHeight + amountWordsGap;

  previewAmountInWordsElements.push({
    id: 'preview-amount-in-words',
    type: 'text',

    x: 20,
    y: liveTableBottom + amountWordsGap,

    width: Math.max(100, cwData.pageConfig.width - 40),
    height: Math.max(30, amountInWordsStyle.fontSize * 3),

    zIndex: 998,
    opacity: 100,
    rotation: 0,

    textContent: `Amount in Words: ${amountWords}`,

    fontFamily: amountInWordsStyle.fontFamily,
    fontSize: amountInWordsStyle.fontSize,
    textAlign: amountInWordsStyle.textAlign,
    fontStyle: amountInWordsStyle.fontStyle,
    fontWeight: amountInWordsStyle.fontWeight,
    color: amountInWordsStyle.color,
  });
}

// Push existing template elements that occupy the Amount-in-Words area
// downward. This handles Thank You, Bill No, Goods once sold, QR,
// signature, stamp, notes, etc. on every page size.
if (showAmountInWords && amountInWordsPushStart >= 0) {
  driftedCwElements.forEach((el: any) => {
    if (el.type === 'table') return;

    const elTop = el.y || 0;

    if (elTop >= amountInWordsPushStart) {
      el.y = elTop + amountInWordsPushAmount;
    }
  });
}

  {
    const contentBottom = driftedCwElements.reduce(
      (max, el) => Math.max(max, (el.y || 0) + (el.height || 0)),
      0
    );
    const dragDx = (g: 'invoice'|'tracking') => previewQrDragDelta && previewQrDragDelta.group === g ? previewQrDragDelta.dx : 0;
    const dragDy = (g: 'invoice'|'tracking') => previewQrDragDelta && previewQrDragDelta.group === g ? previewQrDragDelta.dy : 0;
    let cursor = contentBottom + (
  cwData.pageConfig.width <= 220 ? 8 :
  cwData.pageConfig.width <= 302 ? 10 :
  20
);
    const alreadyHasRealInvoiceQr = cwData.cwElements.some((el: any) => el._auto === 'invoice');
    if (showInvoiceQR && !alreadyHasRealInvoiceQr) {
      const tableEl = cwData.cwElements.find((e: any) => e.type === 'table');
      const tableX = tableEl?.x ?? 20;
      const tableW = tableEl?.width ?? (cwData.pageConfig.width - 40);

      // Align flush with table bounds (Left = table start, Right = table end, Center = table center)
      const baseX = autoQrPosition === 'left'
        ? tableX
        : autoQrPosition === 'right'
          ? (tableX + tableW - autoQrSize)
          : tableX + (tableW - autoQrSize) / 2;

      const x = Math.max(0, Math.min(cwData.pageConfig.width - autoQrSize, baseX + autoQrOffset.x + dragDx('invoice')));
      const y = cursor + autoQrOffset.y + dragDy('invoice');

      // Caption positioned snugly 3px under QR code
      const labelW = Math.max(autoQrSize + 40, 120);
      const labelX = x + (autoQrSize - labelW) / 2;
      const labelY = y + autoQrSize + 3;

      previewQrElements.push({ id: 'preview-invoice-qr', type: 'qr', x, y, width: autoQrSize, height: autoQrSize, zIndex: 999, opacity: 100, rotation: 0, qrValue: 'preview' });
      previewQrElements.push({ id: 'preview-invoice-qr-label', type: 'text', x: labelX, y: labelY, width: labelW, height: 16, zIndex: 999, opacity: 100, rotation: 0, textContent: 'Scan to view invoice', arabicContent: quickInvTranslatedTemplate ? (translatedValuesRef.current['qr_caption_invoice'] || translatedValues['qr_caption_invoice'] || '') : undefined, fontSize: autoQrLabelSize, textAlign: 'center', color: '#000000', fontWeight: '800', fontFamily: 'Inter' });
      cursor = cursor + autoQrOffset.y + autoQrSize + 28;
    }
    if (showTrackingQR) {
      const trackingQrMargin = Math.max(4, Math.min(60, (cwData.pageConfig.width - trackingQrSize) / 4));
      const baseX = trackingQrPosition === 'left' ? trackingQrMargin : trackingQrPosition === 'right' ? cwData.pageConfig.width - trackingQrSize - trackingQrMargin : (cwData.pageConfig.width - trackingQrSize) / 2;
      const x = baseX + trackingQrOffset.x + dragDx('tracking');
      const y = cursor + trackingQrOffset.y + dragDy('tracking');
      previewQrElements.push({ id: 'preview-tracking-qr', type: 'qr', x, y, width: trackingQrSize, height: trackingQrSize, zIndex: 999, opacity: 100, rotation: 0, qrValue: 'preview' });
      previewQrElements.push({ id: 'preview-tracking-qr-label', type: 'text', x: x - 40, y: y + trackingQrSize + 4, width: trackingQrSize + 80, height: 16, zIndex: 999, opacity: 100, rotation: 0, textContent: 'Scan to track your order', arabicContent: quickInvTranslatedTemplate ? (translatedValues['qr_caption_tracking'] || '') : undefined, fontSize: trackingQrLabelSize, textAlign: 'center', color: '#64748b', fontFamily: 'Inter' });
    }
  }

  livePreviewElementsRef.current = [
    ...driftedCwElements,
    ...previewAmountInWordsElements,
    ...previewQrElements,
  ];
  const __cwComputedHeight = previewPageBehavior === 'fixed'
    ? cwData.pageConfig.height
    : Math.max(cwData.pageConfig.height, cwData.naturalContentBottom);
  const __cwPageCount = previewPageBehavior === 'multi_page'
    ? Math.max(1, Math.ceil(cwData.naturalContentBottom / cwData.pageConfig.height))
    : 1;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 8, padding: 4, width: 'fit-content' }}>
        {([
          { key: 'fixed', label: 'Single' },
          { key: 'auto_expand', label: 'Auto' },
          { key: 'multi_page', label: 'A4 Split' },
        ] as const).map(opt => (
          <button
            key={opt.key}
            onClick={() => setPreviewPageBehavior(opt.key)}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: previewPageBehavior === opt.key ? '#fff' : 'transparent',
              color: previewPageBehavior === opt.key ? '#2563eb' : '#64748b',
              boxShadow: previewPageBehavior === opt.key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

    

      {showPrintSettings && (
        <div onClick={() => setShowPrintSettings(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.25)', position: 'relative' }}>
        <button onClick={() => setShowPrintSettings(false)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af' }}>×</button>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>Print Settings</div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>
  Printer
</label>

<select
  value={selectedPrinter}
  onChange={e => setSelectedPrinter(e.target.value)}
  style={{
    width: '100%',
    padding: '6px 9px',
    borderRadius: 7,
    border: '1px solid #e5e7eb',
    fontSize: 11,
    outline: 'none'
  }}
>
  <option value="">Default Printer</option>
  {availablePrinters.map(p => (
    <option key={p} value={p}>{p}</option>
  ))}
</select>
            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>
              {availablePrinters.length === 0 ? 'No printers detected — will use browser print' : `${availablePrinters.length} printer(s) found`}
            </div>
          </div>

          <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Printer Connection</div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => {
              setUseUSBPrinter(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false');
              setUseBTPrinter(false); localStorage.setItem('cloudairambo_qi_bt_print', 'false');
            }} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '8px 4px', borderRadius: 8, border: '1.5px solid',
              cursor: 'pointer', fontSize: 10, fontWeight: 700,
              background: (!useUSBPrinter && !useBTPrinter) ? '#eef2ff' : '#fff',
              borderColor: (!useUSBPrinter && !useBTPrinter) ? '#6366f1' : '#e5e7eb',
              color: (!useUSBPrinter && !useBTPrinter) ? '#4f46e5' : '#6b7280',
            }}>
              <span style={{ fontSize: 15 }}>📶</span>
              WiFi
            </button>
            <button onClick={() => { setUseUSBPrinter(true); localStorage.setItem('cloudairambo_qi_usb_print', 'true'); setUseBTPrinter(false); localStorage.setItem('cloudairambo_qi_bt_print', 'false'); }}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                padding: '8px 4px', borderRadius: 8, border: '1.5px solid',
                cursor: 'pointer', fontSize: 10, fontWeight: 700,
                background: useUSBPrinter ? '#eef2ff' : '#fff',
                borderColor: useUSBPrinter ? '#6366f1' : '#e5e7eb',
                color: useUSBPrinter ? '#4f46e5' : '#6b7280',
              }}>
              <span style={{ fontSize: 15 }}>🔌</span>
              USB
            </button>
            <button onClick={() => { setUseBTPrinter(true); localStorage.setItem('cloudairambo_qi_bt_print', 'true'); setUseUSBPrinter(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false'); }}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                padding: '8px 4px', borderRadius: 8, border: '1.5px solid',
                cursor: 'pointer', fontSize: 10, fontWeight: 700,
                background: useBTPrinter ? '#eef2ff' : '#fff',
                borderColor: useBTPrinter ? '#6366f1' : '#e5e7eb',
                color: useBTPrinter ? '#4f46e5' : '#6b7280',
              }}>
              <span style={{ fontSize: 15 }}>🔵</span>
              Bluetooth
            </button>
          </div>

          {!useUSBPrinter && !useBTPrinter && (
            <div style={{ fontSize: 9, color: '#6b7280', lineHeight: 1.5 }}>
              Opens your device's print dialog — pick your WiFi printer there,
              or your selected printer above, same as printing from any other app.
            </div>
          )}

          {useUSBPrinter && (
            <>
              <div style={{ fontSize: 9, color: '#6b7280' }}>
                {usbConnected ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected'}
              </div>
              {!usbConnected && (
                <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }} style={{
                  width: '100%', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
                  border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer',
                }}>
                  🔌 Connect USB Printer
                </button>
              )}
            </>
          )}

          {useBTPrinter && (
            <>
              <div style={{ fontSize: 9, color: '#6b7280' }}>
                {btConnected ? `Connected: ${getBTPrinterName() || 'BT Printer'}` : 'No Bluetooth printer connected'}
              </div>
              <button onClick={async () => {
                const char = await connectBluetoothPrinter();
                setBtConnected(!!char);
              }} style={{
                width: '100%', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6,
                border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer',
              }}>
                🔵 Connect Bluetooth Printer
              </button>
            </>
          )}
        </div>
        </div>
      )}
      </div>
      <div style={{ marginBottom: 0, border: 'none', borderRadius: 0, width: '100%', position: 'relative' }} className="qi-cw-embed">
        {/* Floating sticky zoom control for the preview */}
        {!isMobile && (
          <div style={{
            position: 'sticky', top: 12, float: 'right', marginRight: 16,
            zIndex: 200, display: 'flex', alignItems: 'center', gap: 10,
            background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10,
            padding: '6px 12px', boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
          }}>
            <button onClick={() => setZoom(z => Math.max(0.4, +(z - 0.1).toFixed(2)))}
              style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 16, color: '#374151', lineHeight: 1, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', minWidth: 42, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(2.0, +(z + 0.1).toFixed(2)))}
              style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 16, color: '#374151', lineHeight: 1, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            <button onClick={() => setZoom(1)}
              style={{ background: '#e2e8f0', border: 'none', cursor: 'pointer', fontSize: 11, color: '#374151', borderRadius: 6, padding: '4px 10px', fontWeight: 700, marginLeft: 2 }}>Reset</button>
          </div>
        )}
        {isThermalTemplate ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 12px 60px', width: '100%', boxSizing: 'border-box' }}>
            {/* 🧾 Crisp Paper Card Container with Paper Shadow & Zoom height compensation */}
            <div
              ref={previewCanvasRef}
              style={{
                width: (selectedTemplate?.pageSize === 'THERMAL_58MM' ? 220 : 302),
                background: '#ffffff',
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                border: '1px solid #cbd5e1',
                padding: '6px 8px 10px',
                boxSizing: 'border-box',
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                marginBottom: zoom > 1 ? `${(zoom - 1) * 500}px` : '0px',
                transition: 'transform 0.12s ease',
              }}
            >
              <POSReceiptRenderer
                template={{
                  ...selectedTemplate,
                  elements: (bilingualEnabled && quickInvTranslatedTemplate ? quickInvTranslatedTemplate.elements : selectedTemplate.elements).map((el: any) => {
                    const activeStaff = authUser?.name?.trim() || 'Staff';
                    const activeCustFallback = includeFields.name && clientName.trim() ? clientName.trim() : '';

                    // Use what you typed in TEMPLATE FIELDS; if left blank, fall back to Customer Info
                    const mergedFields = {
                      patient: activeCustFallback,
                      guest: activeCustFallback,
                      pet: activeCustFallback,
                      student: activeCustFallback,
                      ...previewDynamicValues,
                    };
                    if (!mergedFields.patient) mergedFields.patient = activeCustFallback;

                    return {
                      ...el,
                      textContent: el.textContent
                        ? patchDynamicFields(
                            el.textContent.replace(/\{staff_name\}/gi, activeStaff),
                            mergedFields
                          )
                        : el.textContent,
                    };
                  }),
                  includeFields: { ...includeFields },
                  include_fields: { ...includeFields },
                }}
                cashierName={authUser?.name?.trim() || 'Staff'}
                clientName={includeFields.name ? (clientName || 'Walk-in Customer') : ''}
                clientEmail={includeFields.email ? clientEmail : ''}
                clientPhone={includeFields.phone ? clientPhone : ''}
                clientAddress={includeFields.address ? clientAddress : ''}
                buyerTaxId={includeFields.taxId ? (clientGSTIN.trim() || undefined) : undefined}
                isB2B={isB2B || Boolean(clientGSTIN.trim())}
                invoiceDate={invoiceDate}
                invoiceTime={invoiceTime}
                notes={notes}
                lineItems={lineItems.filter(li => li.description.trim()).map(li => ({
                  id: li.id,
                  description: li.description,
                  qty: li.qty,
                  rate: li.rate,
                  arabicName: bilingualEnabled ? translatedValues[`item_${li.id}`] : undefined,
                  taxRate: getEffectiveTaxRate(li),
                  taxName: li.taxName,
                  b2bTaxRate: isB2B ? li.b2bTaxRate : undefined,
                  b2bTaxName: isB2B ? (li.b2bTaxName || (customTaxScheme as any)?.b2b_name || li.taxName || customTaxScheme?.name) : undefined,
                  taxInclusive: !!li.taxInclusive,
                  ...Object.fromEntries(customColumns.map(col => [col.key, (li as any)[col.key] || ''])),
                  customColAr: Object.fromEntries(customColumns.map(col => {
                    const raw = (li as any)[col.key] || '';
                    return [col.key, translatedValues[`custom_${li.id}_${col.key}`] ||
                      translatedValues[`custom_${li.id}_${col.key.replace(/^cat_/, '')}`] ||
                      translatedValues[`tax_name_${raw}`] || ''];
                  })),
                }))}
                taxLines={taxLines}
                discountLines={previewDiscountLines}
                sym={sym}
                shippingCost={shippingCost}
                invoiceStatus={invoiceStatus}
                translateTargetLang={quickInvLang}
                isBilingual={bilingualEnabled && !!quickInvTranslatedTemplate}
                isThermal={true}
                showTokenQRPlaceholder={false}
                clientNameAr={bilingualEnabled ? (translatedValues['clientName'] || undefined) : undefined}
                clientLineAr={bilingualEnabled ? (translatedValues['clientAddress'] || undefined) : undefined}
                extraColumns={customColumns}
                showWalkIn={true}
                dynamicValues={previewDynamicValues}
                dynamicValuesAr={bilingualEnabled ? translatedValues : undefined}
                dynamicFieldLabelsAr={bilingualEnabled ? dynamicFieldLabelsAr : undefined}
                productTaxTotal={productTaxTotal}
                netSubtotal={subtotal}
                taxBreakdown={quickInvTaxBreakdown}
                taxBreakdownAr={bilingualEnabled ? taxBreakdownAr : undefined}
                showAmountInWords={showAmountInWords}
                amountInWords={amountToWords(total, activeCurrency)}
                amountInWordsTranslated={bilingualEnabled ? (translatedValues['amountInWords'] || '') : undefined}
              />
            </div>
          </div>
        ) : (bilingualEnabled && quickInvTranslatedTemplate) ? (
           <div ref={previewCanvasRef} style={{ display: 'flex', justifyContent: 'center', padding: '20px 0', overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
          <BilingualInvoiceRenderer
            template={{
  ...quickInvTranslatedTemplate,
  elements: quickInvTranslatedTemplate.elements.map((el: any) => {
    if (el.type !== 'text') return el;
    const activeStaff = authUser?.name?.trim() || 'Staff';

    return {
      ...el,
      textContent: patchDynamicFields(
        (el.textContent || '').replace(/\{staff_name\}/gi, activeStaff),
        omitStaffKeys(previewDynamicValues)
      ),
      arabicContent: el.arabicContent
        ? patchDynamicFields(
            el.arabicContent.replace(/\{staff_name\}/gi, translatedValues['staffName_translit'] || activeStaff),
            omitStaffKeys(previewDynamicValues)
          )
        : el.arabicContent,
    };
  }),
}}
            clientName={includeFields.name ? (clientName || 'Customer') : ''}
            clientEmail={includeFields.email ? clientEmail : ''}
            clientPhone={includeFields.phone ? clientPhone : ''}
            clientAddress={includeFields.address ? clientAddress : ''}
            buyerTaxId={includeFields.taxId ? (clientGSTIN.trim() || undefined) : undefined}
            isB2B={isB2B || Boolean(clientGSTIN.trim())}
            taxBreakdown={quickInvTaxBreakdown}
            invoiceDate={invoiceDate}
            invoiceTime={invoiceTime}
            notes={notes}
            lineItems={lineItems.filter(li => li.description.trim()).map(li => {
              const getColVal = (col: { key: string; header: string }) => {
                const direct = (li as any)[col.key] ?? (li as any)[col.header] ?? (col.key === 'cat_tax_name' ? (li.taxName || li.b2bTaxName) : '');
                if (direct != null && direct !== '') return String(direct);
                const p = catalogProducts.find(cp => li.description && cp.name?.trim().toLowerCase() === li.description.trim().toLowerCase());
                return getCatalogColValue(p, col) ?? '';
              };
              const getColAr = (col: { key: string; header: string }) => {
                const rawVal = getColVal(col);
                return translatedValues[`custom_${li.id}_${col.key}`] ||
                       translatedValues[`custom_${li.id}_${col.key.replace(/^cat_/, '')}`] ||
                       translatedValues[`custom_${li.id}_${col.header}`] ||
                       translatedValues[`tax_name_${rawVal}`] ||
                       taxBreakdownAr[rawVal] ||
                       '';
              };
              return {
                ...li,
                arabicName: translatedValues[`item_${li.id}`],
                taxRate: getEffectiveTaxRate(li),
                taxName: li.taxName,
                b2bTaxRate: isB2B ? li.b2bTaxRate : undefined,
                b2bTaxName: isB2B ? (li.b2bTaxName || (customTaxScheme as any)?.b2b_name || li.taxName || customTaxScheme?.name) : undefined,
                taxInclusive: !!li.taxInclusive,
                ...Object.fromEntries(customColumns.flatMap(col => [
                  [col.key, getColVal(col)],
                  [col.header, getColVal(col)],
                ])),
                __customAr: Object.fromEntries(
                  customColumns.flatMap(col => [
                    [col.key, getColAr(col)],
                    [col.header, getColAr(col)],
                  ])
                ),
              };
            })}
            taxLines={taxLines}
            discountLines={previewDiscountLines}
            sym={sym}
            shippingCost={shippingCost}
            invoiceStatus={invoiceStatus}
            translateTargetLang={quickInvLang}
            isBilingual={true}
            bilingualLayout={bilingualLayout}
            isThermal={isThermalTemplate}
            showAmountInWords={showAmountInWords}
            currencyCode={activeCurrency}
            amountInWordsTranslated={translatedValues['amountInWords'] || ''}
            amountInWordsStyle={amountInWordsStyle}
            savedElements={previewQrElements.map((el: any) => ({
              ...el,
              _auto:
                el.id === 'preview-invoice-qr' ? 'invoice' :
                el.id === 'preview-invoice-qr-label' ? 'invoice_label' :
                el.id === 'preview-tracking-qr' ? 'tracking' :
                el.id === 'preview-tracking-qr-label' ? 'tracking_label' :
                undefined,
            }))}
            onQrMouseDown={handlePreviewQrMouseDown}
              autoQrPosition={autoQrPosition}
              zoom={zoom}
              cashierName={authUser?.name || 'Staff'}
              pharmacistName={dynamicValues?.pharmacist || 'Pharmacist'}
              cashierNameTranslit={translatedValues['staffName_translit'] || ''}
              pharmacistNameTranslit={translatedValues['pharmacistName_translit'] || ''}
              invoiceNumber={invoiceNumberOverride}
              dynamicValues={previewDynamicValues}
              dynamicValuesAr={translatedValues}
              dynamicFieldLabelsAr={dynamicFieldLabelsAr}
              // Without these, BilingualInvoiceRenderer has no scheme
              // context and always falls back to the blended "Total Tax"
              // line, even for a component-based (CGST/SGST/IGST) scheme —
              // same context the non-bilingual live preview and PDF export
              // already pass to computeTaxBreakdown.
              taxScheme={qiTaxScheme}
              sellerRegion={qiOutletRegion}
              buyerRegion={buyerRegion}
              taxBreakdownAr={taxBreakdownAr}
          />
          </div>
        ) : (
          <>
        <style>{`
          /* CanvasWorkspace renders its own <style> tag as main's real
             first child (the toolbar div is second) — :first-child never
             matched, so the internal toolbar was rendering un-hidden,
             stacked right above/inside our own Single/Auto/A4 Split toggle
             and eating clicks meant for the outer one. */
          .qi-cw-embed > main > style + div { display: none !important; }
        `}</style>
        <CanvasWorkspace
          pageConfig={{ ...cwData.pageConfig, name: previewSourceTemplate.pageSize || 'A4' }}
          computedHeight={__cwComputedHeight}
          pageCount={__cwPageCount}
          pageBehavior={previewPageBehavior}
          setPageBehavior={setPreviewPageBehavior}
          zoom={zoom}
          setZoom={setZoom}
          showGridLines={false}
          elements={[
            ...driftedCwElements,
            ...previewAmountInWordsElements,
            ...previewQrElements,
          ].map((el: any) => {
            if (!bilingualEnabled) {
              const { arabicContent, ...rest } = el;
              if (rest.type === 'table') {
                return {
                  ...rest,
                  columns: (rest.columns || []).map((c: any) => {
                    const { arabicHeader, ...colRest } = c;
                    return colRest;
                  }),
                  rows: (rest.rows || []).map((r: any) => {
                    const cleanValues = { ...r.values };
                    delete cleanValues.arabicDescription;
                    delete cleanValues.arabicName;
                    Object.keys(cleanValues).forEach(k => {
                      if (k.startsWith('arabic_') || k.startsWith('arabicContent_')) {
                        delete cleanValues[k];
                      }
                    });
                    return { ...r, values: cleanValues };
                  }),
                };
              }
              return rest;
            }
            return el;
          })}
          selectedElementId={null}
          inlineEditingId={null}
          inlineEditText=""
          setInlineEditText={() => {}}
          lockedElementIds={[]}
          dragState={null}
          qrDataUrls={{}}
          isDrawingMode={false}
          isDrawingActive={false}
          currentPathPoints={[]}
          currentPenColor="#000000"
          currentPenWidth={2}
          bilingualMode={Boolean(bilingualEnabled && quickInvTranslatedTemplate)}
          bilingualDirection={`en-${quickInvLang}`}
          translateTargetLang={quickInvLang}
          activeCurrency={activeCurrency}
          currencyDisplayMode={(localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol'|'code') || 'symbol'}
          computeTableTotals={(el) => {
            let subtotal = 0;
            const lineBases: { base: number; taxRate: number }[] = [];
            (el.rows || []).forEach((r: any) => {
              const qty = parseFloat(r.values.qty) || 0;
              const rate = parseFloat(r.values.rate) || 0;
              const lineTotal = qty * rate;
              const rawB2b = r.values.b2bTaxRate ?? r.values.b2b_tax_rate;
              const taxRate = isB2B && rawB2b !== undefined && rawB2b !== null
                ? parseFloat(String(rawB2b)) || 0
                : parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
              const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
              const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
              lineBases.push({ base, taxRate });
              subtotal += base;
            });
            const discount = ((el as any).discounts || []).reduce((s: number, d: any) => s + subtotal * (d.rate/100), 0);
            const discountRatio = subtotal > 0 ? Math.min(1, discount / subtotal) : 0;
            const productTax = lineBases.reduce((s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0);
            const taxableValue = subtotal - discount;
            const tax = ((el as any).taxes || []).reduce((s: number, t: any) => s + taxableValue * (t.rate/100), 0);
            const shipping = el.shippingCost || 0;

            const cleanBreakdown = bilingualEnabled
              ? quickInvTaxBreakdown
              : quickInvTaxBreakdown?.map(t => ({ name: t.name, rate: t.rate, amount: t.amount }));

            return { subtotal, tax, discount, shipping, productTax, taxBreakdown: cleanBreakdown, total: taxableValue + productTax + tax + shipping };
          }}
        formatSerialNumber={(s) => s ? `${s.prefix}${String(s.current).padStart(s.digits,'0')}${s.suffix}` : ''}
        canvasRef={previewCanvasRef}
        handleMouseDown={handlePreviewQrMouseDown}
        handleDeleteElement={() => {}}
        handleInlineEditStart={() => {}}
        handleInlineEditSave={() => {}}
        commitCurrentState={() => {}}
        updateElementProps={() => {}}
        handleCanvasMouseDown={() => {}}
        handleCanvasMouseMove={() => {}}
        handleCanvasMouseUp={() => {}}
        onBackgroundClick={() => {}}
      />
          </>
        )}
      </div>
    </div>
  );
})()}




         
        </div>
      </div>

      {formSide === 'right' && (isMobile ? !showMobilePreview : true) && formPanel}



      {paymentWaitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <style>{`@keyframes qiSpin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ background: '#fff', borderRadius: 24, padding: '30px 26px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>
            {paymentFailed ? (
              <>
                <div style={{ fontSize: 36, marginBottom: 6 }}>❌</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#dc2626', marginBottom: 4 }}>
                  Payment Failed — {paymentWaitModal.invoiceNumber}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 18 }}>
                  Nothing was printed or sent. Retry with this customer, or discard and move on.
                </div>
                <button onClick={regeneratePaymentQR} disabled={regeneratingQR}
                  style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 12, fontWeight: 800, cursor: regeneratingQR ? 'not-allowed' : 'pointer', marginBottom: 8 }}>
                  {regeneratingQR ? 'Regenerating...' : '🔄 Regenerate QR & Try Again'}
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setPaymentFailed(false)}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    🔄 Retry
                  </button>
                  <button onClick={async () => {
                    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
                    try {
                      await fetch(`${BACKEND}/invoices/${paymentWaitModal.invoiceId}/cancel`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                        body: JSON.stringify({ reason: 'Online payment failed — discarded by cashier' }),
                      });
                      setBills(prev => prev.filter(b => b.id !== paymentWaitModal.invoiceId));
                      onAddLog('INVOICE_DISCARDED', `Invoice ${paymentWaitModal.invoiceNumber} discarded after failed payment.`);
                    } catch (e) { console.warn('Discard failed:', e); }
                    pendingPostPaymentRef.current = null;
                    setPaymentWaitModal(null);
                    setPaymentFailed(false);
                    handleReset();
                  }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#fee2e2', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    🗑 Discard
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>
                  Waiting for payment — {paymentWaitModal.invoiceNumber}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16 }}>
                  Ask the customer to scan and pay. Nothing prints or sends until the gateway confirms.
                </div>
                <img src={paymentWaitModal.qr} alt="Payment QR" style={{ width: 240, height: 240, borderRadius: 16, border: '2px solid #e2e8f0', margin: '0 auto 14px', display: 'block', opacity: regeneratingQR ? 0.4 : 1 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: '#6366f1', fontWeight: 700, marginBottom: 18, background: '#eef2ff', borderRadius: 99, padding: '6px 0' }}>
                  <div style={{ width: 12, height: 12, border: '2px solid #c7d2fe', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'qiSpin 0.8s linear infinite' }} />
                  {paymentPolling ? 'Checking for payment...' : 'Connecting...'}
                </div>

                {/* Primary actions — works for both UPI and physical Card machines */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <button onClick={confirmCardManually}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px 10px', borderRadius: 14, border: 'none',
                      background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                      color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(22,163,74,0.35)',
                    }}>
                    <CheckCircle size={18} /> {paymentMode === 'upi' ? 'UPI Received' : 'Card Approved'}
                  </button>

                  <button onClick={switchToCashAndConfirm}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px 10px', borderRadius: 14, border: 'none',
                      background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                      color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
                    }}>
                    <Banknote size={18} /> Switch to Cash
                  </button>
                </div>

                {/* Secondary actions */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <button onClick={() => { setEditingPendingInvoice(paymentWaitModal); setPaymentWaitModal(null); }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 6px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    <Plus size={16} /> Add Item
                  </button>
                  <button onClick={() => navigator.clipboard.writeText(paymentWaitModal.link)}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 6px', borderRadius: 12, border: '1.5px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    🔗 Copy Link
                  </button>
                  <button onClick={regeneratePaymentQR} disabled={regeneratingQR}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 6px', borderRadius: 12, border: '1.5px solid #fde68a', background: '#fffbeb', color: '#92400e', fontSize: 11, fontWeight: 700, cursor: regeneratingQR ? 'not-allowed' : 'pointer', opacity: regeneratingQR ? 0.6 : 1 }}>
                    🔄 {regeneratingQR ? 'Wait...' : 'New QR'}
                  </button>
                  <button
                    onClick={() => window.open(outletDisplayToken ? `/display?token=${outletDisplayToken}&register_id=${registerId}` : '#', '_blank')}
                    disabled={!outletDisplayToken}
                    title={outletDisplayToken ? 'Open Counter Display' : 'No display token found for this outlet'}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 6px', borderRadius: 12, border: '1.5px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: outletDisplayToken ? 'pointer' : 'not-allowed', opacity: outletDisplayToken ? 1 : 0.5 }}>
                    🖥 Display
                  </button>
                </div>

                {/* Footer actions */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                  <button onClick={() => { setPaymentWaitModal(null); handleReset(); setSaved(true); }}
                    style={{ padding: '13px 0', borderRadius: 12, border: 'none', background: '#f1f5f9', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    ✓ Done (pay later)
                  </button>
                  <button onClick={discardPendingInvoice}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '13px 0', borderRadius: 12, border: 'none', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    <Trash2 size={15} /> Discard
                  </button>
                </div>
                <div style={{ fontSize: 9, color: '#cbd5e1', textAlign: 'center' }}>"Done (pay later)" keeps it unpaid in Bills. "Discard" removes it entirely.</div>
              </>
            )}
          </div>
        </div>
      )}

      {showScanner && (
        <QuickScannerModal
          onAddItem={handleScanAdd}
          onClose={() => setShowScanner(false)}
          onSessionStart={(sessionId) => setActiveScanSession(sessionId)}
        />
      )}

      {/* Tax ID Legal Disclaimer Modal */}
      {showTaxDisclaimerModal && (
        <div 
          onClick={() => setShowTaxDisclaimerModal(false)} 
          style={{ 
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', 
            backdropFilter: 'blur(3px)', zIndex: 9999, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 
          }}
        >
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ 
              background: '#fff', borderRadius: 16, padding: '22px 24px', 
              maxWidth: 440, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' 
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>⚖️</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Tax ID Verification Notice</span>
              </div>
              <button 
                onClick={() => setShowTaxDisclaimerModal(false)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#991b1b', marginBottom: 4 }}>
                ⚠️ Do NOT rely on or trust this badge for legal or tax compliance:
              </div>
              <ul style={{ fontSize: 11, color: '#7f1d1d', lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                <li><strong>Format check only:</strong> It only tests character structure and length. It has <strong>no connection</strong> to government databases.</li>
                <li><strong>Filtering can be wrong:</strong> Automated filters can make mistakes, pass fake numbers, or misidentify edge cases.</li>
                <li><strong>Not a legal verification:</strong> A green badge does <strong>not</strong> mean the company is real, active, or legally authorized to charge taxes.</li>
              </ul>
            </div>

            <p style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginBottom: 16 }}>
              You must independently authenticate and verify all buyer tax numbers on the <strong>official government tax portal</strong> (e.g., GST Portal, IRS, HMRC, VIES) before claiming tax credits or exemptions.
            </p>

            <button 
              onClick={() => setShowTaxDisclaimerModal(false)} 
              style={{ 
                width: '100%', padding: '10px 0', background: '#3b82f6', color: '#fff', 
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' 
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 800, color: '#475569',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 5,
};

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1.5px solid #cbd5e1', fontSize: 13, color: '#0f172a',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
};

const row: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
